/**
 * Claude Master Tour Calendar operational pages (v2 flag).
 * Layout matches Tour Calendar v2 / offline HTML. Figures stay empty until typed or imported.
 */
import React, { useContext, useEffect, useMemo, useRef, useState, useCallback } from 'react';
import {
  ActivityIndicator,
  Alert,
  Image,
  Linking,
  useWindowDimensions,
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import * as DocumentPicker from 'expo-document-picker';
import { getAuth } from 'firebase/auth';
import { getFirestore } from 'firebase/firestore';
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { createPortal } from 'react-dom';
import { MemberPermissionsEditor } from './TourPartyPermissionsPanel';
import TourFinancePanel, { RemindersStrip } from './TourFinancePanel';
import { settlementToPrintHtml, openPrintWindow } from '../../utils/calendarTourPnl';
import {
  isTourOwner,
  getMemberPermissionsEntry,
  normalizeMemberPermissionsEntry,
  saveMemberPermissionEntry,
  saveSetlistModerator,
  memberPermissionEditState,
  summarizeMemberPermissionTabs,
  buildOwnerFullAccessEntry,
  canPublishSetlist,
  resolveSetlistModeratorId,
} from '../../utils/tourMemberPermissionsService';
import { palette, FONT, PAL_DARK } from '../../styles/tourTheme';
import {
  fieldWellStyle,
  inlineBlendInputStyle,
  insetBlendStyle,
  V2_INPUT_BLEND_CLASS,
  V2_INPUT_WELL_CLASS,
  webInputClassProps,
} from '../../utils/transparentTextInputStyle';
import { parseSetlistText, serializeSetlistDoc, newSetlistSongId, newSetlistLineId, normalizeSongDuration } from '../SetlistEditor';
import { pickTourDayCard } from '../../utils/calendarDayCard';
import { Ionicons } from '@expo/vector-icons';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { reorderListById } from '../../utils/listReorder';
import {
  bucketContacts,
  contactsFromStructured,
  contactsToStructured,
  emptyContact,
  emptyContactCategory,
  formatDateLong,
  formatDateShort,
  parseContactsDoc,
  resolveShowDate,
} from '../../utils/calendarContacts';
import {
  DEAL_PRESETS,
  applySettleLines,
  emptyLedger,
  emptySettle,
  emptyExpenseLine,
  emptyPerDiem,
  emptyDayRate,
  formatUsd,
  ledgerTotals,
  linesFromSheetText,
  netLedgerColor,
  parseSheetRows,
  parseWorksheetSeed,
  perDiemAmount,
  perDiemBase,
  PER_DIEM_RATE_TYPES,
  PER_DIEM_SPANS,
  merchRowsToSetlistDoc,
  merchSoldCount,
  merchSheetGrossSplit,
  merchLineGross,
  merchTotalIn,
  merchItemGrossIn,
  merchInvRemaining,
  merchActiveSizes,
  merchUnitPrice,
  merchSizeSold,
  parseMerchSheet,
  parseMerchCatalog,
  serializeMerchCatalog,
  coerceMerchStore,
  emptyMerchRow,
  emptyMerchSize,
  mergeMerchNight,
  merchCountsAreEmpty,
  previousShowKey,
  toggleMerchSize,
  toggleMerchColor,
  merchVariantKey,
  merchVariantLabel,
  NUM,
  numericText,
  addMerchUserOption,
  rewriteMerchUserOptions,
  rewriteMerchRowsOption,
  rewriteMerchSheetCustoms,
  merchSheetCustomsFromUserOptions,
  merchTypesForCategory,
  defaultMerchUserOptions,
  mergeTourCustomsIntoUserOptions,
  resolvePerDiemDates,
  settleTotals,
  syncPerDiemCopies,
  perDiemSyncTargets,
  findDuplicateExpenses,
  settlementToCsv,
} from '../../utils/calendarSettlement';
import {
  loadMerchUserOptionsWithSource,
  persistMerchUserOptions,
  currentMerchUserId,
  rewriteAllLocalMerchSheets,
} from '../../utils/merchUserOptions';
import { loadStoredJson, loadStoredJsonIfPresent, saveStoredJson } from '../../utils/calendarLocalStore';
import { getLodgingVenueForDay, parseLodgingStays, serializeLodgingStays, emptyLodgingStay } from '../../utils/tourDayLodgingUtils';
import { parseScheduleText } from '../ScheduleEditor';
import DaySheetScheduleEditor, {
  appendDaySheetCue,
  appendDaySheetText,
} from './DaySheetScheduleEditor';
import TourFundSettlementSection from './TourFundSettlementSection';
import { buildDaySheetPreviewRows, downloadDaySheetPdf } from '../DaySheetPaper';
import { SETLIST_LABELS, GUEST_LIST_LABELS } from '../structuredDayListLabels';
import { formatSetlistDateLabel, openSetlistPrintWindow, openSetlistPrintAllWindow, summarizeSetlistTiming } from '../SetlistPaper';
import {
  acceptSetlistPending,
  applySetlistEditorChange,
  commitBpmInput,
  isHawkDemoTour,
  needsHawkBpmSeed,
  officialSetlistRaw,
  parseBpmInputDraft,
  parseSetlistDoc,
  rejectSetlistPending,
  seedHawkSongBpms,
  serializeSetlistDocWithPending,
  setlistDisplaySongs,
  setlistHighlightMap,
} from '../../utils/setlistWorkflow';
import { notifySetlistProposal } from '../../utils/setlistProposalNotify';
import { SavedContactsCard } from './SavedContactsCard';
import { hexWithAlpha, glossyFill, glossyWebStyle, mixHex } from '../../utils/calendarV2Prefs';
import {
  GUEST_ALLOC,
  SPOTS_PLACEHOLDER,
  allocateGuestParties,
  countGuestNames,
  guestAllocLabel,
  newGuestPartySong,
  parseGuestCap,
  parseGuestParties,
  sanitizeSpotCountInput,
  toggleGuestPartyCut,
  patchGuestPartySong,
} from '../../utils/calendarGuestList';

function useDebouncedCallback(fn, delayMs = 450) {
  const fnRef = useRef(fn);
  const timerRef = useRef(null);
  useEffect(() => { fnRef.current = fn; }, [fn]);
  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);
  return useCallback((arg) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => fnRef.current(arg), delayMs);
  }, [delayMs]);
}

/** False inside a read-only ops page: shared inputs and chips read it so one flag locks the whole page. */
const OpsEditContext = React.createContext(true);

function ViewOnlyBadge({ pal }) {
  return (
    <Text
      accessibilityLabel="View only"
      style={{ color: pal.warn, fontSize: 11, fontWeight: '600', letterSpacing: 0.6, textTransform: 'uppercase', marginTop: 6 }}
    >
      View only
    </Text>
  );
}

function copyText(value) {
  try {
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      return navigator.clipboard.writeText(String(value || ''));
    }
  } catch {
    /* ignore */
  }
  return Promise.resolve();
}

function initialsOf(name) {
  return String(name || '')
    .split(/\s+/)
    .filter(Boolean)
    .map((p) => p[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

function PartyAvatar({ member, size = 40, pal, accent }) {
  const uri = String(member?.photoURL || '').trim();
  if (uri.startsWith('http://') || uri.startsWith('https://')) {
    return (
      <Image
        source={{ uri }}
        style={{ width: size, height: size, borderRadius: size / 2, backgroundColor: pal.sunk }}
      />
    );
  }
  return (
    <View
      style={{
        width: size,
        height: size,
        borderRadius: size / 2,
        backgroundColor: accent || pal.teal,
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Text style={{ color: '#fffffe', fontWeight: '600', fontSize: Math.max(10, size * 0.32) }}>
        {initialsOf(member?.name || member?.displayName)}
      </Text>
    </View>
  );
}

function FileImportButton({ label, accept, onText, pal, solid }) {
  const ref = useRef(null);
  const canEdit = useContext(OpsEditContext);
  if (!canEdit) return null;
  if (Platform.OS !== 'web') {
    return (
      <View style={[s.outlineBtn, { borderColor: pal.line }]}>
        <Text style={{ color: pal.mute, fontSize: 11, letterSpacing: 0.7, textTransform: 'uppercase' }}>
          Import on web
        </Text>
      </View>
    );
  }
  return (
    <View>
      <TouchableOpacity
        onPress={() => ref.current?.click?.()}
        style={solid
          ? [s.primaryBtn, { backgroundColor: pal.ink }]
          : [s.outlineBtn, { borderColor: pal.ink, backgroundColor: pal.surf }]}
      >
        <Text style={{ color: solid ? pal.page : pal.ink, fontWeight: '600', fontSize: 11, letterSpacing: 0.7, textTransform: 'uppercase' }}>
          {label}
        </Text>
      </TouchableOpacity>
      <input
        ref={ref}
        type="file"
        accept={accept}
        style={{ display: 'none' }}
        onChange={(ev) => {
          const file = ev.target.files && ev.target.files[0];
          ev.target.value = '';
          if (!file) return;
          const reader = new FileReader();
          reader.onload = () => onText(String(reader.result || ''), file.name || 'file');
          reader.readAsText(file);
        }}
      />
    </View>
  );
}

function isDarkPal(pal) {
  return pal.page === PAL_DARK.page;
}

function opsInputClass(kind) {
  return webInputClassProps(kind === 'blend' ? V2_INPUT_BLEND_CLASS : V2_INPUT_WELL_CLASS);
}

function Field({ value, onChange, pal, align, placeholder, flex, color, editable }) {
  const pageEditable = useContext(OpsEditContext);
  const canEdit = pageEditable && editable !== false;
  const [focused, setFocused] = useState(false);
  const [draft, setDraft] = useState('');
  const raw = value == null ? '' : String(value);
  const isBlankZero = raw === '' || Number(raw) === 0;
  const shown = focused ? draft : (isBlankZero ? '' : raw);
  return (
    <TextInput
      value={shown}
      editable={canEdit}
      onChangeText={(text) => {
        if (!canEdit) return;
        const next = numericText(text);
        setDraft(next);
        onChange(next);
      }}
      inputMode="decimal"
      onFocus={() => {
        setFocused(true);
        setDraft(isBlankZero ? '' : raw);
      }}
      onBlur={() => setFocused(false)}
      placeholder={placeholder || ''}
      placeholderTextColor={pal.faint}
      keyboardType="numeric"
      {...opsInputClass('well')}
      style={[
        s.field,
        fieldWellStyle(pal, isDarkPal(pal)),
        { color: color || pal.ink, textAlign: align || 'left', flex: flex || undefined },
      ]}
    />
  );
}

function Chip({ pal, label, on, onPress }) {
  return (
    <TouchableOpacity
      onPress={onPress}
      style={[s.outlineBtn, { paddingVertical: 3, paddingHorizontal: 8, borderColor: on ? pal.ink : pal.line, backgroundColor: on ? pal.ink : 'transparent', flexDirection: 'row', alignItems: 'center', gap: 4 }]}
    >
      <Text style={{ fontSize: 11, fontWeight: '600', color: on ? pal.page : pal.mute }}>{label}</Text>
    </TouchableOpacity>
  );
}

function InfoTip({ pal, text }) {
  const [open, setOpen] = useState(false);
  const [box, setBox] = useState(null);
  const hostRef = useRef(null);
  const place = useCallback(() => {
    const node = hostRef.current;
    if (!node || typeof node.getBoundingClientRect !== 'function') return;
    const r = node.getBoundingClientRect();
    const width = 260;
    const vw = typeof window !== 'undefined' ? window.innerWidth : 800;
    const vh = typeof window !== 'undefined' ? window.innerHeight : 800;
    const left = Math.min(Math.max(8, r.left), Math.max(8, vw - width - 8));
    const below = r.top < 120;
    setBox({
      left,
      top: below ? r.bottom + 8 : r.top - 8,
      below,
      maxHeight: below ? Math.max(96, vh - r.bottom - 16) : Math.max(96, r.top - 16),
    });
  }, []);
  useEffect(() => {
    if (!open || Platform.OS !== 'web' || typeof window === 'undefined') return undefined;
    place();
    const onMove = () => place();
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);
    return () => {
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
    };
  }, [open, place]);
  if (!text) return null;
  const bubble = (
    <View
      pointerEvents="none"
      style={{
        width: 260,
        maxHeight: box?.maxHeight || 240,
        padding: 10,
        borderRadius: 8,
        backgroundColor: pal.ink,
        borderWidth: 1,
        borderColor: pal.line,
      }}
    >
      <Text style={{ color: pal.page, fontSize: 12, lineHeight: 17 }}>{text}</Text>
    </View>
  );
  const webPopup = open && box && Platform.OS === 'web' && typeof document !== 'undefined'
    ? createPortal(
      <div
        className="v2-info-tip-float"
        data-tip-above={box.below ? '0' : '1'}
        style={{
          position: 'fixed',
          top: `${box.top}px`,
          left: `${box.left}px`,
          zIndex: 2147483647,
          pointerEvents: 'none',
          transform: box.below ? undefined : 'translateY(-100%)',
        }}
      >
        {bubble}
      </div>,
      document.body
    )
    : null;
  return (
    <View
      ref={hostRef}
      style={{ marginLeft: 5, position: 'relative', zIndex: 8 }}
      {...(Platform.OS === 'web' ? {
        onMouseEnter: () => setOpen(true),
        onMouseLeave: () => setOpen(false),
        title: text,
      } : {})}
    >
      <TouchableOpacity
        accessibilityLabel={text}
        onPress={() => setOpen((v) => !v)}
        hitSlop={{ top: 6, bottom: 6, left: 6, right: 6 }}
      >
        <Ionicons name="information-circle-outline" size={14} color={pal.mute} />
      </TouchableOpacity>
      {Platform.OS === 'web' ? webPopup : (open ? (
        <View style={{
          position: 'absolute',
          left: 0,
          bottom: 20,
          zIndex: 2147483647,
          elevation: 24,
        }}>
          {bubble}
        </View>
      ) : null)}
    </View>
  );
}

const SELECT_TOUR_HINT = 'Select a tour to add days.';

function HoverNote({ pal, text, children, style }) {
  const [open, setOpen] = useState(false);
  const [box, setBox] = useState(null);
  const hostRef = useRef(null);
  const place = useCallback(() => {
    const node = hostRef.current;
    if (!node || typeof node.getBoundingClientRect !== 'function') return;
    const r = node.getBoundingClientRect();
    const width = 220;
    const vw = typeof window !== 'undefined' ? window.innerWidth : 800;
    const left = Math.min(Math.max(8, r.left), Math.max(8, vw - width - 8));
    setBox({ left, top: r.top - 8, width });
  }, []);
  useEffect(() => {
    if (!open || Platform.OS !== 'web' || typeof window === 'undefined') return undefined;
    place();
    const onMove = () => place();
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);
    return () => {
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
    };
  }, [open, place]);
  if (!text) return <View style={style}>{children}</View>;
  const popup = open && box && Platform.OS === 'web' && typeof document !== 'undefined'
    ? createPortal(
      <div
        style={{
          position: 'fixed',
          top: `${box.top}px`,
          left: `${box.left}px`,
          width: `${box.width}px`,
          zIndex: 2147483647,
          pointerEvents: 'none',
          transform: 'translateY(-100%)',
        }}
      >
        <View
          pointerEvents="none"
          style={{
            paddingVertical: 8,
            paddingHorizontal: 10,
            borderRadius: 8,
            backgroundColor: pal.ink,
            borderWidth: 1,
            borderColor: pal.line,
          }}
        >
          <Text style={{ color: pal.page, fontSize: 12, lineHeight: 16 }}>{text}</Text>
        </View>
      </div>,
      document.body
    )
    : null;
  return (
    <View
      ref={hostRef}
      accessibilityLabel={text}
      style={style}
      {...(Platform.OS === 'web' ? {
        onMouseEnter: () => setOpen(true),
        onMouseLeave: () => setOpen(false),
      } : {})}
    >
      {children}
      {popup}
    </View>
  );
}

function OptionChip({ pal, label, on, onPress, onEdit }) {
  const canEdit = useContext(OpsEditContext);
  return (
    <View style={[s.outlineBtn, {
      paddingVertical: 3,
      paddingLeft: 8,
      paddingRight: 4,
      borderColor: on ? pal.ink : pal.line,
      backgroundColor: on ? pal.ink : 'transparent',
      flexDirection: 'row',
      alignItems: 'center',
      gap: 2,
    }]}>
      <TouchableOpacity onPress={canEdit ? onPress : undefined} disabled={!canEdit}>
        <Text style={{ fontSize: 11, fontWeight: '600', color: on ? pal.page : pal.mute }}>{label}</Text>
      </TouchableOpacity>
      {onEdit && canEdit ? (
        <TouchableOpacity
          onPress={onEdit}
          accessibilityLabel={`Edit ${label}`}
          hitSlop={{ top: 6, bottom: 6, left: 6, right: 6 }}
          style={{ paddingHorizontal: 4, paddingVertical: 2 }}
        >
          <Ionicons name="create-outline" size={12} color={on ? pal.page : pal.mute} />
        </TouchableOpacity>
      ) : null}
    </View>
  );
}

function OptionEditCard({ pal, field, label, onClose, onRename, onDelete }) {
  const [text, setText] = useState(label);
  const title = {
    category: 'category',
    type: 'type',
    size: 'size',
    avail: 'For',
    seller: 'seller',
    sex: 'sex',
    reorder: 'reorder',
    color: 'color',
  }[field] || 'option';
  return (
    <View style={[s.card, { backgroundColor: pal.surf, borderColor: pal.line, marginTop: 12 }]}>
      <Text style={[s.label, { color: pal.mute, marginBottom: 8 }]}>Edit {title}</Text>
      <TextInput
        value={text}
        onChangeText={setText}
        autoFocus
        {...opsInputClass('blend')}
        style={[inlineBlendInputStyle, { color: pal.ink, fontSize: 16 }]}
      />
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: 12 }}>
        <TouchableOpacity
          onPress={() => {
            const next = String(text || '').trim();
            if (next && next !== label) onRename(next);
            else onClose();
          }}
          style={[s.primaryBtn, { backgroundColor: pal.ink }]}
        >
          <Text style={{ color: pal.page, fontWeight: '600' }}>Save name</Text>
        </TouchableOpacity>
        <TouchableOpacity
          onPress={() => {
            const ok = Platform.OS === 'web' && typeof window !== 'undefined' && typeof window.confirm === 'function'
              ? window.confirm(`Delete “${label}”? Items using it will be cleared or updated.`)
              : true;
            if (Platform.OS !== 'web') {
              Alert.alert(`Delete “${label}”?`, 'Items using it will be cleared or updated.', [
                { text: 'Keep', style: 'cancel' },
                { text: 'Delete', style: 'destructive', onPress: onDelete },
              ]);
              return;
            }
            if (ok) onDelete();
          }}
          style={[s.outlineBtn, { borderColor: pal.alert || '#c0483c' }]}
        >
          <Text style={{ color: pal.alert || '#c0483c', fontWeight: '600' }}>Delete</Text>
        </TouchableOpacity>
        <TouchableOpacity onPress={onClose} style={[s.outlineBtn, { borderColor: pal.line }]}>
          <Text style={{ color: pal.mute, fontWeight: '600' }}>Cancel</Text>
        </TouchableOpacity>
      </View>
    </View>
  );
}

function AddChoice({ pal, label, onAdd }) {
  const canEdit = useContext(OpsEditContext);
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const stayOpen = useRef(false);
  const commit = ({ closeIfEmpty } = {}) => {
    const next = String(text || '').trim();
    if (next) {
      onAdd(next);
      setText('');
      return;
    }
    if (closeIfEmpty) setOpen(false);
  };
  if (!canEdit) return null;
  if (!open) {
    return (
      <TouchableOpacity
        onPress={() => setOpen(true)}
        accessibilityLabel={label}
        style={[s.outlineBtn, {
          paddingVertical: 3,
          paddingHorizontal: 8,
          borderStyle: 'dashed',
          borderColor: pal.teal,
          flexDirection: 'row',
          alignItems: 'center',
          gap: 4,
        }]}
      >
        <Ionicons name="add" size={14} color={pal.teal} />
        <Text style={{ fontSize: 11, fontWeight: '600', color: pal.teal }}>{label}</Text>
      </TouchableOpacity>
    );
  }
  return (
    <TextInput
      autoFocus
      value={text}
      onChangeText={setText}
      onSubmitEditing={() => {
        stayOpen.current = true;
        commit();
      }}
      onBlur={() => {
        if (stayOpen.current) {
          stayOpen.current = false;
          return;
        }
        commit({ closeIfEmpty: true });
      }}
      placeholder={label}
      placeholderTextColor={pal.faint}
      {...opsInputClass('blend')}
      style={[inlineBlendInputStyle, { color: pal.ink, minWidth: 128, fontSize: 13, paddingVertical: 4 }]}
    />
  );
}

function LabeledBox({ pal, label, tip, children, width, flex }) {
  return (
    <View style={{ width: width || undefined, minWidth: width || 84, flex: flex || undefined }}>
      <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 6 }}>
        <Text style={[s.label, { color: pal.mute }]}>{label}</Text>
        <InfoTip pal={pal} text={tip} />
      </View>
      {children}
    </View>
  );
}

function ColHead({ pal, label, tip, width }) {
  return (
    <View style={{ width, flexDirection: 'row', alignItems: 'center' }}>
      <Text style={[s.colH, { color: pal.mute }]}>{label}</Text>
      <InfoTip pal={pal} text={tip} />
    </View>
  );
}

const MERCH_TIPS = {
  merchCut: 'Percent of apparel/other merch gross the venue keeps tonight.',
  musicCut: 'Percent of music (CD, vinyl, etc.) gross the venue keeps. Often different from apparel.',
  tax: 'Sales tax rate applied to merch. Leave blank if tax is already in the price.',
  taxOnTop: 'When on, tax is added on top of the sticker price instead of included in it.',
  category: 'Apparel, Music, or another bucket you add. Music uses the music cut; everything else uses merch cut.',
  seller: 'Who is selling this item tonight — house, band, crew, artist, hired, or a type you add.',
  sku: 'Your stock code for this item or size.',
  cogs: 'Cost of goods sold — what you paid per unit, not the selling price.',
  qtyAvail: 'How many units you have available to sell from inventory, before tonight’s stand counts.',
  countIn: 'How many units counted into the stand at the start of the night. Often last night’s leftover Out.',
  countAdd: 'Units added to the stand during the night (restock from the trailer or a new box).',
  totalIn: 'Count In plus Adds.',
  countComp: 'Giveaways that left the stand. Comps are not sold, but they do leave inventory.',
  itemGross: 'Price × Total In — the dollar value of stock that came onto the stand.',
  invRmg: 'Inventory remaining after comps: Total In minus Comp.',
  countOut: 'How many units counted out of the stand at the end of the night. Becomes tomorrow’s In.',
  sold: 'In + Add − Out − Comp. This is the sold quantity used for settlement.',
  gross: 'Sold × price. Venue cut is taken from this.',
  type: 'Kind of item (shirt, hoodie, vinyl…). Add your own and it stays on your list.',
  sex: 'Garment cut / fit.',
  sizes: 'Which sizes are on the stand tonight. Add a size once and it stays on your list.',
  color: 'Pick every color this item is selling tonight. Add your own and it stays on your list.',
  reorder: 'How urgently this item needs restocking.',
  alertShows: 'Warn after this many shows at the current reorder level.',
  avail: 'Which set this item is sold at — headline, support, festival, or a slot you add.',
  crewQty: 'Units sold to crew off the stand (not in In/Out counts).',
  crewAmount: 'Dollar amount of those crew sales.',
  trailerComp: 'Comps taken from the trailer, not from the stand count.',
  taxOverride: 'Tax rate for this item only, instead of the sheet tax %.',
  cutOverride: 'Venue cut for this item only, instead of the sheet merch/music cut.',
};

function MerchAddItemButton({ pal, onPress, empty }) {
  const canEdit = useContext(OpsEditContext);
  if (!canEdit) return null;
  return (
    <TouchableOpacity
      onPress={onPress}
      accessibilityLabel="Add item"
      style={{
        marginTop: 12,
        minHeight: empty ? 104 : 72,
        borderWidth: 1.5,
        borderStyle: 'dashed',
        borderColor: pal.teal,
        borderRadius: 12,
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'row',
        gap: 12,
        backgroundColor: pal.surf,
        paddingVertical: 16,
        paddingHorizontal: 18,
      }}
    >
      <View style={{
        width: 36,
        height: 36,
        borderRadius: 18,
        backgroundColor: pal.teal,
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <Ionicons name="add" size={22} color={pal.page} />
      </View>
      <View>
        <Text style={{ color: pal.ink, fontWeight: '700', fontSize: 16 }}>Add item</Text>
        <Text style={{ color: pal.mute, fontSize: 13, marginTop: 2 }}>
          {empty ? 'Fill this first card, then add another below' : 'Add another item under this one'}
        </Text>
      </View>
    </TouchableOpacity>
  );
}

function useShowDates({ tourDayMetaByDate, selectedTourFilterId, showDateKeys, selectedDateKey }) {
  return useMemo(() => {
    const keys = (showDateKeys || []).filter(Boolean);
    return keys.map((key) => {
      const card = pickTourDayCard(tourDayMetaByDate?.[key], selectedTourFilterId);
      const city = String(card?.cityLine || '').split(',')[0] || '';
      return { key, label: formatDateShort(key), city };
    });
  }, [showDateKeys, tourDayMetaByDate, selectedTourFilterId, selectedDateKey]);
}

function venueBits({ dateKey, tourDayMetaByDate, selectedTourFilterId, tour }) {
  const card = pickTourDayCard(tourDayMetaByDate?.[dateKey], selectedTourFilterId);
  const { day } = tour ? { day: (tour.days || []).find((d) => String(d?.scheduledDate || '').trim() === dateKey) } : { day: null };
  const venue = (day?.venues || []).find((v) => !String(v?.backstage || '').toLowerCase().includes('lodging')) || (day?.venues || [])[0];
  const cap = venue?.capacity || venue?.cap || card?.chips?.find((c) => /cap/i.test(c.text || ''))?.text;
  return {
    venueName: card?.venueLine || venue?.name || 'Venue',
    cityLine: card?.cityLine || [venue?.city, venue?.state].filter(Boolean).join(', '),
    cap: cap ? String(cap).replace(/cap/i, '').trim() : '',
    card,
    day,
  };
}

const DEPT_ADD_ROLE = {
  ops: 'Production manager',
  sec: 'Head of security',
  hosp: 'Hospitality',
  tech: 'Head audio',
  box: 'Box office',
  mkt: 'Marketing manager',
  other: 'Contact',
};

const CONTACT_COL_STYLE = {
  flexGrow: 1,
  flexBasis: 320,
  minWidth: 280,
  maxWidth: '100%',
  padding: 0,
  overflow: 'hidden',
};

function confirmRemoveCategory(onYes) {
  const title = 'Remove this category?';
  const message = 'People in it move to Other. You can still edit them there.';
  if (Platform.OS === 'web' && typeof window !== 'undefined' && typeof window.confirm === 'function') {
    if (window.confirm(`${title}\n${message}`)) onYes();
    return;
  }
  Alert.alert(title, message, [
    { text: 'Keep', style: 'cancel' },
    { text: 'Remove', style: 'destructive', onPress: onYes },
  ]);
}

function ContactField({ pal, label, value, onChange, multiline, keyboardType }) {
  return (
    <View style={{ marginTop: 8 }}>
      <Text style={{ color: pal.faint, fontSize: 10, letterSpacing: 0.8, textTransform: 'uppercase', marginBottom: 4, fontFamily: FONT.mono }}>{label}</Text>
      <TextInput
        value={value}
        onChangeText={onChange}
        multiline={Boolean(multiline)}
        keyboardType={keyboardType || 'default'}
        placeholderTextColor={pal.faint}
        {...opsInputClass('well')}
        style={[
          fieldWellStyle(pal, isDarkPal(pal)),
          {
            color: pal.ink,
            fontSize: 14.5,
            borderWidth: 1,
            borderRadius: 8,
            paddingHorizontal: 10,
            paddingVertical: multiline ? 8 : 7,
            minHeight: multiline ? 72 : 36,
            textAlignVertical: multiline ? 'top' : 'center',
            width: '100%',
          },
        ]}
      />
    </View>
  );
}

function ContactPersonCard({
  person,
  pal,
  hue,
  accent,
  photoByEmail,
  editing,
  canEdit,
  onStartEdit,
  onChange,
  onSave,
  onCancel,
  onDelete,
}) {
  const member = photoByEmail[String(person.email || '').trim().toLowerCase()] || person;
  if (editing) {
    return (
      <View style={{ paddingHorizontal: 16, paddingVertical: 12, borderTopWidth: 1, borderTopColor: pal.lineSoft, width: '100%' }}>
        <ContactField pal={pal} label="Name" value={person.name} onChange={(t) => onChange({ ...person, name: t })} />
        <ContactField pal={pal} label="Role" value={person.role} onChange={(t) => onChange({ ...person, role: t })} />
        <ContactField pal={pal} label="Email" value={person.email} keyboardType="email-address" onChange={(t) => onChange({ ...person, email: t })} />
        <ContactField pal={pal} label="Phone" value={person.phone} keyboardType="phone-pad" onChange={(t) => onChange({ ...person, phone: t })} />
        <ContactField pal={pal} label="Hours / call" value={person.hours} onChange={(t) => onChange({ ...person, hours: t })} />
        <ContactField pal={pal} label="Notes" value={person.note} multiline onChange={(t) => onChange({ ...person, note: t })} />
        <View style={{ flexDirection: 'row', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
          <TouchableOpacity onPress={onSave} style={[s.primaryBtn, { backgroundColor: pal.ink, paddingVertical: 8, paddingHorizontal: 14 }]}>
            <Text style={{ color: pal.page, fontWeight: '600' }}>Save</Text>
          </TouchableOpacity>
          <TouchableOpacity onPress={onCancel} style={[s.outlineBtn, { borderColor: pal.line, paddingVertical: 8, paddingHorizontal: 14 }]}>
            <Text style={{ color: pal.ink, fontWeight: '500' }}>Cancel</Text>
          </TouchableOpacity>
          <TouchableOpacity onPress={onDelete} style={[s.outlineBtn, { borderColor: pal.line, paddingVertical: 8, paddingHorizontal: 14, marginLeft: 'auto' }]}>
            <Text style={{ color: pal.warn || '#c0483c', fontWeight: '600' }}>Delete</Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  }
  return (
    <TouchableOpacity
      onPress={canEdit ? onStartEdit : undefined}
      activeOpacity={canEdit ? 0.75 : 1}
      style={{ flexDirection: 'column', gap: 8, paddingHorizontal: 16, paddingVertical: 12, borderTopWidth: 1, borderTopColor: pal.lineSoft, width: '100%' }}
    >
      <View style={{ flexDirection: 'row', gap: 12, alignItems: 'flex-start', width: '100%' }}>
        <PartyAvatar member={member} size={32} pal={pal} accent={hue} />
        <View style={{ flex: 1, minWidth: 0 }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <Text style={{ color: pal.ink, fontWeight: '400', fontSize: 15 }}>{person.name || 'Untitled'}</Text>
            {person.primary ? (
              <Text style={{ fontSize: 9, fontWeight: '700', letterSpacing: 0.8, color: pal.page, backgroundColor: pal.ink, paddingHorizontal: 6, paddingVertical: 2, borderRadius: 4 }}>DAY-OF</Text>
            ) : null}
            {canEdit ? <Text style={{ color: pal.faint, fontSize: 12, marginLeft: 'auto' }}>Edit</Text> : null}
          </View>
          {person.role ? <Text style={{ color: pal.mute, fontSize: 14.5, marginTop: 2 }}>{person.role}</Text> : null}
        </View>
      </View>
      {(person.email || person.phone || person.hours) ? (
        <View style={{ width: '100%', gap: 2, paddingLeft: 44 }}>
          {person.email ? <Text style={{ color: pal.link || pal.ink, fontSize: 13.5 }}>{person.email}</Text> : null}
          {person.phone ? <Text style={{ color: pal.ink, fontSize: 14.5 }}>{person.phone}</Text> : null}
          {person.hours ? <Text style={{ color: pal.mute, fontSize: 13.5 }}>{person.hours}</Text> : null}
        </View>
      ) : null}
      {person.note ? (
        <Text style={{ color: pal.mute, fontSize: 13.5, lineHeight: 19, width: '100%', paddingLeft: 44 }}>
          {person.note}
        </Text>
      ) : null}
    </TouchableOpacity>
  );
}

export function MasterTourContacts({
  venueName,
  cityLine,
  contactsText,
  partyMembers,
  isDarkMode,
  sharePath,
  onEmailParty,
  onEnsureShareLink,
  shareLinkBusy = false,
  accent,
  onPersist,
  viewerMode = false,
  userId = '',
  userName = '',
  tour = null,
  dateKey = '',
}) {
  const pal = palette(isDarkMode);
  const [copied, setCopied] = useState(false);
  const [emailed, setEmailed] = useState(false);
  const [emailing, setEmailing] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [people, setPeople] = useState(() => parseContactsDoc(contactsText, parseSetlistText).people);
  const [categories, setCategories] = useState(() => parseContactsDoc(contactsText, parseSetlistText).categories);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState(null);
  const [namingCat, setNamingCat] = useState(false);
  const [newCatTitle, setNewCatTitle] = useState('');
  const [renamingKey, setRenamingKey] = useState(null);
  const [renameTitle, setRenameTitle] = useState('');
  const skipHydrate = useRef(false);

  useEffect(() => {
    if (skipHydrate.current) {
      skipHydrate.current = false;
      return;
    }
    const parsed = parseContactsDoc(contactsText, parseSetlistText);
    setPeople(parsed.people);
    setCategories(parsed.categories);
    setEditingId(null);
    setDraft(null);
    setNamingCat(false);
    setRenamingKey(null);
  }, [contactsText]);

  const persistDoc = (nextPeople, nextCats) => {
    skipHydrate.current = true;
    setPeople(nextPeople);
    setCategories(nextCats);
    onPersist?.(contactsToStructured(nextPeople, nextCats));
  };

  const persistPeople = (next) => persistDoc(next, categories);

  const photoByEmail = useMemo(() => {
    const map = {};
    (partyMembers || []).forEach((m) => {
      const email = String(m?.email || '').trim().toLowerCase();
      const uri = String(m?.photoURL || '').trim();
      if (email && (uri.startsWith('http://') || uri.startsWith('https://'))) map[email] = { ...m, photoURL: uri };
    });
    return map;
  }, [partyMembers]);
  const depts = useMemo(() => bucketContacts(people, categories), [people, categories]);
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const link = String(sharePath || '').trim();
  const canEdit = Boolean(onPersist) && !viewerMode;

  const copyShareLink = async () => {
    let url = link;
    if (!url && onEnsureShareLink) {
      try {
        url = String((await onEnsureShareLink()) || '').trim();
      } catch {
        url = '';
      }
    }
    if (!url) return;
    copyText(url);
    setCopied(true);
  };

  const emailParty = async () => {
    if (emailing || !onEmailParty) return;
    setEmailing(true);
    setEmailError('');
    try {
      await onEmailParty();
      setEmailed(true);
    } catch (err) {
      setEmailError(err?.message || 'Could not send emails. Try again.');
    } finally {
      setEmailing(false);
    }
  };

  const startEdit = (person) => {
    setEditingId(person.id);
    setDraft({ ...person });
  };

  const saveDraft = () => {
    if (!draft) return;
    const nextPerson = emptyContact(draft);
    if (!nextPerson.name && !nextPerson.email && !nextPerson.phone) {
      persistPeople(people.filter((p) => p.id !== draft.id));
    } else {
      const exists = people.some((p) => p.id === draft.id);
      persistPeople(exists ? people.map((p) => (p.id === draft.id ? nextPerson : p)) : [...people, nextPerson]);
    }
    setEditingId(null);
    setDraft(null);
  };

  const cancelEdit = () => {
    if (draft) {
      const saved = contactsFromStructured(contactsText, parseSetlistText);
      if (!saved.some((p) => p.id === draft.id)) {
        setPeople((prev) => prev.filter((p) => p.id !== draft.id));
      }
    }
    setEditingId(null);
    setDraft(null);
  };

  const deleteDraft = () => {
    if (!draft) return;
    persistPeople(people.filter((p) => p.id !== draft.id));
    setEditingId(null);
    setDraft(null);
  };

  const addInDept = (deptKey) => {
    const created = emptyContact({
      role: DEPT_ADD_ROLE[deptKey] || 'Contact',
      name: '',
      deptKey,
    });
    setPeople((prev) => [...prev, created]);
    setEditingId(created.id);
    setDraft(created);
  };

  const commitNewCategory = () => {
    const title = String(newCatTitle || '').trim();
    if (!title) {
      setNamingCat(false);
      setNewCatTitle('');
      return;
    }
    const created = emptyContactCategory({ title }, categories.length);
    persistDoc(people, [...categories, created]);
    setNamingCat(false);
    setNewCatTitle('');
  };

  const commitRename = (deptKey) => {
    const title = String(renameTitle || '').trim();
    setRenamingKey(null);
    if (!title) return;
    persistDoc(people, categories.map((c) => (c.id === deptKey ? { ...c, title } : c)));
  };

  const removeCategory = (deptKey) => {
    confirmRemoveCategory(() => {
      const nextPeople = people.map((p) => (p.deptKey === deptKey ? { ...p, deptKey: 'other' } : p));
      persistDoc(nextPeople, categories.filter((c) => c.id !== deptKey));
      if (draft?.deptKey === deptKey) {
        setDraft({ ...draft, deptKey: 'other' });
      }
    });
  };

  return (
    <View style={{ flex: 1, flexDirection: 'row', backgroundColor: pal.page, minHeight: 480, width: '100%' }}>
    <ScrollView style={{ flex: 1, minWidth: 0 }} contentContainerStyle={{ padding: 24, paddingBottom: 48 }}>
      <View style={{ flexDirection: 'row', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', borderBottomWidth: 2, borderBottomColor: accent || pal.teal, paddingBottom: 14 }}>
        <View>
          <Text style={[s.label, { color: pal.mute, marginBottom: 6 }]}>Contacts</Text>
          <Text style={[s.heroSm, { color: pal.ink }]}>{venueName || 'Venue contacts'}</Text>
          <Text style={{ color: pal.mute, marginTop: 4 }}>{cityLine || 'Pick a show day to load venue details.'}</Text>
        </View>
        <View style={{ flexDirection: 'row', gap: 22 }}>
          <View>
            <Text style={[s.label, { color: pal.faint }]}>Contacts</Text>
            <Text style={{ fontSize: 17, fontWeight: '500', color: pal.ink, fontFamily: FONT.mono }}>{people.length}</Text>
          </View>
          <View>
            <Text style={[s.label, { color: pal.faint }]}>Advance</Text>
            <Text style={{ fontSize: 17, fontWeight: '500', color: people.length ? pal.ok : pal.warn }}>{people.length ? 'On file' : 'Open'}</Text>
          </View>
        </View>
      </View>
      {!viewerMode ? (
      <View style={[s.card, { backgroundColor: pal.surf, borderColor: pal.line, marginTop: 18, flexDirection: 'row', flexWrap: 'wrap', gap: 12, alignItems: 'center' }]}>
        <View style={{ flex: 1, minWidth: 220 }}>
          <Text style={[s.label, { color: pal.faint, marginBottom: 4 }]}>Shared link — anyone with it can view</Text>
          <Text style={{ fontFamily: FONT.mono, fontSize: 14, color: pal.ink }} numberOfLines={1}>
            {link || (shareLinkBusy ? 'Creating link…' : `${origin}/calendar/share/…`)}
          </Text>
        </View>
        <TouchableOpacity onPress={copyShareLink} disabled={shareLinkBusy && !link} style={[s.outlineBtn, { borderColor: pal.ink, backgroundColor: pal.surf, opacity: shareLinkBusy && !link ? 0.6 : 1 }]}>
          <Text style={{ color: pal.ink, fontWeight: '500' }}>{copied ? 'Link copied ✓' : 'Copy link'}</Text>
        </TouchableOpacity>
        <TouchableOpacity onPress={emailParty} disabled={emailing} style={[s.primaryBtn, { backgroundColor: pal.ink, opacity: emailing ? 0.7 : 1 }]}>
          <Text style={{ color: pal.page, fontWeight: '600' }}>
            {emailing ? 'Sending…' : emailed ? 'Emailed to party ✓' : 'Email to tour party'}
          </Text>
        </TouchableOpacity>
        {emailError ? (
          <Text style={{ color: pal.warn || '#c0483c', width: '100%', fontSize: 13 }}>{emailError}</Text>
        ) : null}
      </View>
      ) : (
        <Text style={{ color: pal.mute, fontSize: 14, marginTop: 12 }}>Read only — this day’s venue contacts.</Text>
      )}
      <Text style={{ color: pal.mute, fontSize: 14.5, marginTop: 10, lineHeight: 20 }}>
        {viewerMode
          ? 'Venue staff change with the city. Tour manager stays on the sheet every night.'
          : 'Venue staff change with the city. Tour manager stays on the sheet every night. Tap a card to edit this day only. Copy link and email send a share URL — not your private /calendar editor.'}
      </Text>
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 16, marginTop: 22, width: '100%' }}>
        {depts.map((dp) => (
          <View key={dp.key} style={[s.card, { backgroundColor: pal.surf, borderColor: pal.line, ...CONTACT_COL_STYLE }]}>
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingHorizontal: 16, paddingVertical: 12, borderBottomWidth: 1, borderBottomColor: pal.lineSoft, gap: 8 }}>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, flex: 1, minWidth: 0 }}>
                <View style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: dp.hue, flexShrink: 0 }} />
                {dp.custom && canEdit && renamingKey === dp.key ? (
                  <TextInput
                    value={renameTitle}
                    onChangeText={setRenameTitle}
                    onBlur={() => commitRename(dp.key)}
                    onSubmitEditing={() => commitRename(dp.key)}
                    autoFocus
                    placeholder="Category name"
                    placeholderTextColor={pal.faint}
                    {...opsInputClass('blend')}
                    style={[inlineBlendInputStyle, { flex: 1, color: pal.ink, fontSize: 12, letterSpacing: 1.2, textTransform: 'uppercase', fontWeight: '500', paddingVertical: 2 }]}
                  />
                ) : (
                  <TouchableOpacity
                    disabled={!(dp.custom && canEdit)}
                    onPress={() => {
                      setRenamingKey(dp.key);
                      setRenameTitle(dp.title);
                    }}
                    style={{ flex: 1, minWidth: 0 }}
                  >
                    <Text style={[s.label, { color: pal.mute }]} numberOfLines={1}>{dp.title}</Text>
                  </TouchableOpacity>
                )}
              </View>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                <Text style={[s.meta, { color: dp.status === 'Confirmed' ? pal.warn : pal.faint, letterSpacing: 0.8 }]}>
                  {dp.status.toUpperCase()}
                </Text>
                {dp.custom && canEdit ? (
                  <TouchableOpacity onPress={() => removeCategory(dp.key)} accessibilityLabel={`Remove ${dp.title} category`} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
                    <Ionicons name="trash-outline" size={15} color={pal.faint} />
                  </TouchableOpacity>
                ) : null}
              </View>
            </View>
            {dp.people.map((p) => (
              <ContactPersonCard
                key={p.id}
                person={editingId === p.id && draft ? { ...p, ...draft, primary: p.primary } : p}
                pal={pal}
                hue={dp.hue}
                accent={accent}
                photoByEmail={photoByEmail}
                editing={editingId === p.id}
                canEdit={canEdit}
                onStartEdit={() => startEdit(p)}
                onChange={setDraft}
                onSave={saveDraft}
                onCancel={cancelEdit}
                onDelete={deleteDraft}
              />
            ))}
            {canEdit ? (
              <TouchableOpacity
                onPress={() => addInDept(dp.key)}
                style={{
                  margin: 12,
                  minHeight: 88,
                  minWidth: 200,
                  borderWidth: 1,
                  borderStyle: 'dashed',
                  borderColor: pal.line,
                  borderRadius: 10,
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 6,
                  paddingHorizontal: 16,
                }}
              >
                <Ionicons name="add" size={22} color={pal.mute} />
                <Text style={{ color: pal.mute, fontWeight: '600', fontSize: 14 }}>Add contact</Text>
              </TouchableOpacity>
            ) : null}
          </View>
        ))}
        {canEdit && namingCat ? (
          <View style={[s.card, { backgroundColor: pal.surf, borderColor: pal.line, ...CONTACT_COL_STYLE, minHeight: 200 }]}>
            <View style={{ paddingHorizontal: 16, paddingVertical: 12, borderBottomWidth: 1, borderBottomColor: pal.lineSoft }}>
              <Text style={[s.label, { color: pal.mute, marginBottom: 8 }]}>New category</Text>
              <TextInput
                value={newCatTitle}
                onChangeText={setNewCatTitle}
                onSubmitEditing={commitNewCategory}
                autoFocus
                placeholder="Name this category"
                placeholderTextColor={pal.faint}
                {...opsInputClass('well')}
                style={[
                  fieldWellStyle(pal, isDarkPal(pal)),
                  {
                    color: pal.ink,
                    fontSize: 15,
                    fontWeight: '600',
                    borderWidth: 1,
                    borderRadius: 8,
                    paddingHorizontal: 10,
                    paddingVertical: 8,
                  },
                ]}
              />
            </View>
            <View style={{ flexDirection: 'row', gap: 8, padding: 12 }}>
              <TouchableOpacity onPress={commitNewCategory} style={[s.primaryBtn, { backgroundColor: pal.ink, paddingVertical: 8, paddingHorizontal: 14 }]}>
                <Text style={{ color: pal.page, fontWeight: '600' }}>Save</Text>
              </TouchableOpacity>
              <TouchableOpacity
                onPress={() => { setNamingCat(false); setNewCatTitle(''); }}
                style={[s.outlineBtn, { borderColor: pal.line, paddingVertical: 8, paddingHorizontal: 14 }]}
              >
                <Text style={{ color: pal.ink, fontWeight: '500' }}>Cancel</Text>
              </TouchableOpacity>
            </View>
          </View>
        ) : null}
        {canEdit && !namingCat ? (
          <TouchableOpacity
            onPress={() => { setNamingCat(true); setNewCatTitle(''); }}
            accessibilityLabel="Add category"
            style={[
              s.card,
              {
                ...CONTACT_COL_STYLE,
                backgroundColor: 'transparent',
                borderColor: pal.line,
                borderStyle: 'dashed',
                minHeight: 200,
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                padding: 24,
              },
            ]}
          >
            <Ionicons name="add" size={26} color={pal.mute} />
            <Text style={{ color: pal.mute, fontWeight: '600', fontSize: 15 }}>Add category</Text>
            <Text style={{ color: pal.faint, fontSize: 13, textAlign: 'center', maxWidth: 220 }}>
              Name a group, then add people to it.
            </Text>
          </TouchableOpacity>
        ) : null}
      </View>
      {!people.length ? (
        <Text style={{ color: pal.mute, fontSize: 13.5, marginTop: 16 }}>
          Tour party lives under Tour Party. This sheet is venue-day contacts — house production, security, and local advance.
        </Text>
      ) : null}
    </ScrollView>
    <View style={{ width: 340, maxWidth: '42%', borderLeftWidth: 1, borderLeftColor: pal.line, backgroundColor: pal.surf }}>
      <ScrollView contentContainerStyle={{ padding: 16, paddingBottom: 32 }} keyboardShouldPersistTaps="handled">
        <SavedContactsCard
          pal={pal}
          isDarkMode={isDarkMode}
          userId={userId}
          userName={userName}
          tour={tour}
          dateKey={dateKey}
          canEdit={!viewerMode && Boolean(userId)}
          previewLimit={5}
        />
      </ScrollView>
    </View>
    </View>
  );
}

function ReceiptAttach({ pal, tourId, dateKey, row, onChange }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const mounted = useRef(true);
  const canEdit = useContext(OpsEditContext);
  useEffect(() => () => { mounted.current = false; }, []);
  if (Platform.OS !== 'web') return null;
  if (!canEdit && !row.receiptUrl) return null;
  const chip = { paddingVertical: 3, paddingHorizontal: 7, borderRadius: 5, borderWidth: 1, borderColor: pal.line };
  const chipText = { fontSize: 10, fontWeight: '600', letterSpacing: 0.6, textTransform: 'uppercase', color: pal.mute };
  const notify = (msg) => {
    if (Platform.OS === 'web' && typeof window !== 'undefined' && window.alert) window.alert(msg);
    else Alert.alert('Receipt', msg);
  };
  const attach = async (file) => {
    if (!file) return;
    const uid = getAuth().currentUser?.uid;
    if (!uid || !tourId || !dateKey) {
      notify('Sign in and pick a tour and show day before attaching a receipt.');
      return;
    }
    if (file.size > 15 * 1024 * 1024) {
      notify('That file is over 15 MB. Attach a smaller image or PDF.');
      return;
    }
    const rid = row.rid || `r${Date.now()}`;
    if (!row.rid) onChange({ rid }, rid);
    setBusy(true);
    try {
      const safeName = String(file.name || 'receipt').replace(/[^\w.\-]+/g, '_');
      const path = `calendarDayFiles/${uid}/${tourId}/${dateKey}/receipt_${Date.now()}_${safeName}`;
      const ref = storageRef(getStorage(), path);
      await uploadBytes(ref, file, file.type ? { contentType: file.type } : undefined);
      onChange({ receiptUrl: await getDownloadURL(ref), receiptName: file.name || 'Receipt' }, rid);
    } catch (e) {
      notify('The receipt could not be uploaded. Check your connection and try again.');
    } finally {
      if (mounted.current) setBusy(false);
    }
  };
  return (
    <View style={{ flexDirection: 'row', gap: 4, alignItems: 'center' }}>
      {row.receiptUrl ? (
        <>
          <TouchableOpacity onPress={() => Linking.openURL(row.receiptUrl)} style={chip}>
            <Text style={[chipText, { color: pal.teal }]}>Receipt</Text>
          </TouchableOpacity>
          {canEdit ? (
            <TouchableOpacity onPress={() => onChange({ receiptUrl: '', receiptName: '' }, row.rid)} style={chip}>
              <Text style={chipText}>×</Text>
            </TouchableOpacity>
          ) : null}
        </>
      ) : (
        <TouchableOpacity onPress={() => inputRef.current?.click?.()} style={chip} disabled={busy}>
          <Text style={chipText}>{busy ? '…' : '+ Receipt'}</Text>
        </TouchableOpacity>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/*,application/pdf"
        style={{ display: 'none' }}
        onChange={(ev) => {
          const file = ev.target.files && ev.target.files[0];
          ev.target.value = '';
          attach(file);
        }}
      />
    </View>
  );
}

export function MasterTourSettlement({
  tourId,
  dateKey,
  showDateKeys,
  tourDayMetaByDate,
  selectedTourFilterId,
  tour,
  merchGross = 0,
  seedText = '',
  merchSeedText = '',
  merchCatalogText = '',
  isDarkMode,
  accent,
  onSelectDate,
  onPersist,
  canEdit = true,
  finance = null,
  todayKey = '',
}) {
  const pal = palette(isDarkMode);
  const hue = accent || pal.teal;
  const gloss = glossyFill(hue, { isDarkMode, role: 'panel' });
  const cta = glossyFill(mixHex(hue, '#000000', 0.18), { isDarkMode: true, role: 'chip', selected: true });
  const showKey = resolveShowDate(dateKey, showDateKeys);
  const fallback = showKey && showKey !== dateKey;
  const bits = venueBits({ dateKey: showKey, tourDayMetaByDate, selectedTourFilterId, tour });
  const hydrateSheet = () => {
    if (seedText && String(seedText).trim()) return parseWorksheetSeed(seedText, emptySettle('club'));
    const stored = loadStoredJsonIfPresent('settle', tourId, showKey);
    if (stored) return stored;
    return emptySettle('club');
  };
  const [sheet, setSheet] = useState(hydrateSheet);
  const sheetRef = useRef(sheet);
  sheetRef.current = sheet;
  const keyRef = useRef(showKey);
  keyRef.current = showKey;
  const { width: winW } = useWindowDimensions();
  const narrowTiers = winW < 480;
  const persistSheet = useDebouncedCallback((next) => {
    onPersist?.(JSON.stringify(next), showKey);
  }, 450);
  const [note, setNote] = useState('');
  const [undo, setUndo] = useState(null);
  const [sent, setSent] = useState(false);
  const merchFromSheet = useMemo(() => {
    const stored = coerceMerchStore(loadStoredJson('merch', tourId, showKey, null));
    const split = merchSheetGrossSplit(stored.rows);
    if (split.total) return split;
    return merchSheetGrossSplit(parseMerchSheet(merchSeedText).rows);
  }, [tourId, showKey, sheet, merchSeedText]);
  const merchRateFromSheet = useMemo(() => {
    const stored = coerceMerchStore(loadStoredJson('merch', tourId, showKey, null));
    const catalog = parseMerchCatalog(merchCatalogText);
    const night = parseMerchSheet(merchSeedText);
    return {
      merchRate: NUM(stored.merchRate) || NUM(night.merchRate) || NUM(catalog.merchRate),
      musicRate: NUM(stored.musicRate) || NUM(night.musicRate) || NUM(catalog.musicRate),
    };
  }, [tourId, showKey, sheet, merchSeedText, merchCatalogText]);

  const settleBoot = useRef('');
  useEffect(() => {
    const key = `${tourId}:${showKey}`;
    if (settleBoot.current === key) return;
    const seeded = seedText && String(seedText).trim();
    if (seeded) {
      settleBoot.current = key;
      setSheet(parseWorksheetSeed(seedText, emptySettle('club')));
      setNote('');
      setUndo(null);
      setSent(false);
      return;
    }
    const stored = loadStoredJsonIfPresent('settle', tourId, showKey);
    if (stored) {
      settleBoot.current = key;
      setSheet(stored);
      setNote('');
      setUndo(null);
      setSent(false);
    }
  }, [tourId, showKey, seedText]);

  const freshSheet = (preset) => {
    const next = emptySettle(preset);
    ['promoter', 'venueRep', 'artistRep', 'dealRef', 'payMethod', 'signedArtist', 'signedVenue', 'signedDate'].forEach((k) => {
      next[k] = sheet[k] || '';
    });
    return next;
  };

  const patch = (next) => {
    if (!canEdit) return;
    settleBoot.current = `${tourId}:${showKey}`;
    setSheet(next);
    saveStoredJson('settle', tourId, showKey, next);
    persistSheet(next);
    setSent(false);
  };

  const sums = useMemo(() => {
    const t = settleTotals(sheet);
    const split = merchFromSheet && typeof merchFromSheet === 'object'
      ? merchFromSheet
      : { merch: Number(merchGross) || Number(sheet.merchGross) || 0, music: 0, total: Number(merchGross) || Number(sheet.merchGross) || 0 };
    const merchRate = Number(sheet.merchRate) || merchRateFromSheet.merchRate || 0;
    const musicRate = Number(sheet.musicRate) || merchRateFromSheet.musicRate || 0;
    const merchNet = split.merch * (1 - merchRate / 100) + split.music * (1 - musicRate / 100);
    return { ...t, merchNet, merchRate, musicRate, merchSplit: split, walkout: t.balance + merchNet };
  }, [sheet, merchGross, merchFromSheet, merchRateFromSheet]);

  const dupes = useMemo(() => findDuplicateExpenses(sheet.expenses), [sheet.expenses]);
  const dupeRows = useMemo(() => new Set(dupes.flatMap((d) => d.indexes)), [dupes]);
  const downloadCsv = () => {
    if (Platform.OS !== 'web' || typeof document === 'undefined' || typeof Blob === 'undefined') return;
    const csv = settlementToCsv(sheet, sums, { venue: bits.venueName, date: showKey, cityLine: bits.cityLine });
    const url = URL.createObjectURL(new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `settlement-${String(showKey || 'show').replace(/[^\w-]+/g, '_')}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(url), 0);
  };
  const [printNote, setPrintNote] = useState('');
  const printPdf = () => {
    const ok = openPrintWindow(settlementToPrintHtml(sheet, sums, { venue: bits.venueName, date: showKey, cityLine: bits.cityLine }));
    setPrintNote(ok ? '' : 'Allow pop-ups to print or save as PDF.');
  };
  const capLine = bits.cap ? ` · ${bits.cap} capacity` : '';
  const meta = [bits.cityLine, formatDateLong(showKey)].filter(Boolean).join(' · ') + capLine;

  return (
    <OpsEditContext.Provider value={canEdit}>
    <ScrollView style={{ flex: 1, backgroundColor: pal.page }} contentContainerStyle={{ padding: 24, paddingBottom: 48 }}>
      <View style={{ flexDirection: 'row', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', borderBottomWidth: 2, borderBottomColor: accent || pal.teal, paddingBottom: 14 }}>
        <View style={{ flex: 1, minWidth: 220 }}>
          <Text style={[s.label, { color: pal.mute, marginBottom: 6 }]}>Settlement</Text>
          {canEdit ? null : <ViewOnlyBadge pal={pal} />}
          <Text style={[s.hero, { color: pal.ink, fontSize: 32 }]}>{bits.venueName}</Text>
          <Text style={{ color: pal.mute, marginTop: 6, fontSize: 15 }}>{meta || 'Pick a show day.'}</Text>
          {fallback ? (
            <Text style={{ color: pal.warn, marginTop: 8, fontSize: 11, letterSpacing: 0.6, textTransform: 'uppercase' }}>
              {formatDateLong(dateKey)} is a day off — showing the next show instead.
            </Text>
          ) : null}
        </View>
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
          <View style={{ flexDirection: 'row', padding: 3, gap: 2, borderWidth: 1, borderColor: pal.line, borderRadius: 8, backgroundColor: pal.page }}>
            {Object.keys(DEAL_PRESETS).map((key) => {
              const on = sheet.preset === key;
              return (
                <TouchableOpacity
                  key={key}
                  disabled={!canEdit}
                  onPress={() => patch(freshSheet(key))}
                  style={{ paddingVertical: 6, paddingHorizontal: 10, borderRadius: 5, backgroundColor: on ? pal.ink : 'transparent' }}
                >
                  <Text style={{ fontSize: 11, fontWeight: '600', letterSpacing: 0.6, textTransform: 'uppercase', color: on ? pal.page : pal.mute }}>
                    {DEAL_PRESETS[key].label}
                  </Text>
                </TouchableOpacity>
              );
            })}
          </View>
          <FileImportButton
            label="Import settlement"
            accept=".csv,.tsv,.txt,text/csv,text/plain"
            pal={pal}
            onText={(text, name) => {
              const { next, ok, fields, exp, tierHits } = applySettleLines(sheet, linesFromSheetText(text));
              if (!ok) {
                setNote(`Nothing readable in ${name} — needs a label and a number per line.`);
                setUndo(null);
                return;
              }
              setUndo(sheet);
              patch(next);
              setNote(`Read ${name} — ${fields} deal field(s), ${tierHits} ticket tier(s), ${exp} expense line(s).`);
            }}
          />
          {canEdit ? (
            <>
              <TouchableOpacity onPress={() => patch(freshSheet(sheet.preset || 'club'))} style={[s.outlineBtn, { borderColor: pal.line }]}>
                <Text style={[s.kicker, { color: pal.mute }]}>Reset</Text>
              </TouchableOpacity>
              <TouchableOpacity onPress={() => { patch(emptySettle(sheet.preset || 'club')); setNote(''); }} style={[s.outlineBtn, { borderColor: pal.line }]}>
                <Text style={[s.kicker, { color: pal.mute }]}>Clear all</Text>
              </TouchableOpacity>
            </>
          ) : null}
          {Platform.OS === 'web' ? (
            <TouchableOpacity onPress={downloadCsv} accessibilityLabel="Download CSV" style={[s.outlineBtn, { borderColor: pal.line }]}>
              <Text style={[s.kicker, { color: pal.mute }]}>Download CSV</Text>
            </TouchableOpacity>
          ) : null}
          {Platform.OS === 'web' ? (
            <TouchableOpacity onPress={printPdf} accessibilityLabel="Print or save settlement as PDF" style={[s.outlineBtn, { borderColor: pal.line }]}>
              <Text style={[s.kicker, { color: pal.mute }]}>Print / PDF</Text>
            </TouchableOpacity>
          ) : null}
        </View>
      </View>
      <Text style={{ color: pal.mute, fontSize: 13.5, marginTop: 10 }}>
        {DEAL_PRESETS[sheet.preset]?.terms || 'Deal terms'} · start from the preset, import the promoter's sheet, or type over any figure
      </Text>
      {printNote ? <Text style={{ color: pal.warn, marginTop: 8, fontSize: 13 }}>{printNote}</Text> : null}
      {finance ? <RemindersStrip pal={pal} finance={finance} todayKey={todayKey} onSelectDate={onSelectDate} /> : null}
      {note ? (
        <View style={{ flexDirection: 'row', gap: 12, marginTop: 12, padding: 12, backgroundColor: pal.sunk, borderWidth: 1, borderColor: pal.teal, borderRadius: 9, alignItems: 'center' }}>
          <Text style={{ flex: 1, color: pal.ink, fontSize: 14.5 }}>{note}</Text>
          {undo && canEdit ? (
            <TouchableOpacity onPress={() => { patch(undo); setUndo(null); setNote('Import reverted.'); }} style={[s.outlineBtn, { borderColor: pal.teal }]}>
              <Text style={{ color: pal.teal, fontWeight: '600', fontSize: 13.5 }}>Undo</Text>
            </TouchableOpacity>
          ) : null}
        </View>
      ) : null}

      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 10, marginTop: 14 }}>
        {[
          ['Gross box office', formatUsd(sums.gross), null],
          ['Deductions', formatUsd((sums.gross || 0) - (sums.net || 0)), null],
          ['Net box office', formatUsd(sums.net), null],
          ['Expenses', formatUsd(sums.expenses), null],
          ['Artist earning', formatUsd(sums.earned), null],
          ['Paid to date', formatUsd(sums.paidToDate), null],
          ['Balance due', formatUsd(sums.balance), hue],
        ].map(([k, v, tint]) => (
          <View
            key={k}
            style={{
              flex: 1,
              minWidth: 128,
              paddingVertical: 12,
              paddingHorizontal: 14,
              borderRadius: 10,
              borderWidth: 1,
              borderColor: tint || pal.line,
              backgroundColor: pal.surf,
            }}
          >
            <Text style={[s.label, { color: pal.mute, marginBottom: 4 }]}>{k}</Text>
            <Text style={{ fontSize: tint ? 22 : 18, fontWeight: '600', color: tint ? pal.ink : pal.ink, letterSpacing: -0.3 }}>{v}</Text>
          </View>
        ))}
        <View style={{ justifyContent: 'center' }}>
          <Text style={{ fontSize: 11, letterSpacing: 0.6, textTransform: 'uppercase', fontWeight: '600', color: sent ? pal.teal : pal.mute }}>
            {sent ? 'Sent' : 'Draft'}
          </Text>
        </View>
      </View>

      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 18, marginTop: 8 }}>
        <View style={{ flex: 1, minWidth: 320, gap: 16 }}>
          <View style={[s.card, { backgroundColor: pal.surf, borderColor: pal.line, padding: 0, overflow: 'hidden' }]}>
            <Text style={[s.label, { color: pal.mute, paddingHorizontal: 18, paddingTop: 14, paddingBottom: 11 }]}>Parties &amp; deal reference</Text>
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 12, paddingHorizontal: 18, paddingBottom: 16 }}>
              {[['Promoter', 'promoter'], ['Venue rep', 'venueRep'], ['Artist rep', 'artistRep'], ['Deal memo ref', 'dealRef']].map(([lab, key]) => (
                <View key={key} style={{ flex: 1, minWidth: 140 }}>
                  <Text style={[s.label, { color: pal.mute, marginBottom: 4 }]}>{lab}</Text>
                  <TextInput editable={canEdit}
                    value={sheet[key] || ''}
                    onChangeText={(v) => patch({ ...sheet, [key]: v })}
                    placeholder={lab}
                    placeholderTextColor={pal.faint}
                    {...opsInputClass('well')}
                    style={[insetBlendStyle(isDarkMode, pal, { wellFill: pal.sunk }), { padding: 10, borderRadius: 8, borderWidth: 1, color: pal.ink, fontSize: 15 }]}
                  />
                </View>
              ))}
            </View>
          </View>
          <View style={[s.card, { backgroundColor: pal.surf, borderColor: pal.line, padding: 0, overflow: 'hidden' }]}>
            <Text style={[s.label, { color: pal.mute, paddingHorizontal: 18, paddingTop: 14, paddingBottom: 11 }]}>Deal terms</Text>
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 12, paddingHorizontal: 18, paddingBottom: 14 }}>
              {[['Guarantee', 'guarantee'], ['Split %', 'split'], ['Deposit paid', 'deposit'], ['Promoter profit', 'promoterProfit']].map(([lab, key]) => (
                <View key={key} style={{ flex: 1, minWidth: 90 }}>
                  <Text style={[s.label, { color: pal.mute, marginBottom: 4 }]}>{lab}</Text>
                  <Field pal={pal} value={sheet[key]} onChange={(v) => patch({ ...sheet, [key]: v })} />
                </View>
              ))}
            </View>
            <View style={{ paddingHorizontal: 18, paddingVertical: 14, borderTopWidth: 1, borderTopColor: pal.lineSoft }}>
              <View style={{ flexDirection: 'row', gap: 12, flexWrap: 'wrap' }}>
                <View style={{ flex: 1, minWidth: 110 }}>
                  <Text style={[s.label, { color: pal.mute }]}>Merch gross</Text>
                  <Text style={{ fontSize: 16, fontWeight: '600', color: pal.ink, marginTop: 4 }}>{formatUsd(sums.merchSplit?.merch || merchGross || sheet.merchGross)}</Text>
                </View>
                <View style={{ flex: 1, minWidth: 110 }}>
                  <Text style={[s.label, { color: pal.mute }]}>Merch cut %</Text>
                  <Field pal={pal} value={sheet.merchRate || merchRateFromSheet.merchRate} onChange={(v) => patch({ ...sheet, merchRate: v })} />
                </View>
                <View style={{ flex: 1, minWidth: 110 }}>
                  <Text style={[s.label, { color: pal.mute }]}>Music gross</Text>
                  <Text style={{ fontSize: 16, fontWeight: '600', color: pal.ink, marginTop: 4 }}>{formatUsd(sums.merchSplit?.music)}</Text>
                </View>
                <View style={{ flex: 1, minWidth: 110 }}>
                  <Text style={[s.label, { color: pal.mute }]}>Music cut %</Text>
                  <Field pal={pal} value={sheet.musicRate || merchRateFromSheet.musicRate} onChange={(v) => patch({ ...sheet, musicRate: v })} />
                </View>
                <View style={{ flex: 1, minWidth: 110 }}>
                  <Text style={[s.label, { color: pal.mute }]}>Deducted</Text>
                  <Text style={{ fontSize: 16, fontWeight: '600', color: pal.ink, marginTop: 4 }}>{formatUsd((sums.merchSplit?.total || 0) - (sums.merchNet || 0))}</Text>
                </View>
              </View>
              <Text style={{ color: pal.mute, fontSize: 14, marginTop: 8 }}>Apparel/other use the merch cut. Music uses the music cut. Totals follow the merch sheet.</Text>
            </View>
          </View>

          <View style={[s.card, { backgroundColor: pal.surf, borderColor: pal.line, padding: 0, overflow: 'hidden' }]}>
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingHorizontal: 18, paddingVertical: 14, gap: 12, flexWrap: 'wrap' }}>
              <Text style={[s.label, { color: pal.mute }]}>Ticket income</Text>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <Text style={{ color: pal.mute, fontSize: 14.5 }}>
                  {sums.paid.toLocaleString()} paid{bits.cap ? ` of ${bits.cap}` : ''}{sums.paid && bits.cap ? ` (${Math.round((sums.paid / NUM_SAFE(bits.cap)) * 100)}%)` : ''}
                </Text>
                {canEdit ? (
                  <TouchableOpacity
                    onPress={() => patch({ ...sheet, tiers: [...(sheet.tiers || []), { label: 'New tier', avail: 0, qty: 0, price: 0 }] })}
                    style={[s.outlineBtn, { borderColor: pal.line, paddingVertical: 4, paddingHorizontal: 9 }]}
                  >
                    <Text style={[s.kicker, { color: pal.teal }]}>+ Line</Text>
                  </TouchableOpacity>
                ) : null}
              </View>
            </View>
            <View style={[s.tierHead, { paddingHorizontal: 18 }]}>
              <Text style={[s.colH, { color: pal.mute, flex: 1 }]}>Tier</Text>
              <Text style={[s.colH, { color: pal.mute, width: 74, textAlign: 'right' }]}>Avail</Text>
              <Text style={[s.colH, { color: pal.mute, width: 74, textAlign: 'right' }]}>Sold</Text>
              <Text style={[s.colH, { color: pal.mute, width: 74, textAlign: 'right' }]}>Price</Text>
              {narrowTiers ? null : <Text style={[s.colH, { color: pal.mute, width: 96, textAlign: 'right' }]}>Total</Text>}
            </View>
            {(sheet.tiers || []).map((t, i) => (
              <View key={`${t.label}-${i}`} style={[s.tierRow, { borderTopColor: pal.lineSoft, paddingHorizontal: 18 }]}>
                <TextInput editable={canEdit}
                  value={t.label}
                  onChangeText={(v) => {
                    const tiers = sheet.tiers.slice();
                    tiers[i] = { ...t, label: v };
                    patch({ ...sheet, tiers });
                  }}
                  placeholder="Tier"
                  placeholderTextColor={pal.faint}
                  {...opsInputClass('blend')}
                  style={[inlineBlendInputStyle, { flex: 1, color: pal.ink, fontSize: 15 }]}
                />
                <Field pal={pal} align="right" value={t.avail} onChange={(v) => {
                  const tiers = sheet.tiers.slice();
                  tiers[i] = { ...t, avail: v };
                  patch({ ...sheet, tiers });
                }} />
                <Field pal={pal} align="right" value={t.qty} onChange={(v) => {
                  const tiers = sheet.tiers.slice();
                  tiers[i] = { ...t, qty: v };
                  patch({ ...sheet, tiers });
                }} />
                <Field pal={pal} align="right" value={t.price} onChange={(v) => {
                  const tiers = sheet.tiers.slice();
                  tiers[i] = { ...t, price: v };
                  patch({ ...sheet, tiers });
                }} />
                {narrowTiers ? null : (
                  <Text style={{ width: 96, textAlign: 'right', color: pal.ink, fontSize: 15 }}>
                    {formatUsd((Number(t.qty) || 0) * (Number(t.price) || 0))}
                  </Text>
                )}
              </View>
            ))}
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', paddingHorizontal: 18, paddingVertical: 14, borderTopWidth: 1, borderTopColor: pal.line, backgroundColor: pal.sunk }}>
              <Text style={[s.label, { color: pal.mute, flex: 1 }]}>
                Gross box office{sums.potential > 0 ? ` · ${Math.round((sums.gross / sums.potential) * 100)}% of ${formatUsd(sums.potential)} potential` : ''}
              </Text>
              <Text style={{ fontSize: 18, fontWeight: '600', color: pal.ink }}>{formatUsd(sums.gross)}</Text>
            </View>
          </View>

          <View style={[s.card, { backgroundColor: pal.surf, borderColor: pal.line, padding: 0, overflow: 'hidden' }]}>
            <Text style={[s.label, { color: pal.mute, paddingHorizontal: 18, paddingTop: 14, paddingBottom: 11 }]}>Deductions off the top</Text>
            {[
              ['Facility fee per paid ticket', 'facility', formatUsd(sums.facility)],
              ['Sales tax %', 'tax', formatUsd(sums.tax)],
              ['Credit card fees %', 'card', formatUsd(sums.card)],
            ].map(([label, key, amt]) => (
              <View key={key} style={[s.tierRow, { borderTopColor: pal.lineSoft, paddingHorizontal: 18 }]}>
                <Text style={{ flex: 1, color: pal.ink, fontSize: 15 }}>{label}</Text>
                <Field pal={pal} align="right" value={sheet[key]} onChange={(v) => patch({ ...sheet, [key]: v })} />
                <Text style={{ width: 100, textAlign: 'right', color: pal.mute, fontSize: 15 }}>{amt}</Text>
              </View>
            ))}
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', paddingHorizontal: 18, paddingVertical: 14, borderTopWidth: 1, borderTopColor: pal.line, backgroundColor: pal.sunk }}>
              <Text style={[s.label, { color: pal.mute }]}>Net box office</Text>
              <Text style={{ fontSize: 18, fontWeight: '600', color: pal.ink }}>{formatUsd(sums.net)}</Text>
            </View>
          </View>
        </View>

        <View style={{ flex: 1, minWidth: 320, gap: 16 }}>
          <View style={[s.card, { backgroundColor: pal.surf, borderColor: pal.line, padding: 0, overflow: 'hidden' }]}>
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', paddingHorizontal: 18, paddingVertical: 14 }}>
              <Text style={[s.label, { color: pal.mute }]}>Show expenses</Text>
              {canEdit ? (
                <TouchableOpacity
                  onPress={() => patch({ ...sheet, expenses: [...(sheet.expenses || []), { label: '', amount: 0 }] })}
                  style={[s.outlineBtn, { borderColor: pal.line, paddingVertical: 4, paddingHorizontal: 9 }]}
                >
                  <Text style={[s.kicker, { color: pal.mute }]}>+ Line</Text>
                </TouchableOpacity>
              ) : null}
            </View>
            {(sheet.expenses || []).map((x, i) => (
              <View key={`x-${i}`} style={[s.tierRow, { borderTopColor: pal.lineSoft, paddingHorizontal: 18 }]}>
                <TextInput editable={canEdit}
                  value={x.label}
                  onChangeText={(v) => {
                    const expenses = sheet.expenses.slice();
                    expenses[i] = { ...x, label: v };
                    patch({ ...sheet, expenses });
                  }}
                  placeholder="Expense line"
                  placeholderTextColor={pal.faint}
                  {...opsInputClass('blend')}
                  style={[inlineBlendInputStyle, { flex: 1, color: pal.ink, fontSize: 15, paddingVertical: 4 }]}
                />
                <Field pal={pal} align="right" value={x.amount} onChange={(v) => {
                  const expenses = sheet.expenses.slice();
                  expenses[i] = { ...x, amount: v };
                  patch({ ...sheet, expenses });
                }} />
                <ReceiptAttach
                  pal={pal}
                  tourId={tourId}
                  dateKey={showKey}
                  row={x}
                  onChange={(fields, rid) => {
                    if (keyRef.current !== showKey) return;
                    const cur = sheetRef.current;
                    const expenses = (cur.expenses || []).slice();
                    let at = rid ? expenses.findIndex((e) => e.rid === rid) : -1;
                    if (at < 0 && fields.rid) at = i;
                    if (at < 0 || !expenses[at]) return;
                    expenses[at] = { ...expenses[at], ...fields };
                    patch({ ...cur, expenses });
                  }}
                />
                <TouchableOpacity
                  disabled={!canEdit}
                  onPress={() => {
                    const order = ['approved', 'pending', 'disputed'];
                    const expenses = sheet.expenses.slice();
                    expenses[i] = { ...x, status: order[(order.indexOf(x.status || 'approved') + 1) % order.length] };
                    patch({ ...sheet, expenses });
                  }}
                  style={{ paddingVertical: 3, paddingHorizontal: 7, borderRadius: 5, borderWidth: 1, borderColor: x.status === 'disputed' || dupeRows.has(i) ? pal.warn : pal.line }}
                >
                  <Text style={{ fontSize: 10, fontWeight: '600', letterSpacing: 0.6, textTransform: 'uppercase', color: x.status === 'disputed' ? pal.warn : pal.mute }}>
                    {x.status || 'approved'}
                  </Text>
                </TouchableOpacity>
                {canEdit ? (
                  <TouchableOpacity onPress={() => patch({ ...sheet, expenses: sheet.expenses.filter((_, j) => j !== i) })}>
                    <Text style={{ color: pal.mute, fontSize: 16, width: 22, textAlign: 'center' }}>×</Text>
                  </TouchableOpacity>
                ) : null}
              </View>
            ))}
            {dupes.map((d) => (
              <Text key={`dupe-${d.label}-${d.amount}`} style={{ color: pal.warn, fontSize: 13, paddingHorizontal: 18, paddingVertical: 6 }}>
                Possible duplicate: {d.label} ${d.amount.toLocaleString('en-US', { maximumFractionDigits: 2 })}
              </Text>
            ))}
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', paddingHorizontal: 18, paddingVertical: 14, borderTopWidth: 1, borderTopColor: pal.line, backgroundColor: pal.sunk }}>
              <Text style={[s.label, { color: pal.mute }]}>Total expenses</Text>
              <Text style={{ fontSize: 18, fontWeight: '600', color: pal.ink }}>{formatUsd(sums.expenses)}</Text>
            </View>
          </View>

          <View style={[s.card, { backgroundColor: pal.surf, borderColor: pal.line, padding: 0, overflow: 'hidden' }]}>
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', paddingHorizontal: 18, paddingVertical: 14 }}>
              <Text style={[s.label, { color: pal.mute }]}>Payments received</Text>
              {canEdit ? (
                <TouchableOpacity
                  onPress={() => patch({ ...sheet, payments: [...(sheet.payments || []), { label: '', amount: 0 }] })}
                  style={[s.outlineBtn, { borderColor: pal.line, paddingVertical: 4, paddingHorizontal: 9 }]}
                >
                  <Text style={[s.kicker, { color: pal.mute }]}>+ Line</Text>
                </TouchableOpacity>
              ) : null}
            </View>
            <View style={[s.tierRow, { borderTopColor: pal.lineSoft, paddingHorizontal: 18 }]}>
              <Text style={{ flex: 1, color: pal.ink, fontSize: 15 }}>Deposit</Text>
              <Text style={{ color: pal.mute, fontSize: 15 }}>{formatUsd(sheet.deposit)}</Text>
            </View>
            {(sheet.payments || []).map((p, i) => (
              <View key={`p-${i}`} style={[s.tierRow, { borderTopColor: pal.lineSoft, paddingHorizontal: 18 }]}>
                <TextInput editable={canEdit}
                  value={p.label}
                  onChangeText={(v) => {
                    const payments = sheet.payments.slice();
                    payments[i] = { ...p, label: v };
                    patch({ ...sheet, payments });
                  }}
                  placeholder="Payment (wire, check, cash)"
                  placeholderTextColor={pal.faint}
                  {...opsInputClass('blend')}
                  style={[inlineBlendInputStyle, { flex: 1, color: pal.ink, fontSize: 15, paddingVertical: 4 }]}
                />
                <Field pal={pal} align="right" value={p.amount} onChange={(v) => {
                  const payments = sheet.payments.slice();
                  payments[i] = { ...p, amount: v };
                  patch({ ...sheet, payments });
                }} />
                {canEdit ? (
                  <TouchableOpacity onPress={() => patch({ ...sheet, payments: sheet.payments.filter((_, j) => j !== i) })}>
                    <Text style={{ color: pal.mute, fontSize: 16, width: 22, textAlign: 'center' }}>×</Text>
                  </TouchableOpacity>
                ) : null}
              </View>
            ))}
            <View style={{ paddingHorizontal: 18, paddingVertical: 12, borderTopWidth: 1, borderTopColor: pal.lineSoft }}>
              <Text style={[s.label, { color: pal.mute, marginBottom: 4 }]}>Payment method for the balance</Text>
              <TextInput editable={canEdit}
                value={sheet.payMethod || ''}
                onChangeText={(v) => patch({ ...sheet, payMethod: v })}
                placeholder="Wire, check, cash at settlement…"
                placeholderTextColor={pal.faint}
                {...opsInputClass('well')}
                style={[insetBlendStyle(isDarkMode, pal, { wellFill: pal.sunk }), { padding: 10, borderRadius: 8, borderWidth: 1, color: pal.ink, fontSize: 15 }]}
              />
            </View>
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', paddingHorizontal: 18, paddingVertical: 14, borderTopWidth: 1, borderTopColor: pal.line, backgroundColor: pal.sunk }}>
              <Text style={[s.label, { color: pal.mute }]}>Paid to date</Text>
              <Text style={{ fontSize: 18, fontWeight: '600', color: pal.ink }}>{formatUsd(sums.paidToDate)}</Text>
            </View>
          </View>

        </View>

        <View style={{
          minWidth: 300,
          flex: 1,
          borderRadius: 12,
          padding: 20,
          borderWidth: 1,
          borderLeftWidth: 3,
          borderLeftColor: hue,
          overflow: 'hidden',
          ...(Platform.OS === 'web' ? glossyWebStyle(gloss) : { backgroundColor: gloss.backgroundColor, borderColor: gloss.borderColor }),
        }}>
          <Text style={[s.label, { color: gloss.mute, marginBottom: 8 }]}>Bottom line</Text>
          {[
            ['Net after expenses', formatUsd(sums.afterCosts)],
            ['Artist share on split', formatUsd(sums.splitEarn)],
            ...(sums.splitPoint ? [['Split point', formatUsd(sums.splitPoint)]] : []),
          ].map(([k, v]) => (
            <View key={k} style={{ flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 7, borderTopWidth: 1, borderTopColor: gloss.line }}>
              <Text style={{ color: gloss.mute, fontSize: 15 }}>{k}</Text>
              <Text style={{ color: gloss.ink, fontSize: 14 }}>{v}</Text>
            </View>
          ))}
          <Text style={{ color: sums.onSplit ? (isDarkMode ? pal.teal : mixHex(hue, '#000000', 0.42)) : gloss.mute, fontSize: 11, letterSpacing: 0.6, textTransform: 'uppercase', paddingVertical: 8 }}>
            {sums.onSplit ? 'Split beats the guarantee' : 'Guarantee holds'}
          </Text>
          {[
            ['Artist earning', formatUsd(sums.earned)],
            ['Overage', formatUsd(sums.overage)],
            ['Merch net to artist', formatUsd(sums.merchNet)],
            ['Paid to date', formatUsd(sums.paidToDate)],
            ...(sums.disputedCount ? [['Expenses disputed', `${sums.disputedCount} · ${formatUsd(sums.disputedTotal)}`]] : []),
          ].map(([k, v]) => (
            <View key={k} style={{ flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 7, borderTopWidth: 1, borderTopColor: gloss.line }}>
              <Text style={{ color: gloss.mute, fontSize: 15 }}>{k}</Text>
              <Text style={{ color: gloss.ink, fontSize: 14 }}>{v}</Text>
            </View>
          ))}
          <View style={{ paddingTop: 18, marginTop: 10, borderTopWidth: 1, borderTopColor: gloss.line }}>
            <Text style={[s.label, { color: gloss.mute, marginBottom: 5 }]}>Balance due at settlement</Text>
            <Text style={{ fontSize: 36, fontWeight: '600', color: gloss.ink, letterSpacing: -0.8 }}>{formatUsd(sums.balance)}</Text>
            <Text style={{ color: gloss.mute, fontSize: 14.5, marginTop: 6 }}>Walkout with merch {formatUsd(sums.walkout)}</Text>
          </View>
          {canEdit ? (
          <TouchableOpacity
            onPress={() => {
              if (!canEdit) return;
              setSent(true);
              onPersist?.(JSON.stringify({ ...sheet, sentAt: new Date().toISOString() }), showKey);
            }}
            style={{
              marginTop: 16,
              paddingVertical: 12,
              borderRadius: 9,
              overflow: 'hidden',
              ...(sent
                ? { backgroundColor: pal.sunk }
                : (Platform.OS === 'web' ? glossyWebStyle(cta) : { backgroundColor: cta.backgroundColor })),
            }}
          >
            <Text style={{ textAlign: 'center', fontWeight: '600', color: sent ? pal.mute : cta.ink }}>
              {sent ? 'Marked sent ✓' : 'Mark settlement sent'}
            </Text>
          </TouchableOpacity>
          ) : null}
          <Text style={[s.label, { color: gloss.mute, marginTop: 18, marginBottom: 8 }]}>Notes</Text>
          <TextInput editable={canEdit}
            value={sheet.notes || ''}
            onChangeText={(v) => patch({ ...sheet, notes: v })}
            placeholder="Deductions disputed, comps counted as paid…"
            placeholderTextColor={gloss.mute}
            multiline
            {...opsInputClass('well')}
            style={[
              insetBlendStyle(isDarkMode, pal, { wellFill: gloss.well }),
              { minHeight: 88, padding: 12, borderRadius: 9, borderWidth: 1, color: gloss.ink, fontSize: 15 },
            ]}
          />
          <Text style={[s.label, { color: gloss.mute, marginTop: 18, marginBottom: 8 }]}>Sign-off</Text>
          {[['Artist / tour manager', 'signedArtist'], ['Promoter / venue', 'signedVenue'], ['Date signed', 'signedDate']].map(([lab, key]) => (
            <TextInput editable={canEdit}
              key={key}
              value={sheet[key] || ''}
              onChangeText={(v) => patch({ ...sheet, [key]: v })}
              placeholder={lab}
              placeholderTextColor={gloss.mute}
              {...opsInputClass('well')}
              style={[
                insetBlendStyle(isDarkMode, pal, { wellFill: gloss.well }),
                { padding: 12, borderRadius: 9, borderWidth: 1, color: gloss.ink, fontSize: 15, marginBottom: 8 },
              ]}
            />
          ))}
        </View>
      </View>
    </ScrollView>
    </OpsEditContext.Provider>
  );
}

function NUM_SAFE(cap) {
  const n = parseInt(String(cap || '').replace(/[^\d]/g, ''), 10);
  return n > 0 ? n : 0;
}

export function MasterTourExpenses({
  tourId,
  tourName = '',
  dateKey,
  showDateKeys,
  tourDayMetaByDate,
  selectedTourFilterId,
  seedText = '',
  seedLegText = '',
  isDarkMode,
  accent,
  onSelectDate,
  onPersist,
  onLoadLedgerDate,
  canEdit = true,
  finance = null,
  onSaveBudget,
}) {
  const pal = palette(isDarkMode);
  const [scope, setScope] = useState('day');
  const storeDate = scope === 'leg' ? 'leg' : dateKey;
  const seedForScope = scope === 'leg' ? seedLegText : seedText;
  const hydrateLedger = () => {
    if (seedForScope && String(seedForScope).trim()) return parseWorksheetSeed(seedForScope, emptyLedger());
    const stored = loadStoredJsonIfPresent('ledger', tourId, storeDate);
    if (stored) return stored;
    return emptyLedger();
  };
  const [ledger, setLedger] = useState(hydrateLedger);
  const persistLedger = useDebouncedCallback((next) => {
    onPersist?.(JSON.stringify(next), scope === 'leg' ? 'leg' : storeDate);
  }, 450);
  const [note, setNote] = useState('');
  const [undo, setUndo] = useState(null);
  const ledgerBoot = useRef('');
  useEffect(() => {
    const key = `${tourId}:${storeDate}`;
    if (ledgerBoot.current === key) return;
    const seeded = seedForScope && String(seedForScope).trim();
    if (seeded) {
      ledgerBoot.current = key;
      setLedger(parseWorksheetSeed(seedForScope, emptyLedger()));
      setNote('');
      setUndo(null);
      return;
    }
    const stored = loadStoredJsonIfPresent('ledger', tourId, storeDate);
    if (stored) {
      ledgerBoot.current = key;
      setLedger(stored);
      setNote('');
      setUndo(null);
    }
  }, [tourId, storeDate, seedForScope]);
  const patch = (next) => {
    if (!canEdit) return;
    ledgerBoot.current = `${tourId}:${storeDate}`;
    setLedger(next);
    saveStoredJson('ledger', tourId, storeDate, next);
    persistLedger(next);
  };
  const totals = ledgerTotals(ledger);
  const netColor = netLedgerColor(totals.net, { positive: pal.ok, negative: pal.alert, zero: pal.ink });
  const persistOtherDate = (key, nextLedger) => {
    if (!canEdit) return;
    saveStoredJson('ledger', tourId, key, nextLedger);
    onPersist?.(JSON.stringify(nextLedger), key);
  };
  const tourDays = (showDateKeys || []).filter(Boolean);
  const canSpanDays = Boolean(selectedTourFilterId) && tourDays.length > 0;
  const repeatsAcrossDays = (row) => row?.kind === 'line' || row?.kind === 'dayrate' || row?.kind === 'perdiem';
  useEffect(() => {
    if (canSpanDays || scope !== 'day') return;
    const expense = ledger.expense || [];
    const resetsWithoutTour = (r) => (r?.kind === 'line' || r?.kind === 'dayrate') && r.span && r.span !== PER_DIEM_SPANS.this;
    if (!expense.some(resetsWithoutTour)) return;
    patch({
      ...ledger,
      expense: expense.map((r) => (
        resetsWithoutTour(r)
          ? { ...r, span: PER_DIEM_SPANS.this, dates: dateKey ? [dateKey] : [] }
          : r
      )),
    });
  }, [canSpanDays, scope, dateKey, ledger]);
  // Other days load from this browser first, then from their saved doc. A day with no loaded copy is
  // skipped: writing an empty ledger over it would wipe that day's real entries.
  const loadOtherLedger = async (key) => {
    const local = loadStoredJsonIfPresent('ledger', tourId, key);
    if (local) return local;
    if (!onLoadLedgerDate) return null;
    try {
      const text = await onLoadLedgerDate(key);
      if (text == null) return null;
      if (!String(text).trim()) return emptyLedger();
      const parsed = JSON.parse(text);
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch {
      return null;
    }
  };
  const syncRow = async (row, previousDates) => {
    if (!canEdit || scope !== 'day' || !repeatsAcrossDays(row) || !row.repeatId) return;
    const targets = perDiemSyncTargets({ row, sourceDateKey: dateKey, tourDateKeys: showDateKeys, previousDates });
    const loaded = {};
    for (const key of targets) loaded[key] = await loadOtherLedger(key);
    syncPerDiemCopies({
      row,
      sourceDateKey: dateKey,
      tourDateKeys: showDateKeys,
      previousDates,
      loadLedger: (key) => loaded[key] || null,
      saveLedger: persistOtherDate,
    });
  };
  const add = (side) => {
    if (!canEdit) return;
    if (side === 'expense' && scope === 'day') {
      patch({ ...ledger, expense: [...(ledger.expense || []), emptyExpenseLine(dateKey)] });
      return;
    }
    patch({ ...ledger, [side]: [...(ledger[side] || []), { label: '', amount: 0 }] });
  };
  const addPerDiem = () => {
    if (!canEdit || !canSpanDays) return;
    patch({ ...ledger, expense: [...(ledger.expense || []), emptyPerDiem(dateKey)] });
  };
  const addDayRate = () => {
    if (!canEdit || !canSpanDays) return;
    patch({ ...ledger, expense: [...(ledger.expense || []), emptyDayRate(dateKey)] });
  };
  const edit = (side, i, patchRow) => {
    if (!canEdit) return;
    if (!canSpanDays && (patchRow.span === PER_DIEM_SPANS.all || patchRow.span === PER_DIEM_SPANS.dates)) return;
    const rows = (ledger[side] || []).slice();
    const prev = rows[i] || {};
    const nextRow = { ...prev, ...patchRow };
    if (nextRow.kind === 'perdiem') {
      nextRow.rateType = PER_DIEM_RATE_TYPES.fixed;
      const total = patchRow.amount != null ? patchRow.amount : perDiemBase(nextRow);
      nextRow.amount = total;
      nextRow.rate = total;
    }
    if (repeatsAcrossDays(nextRow)) {
      if (!nextRow.repeatId) nextRow.repeatId = `${nextRow.kind === 'perdiem' ? 'pd' : nextRow.kind === 'dayrate' ? 'dr' : 'ln'}-${Math.random().toString(36).slice(2, 10)}`;
      if (nextRow.kind === 'dayrate') nextRow.rateType = PER_DIEM_RATE_TYPES.day;
      if (patchRow.span === PER_DIEM_SPANS.this) nextRow.dates = dateKey ? [dateKey] : [];
      if (patchRow.span === PER_DIEM_SPANS.all) nextRow.dates = [...(showDateKeys || [])];
      if (patchRow.span === PER_DIEM_SPANS.dates && dateKey && !(nextRow.dates || []).includes(dateKey)) {
        nextRow.dates = [...(nextRow.dates || []), dateKey];
      }
      if (nextRow.kind !== 'perdiem') nextRow.amount = perDiemBase(nextRow);
    }
    rows[i] = nextRow;
    patch({ ...ledger, [side]: rows });
    if (repeatsAcrossDays(nextRow)) {
      syncRow(nextRow, prev.dates || resolvePerDiemDates(prev, dateKey, showDateKeys));
    }
  };
  const togglePerDiemDate = (i, key) => {
    const row = (ledger.expense || [])[i] || {};
    if (key === dateKey) return;
    const set = new Set(resolvePerDiemDates({ ...row, span: PER_DIEM_SPANS.dates }, dateKey, showDateKeys));
    if (set.has(key)) set.delete(key);
    else set.add(key);
    edit('expense', i, { span: PER_DIEM_SPANS.dates, dates: [...set].sort() });
  };
  const remove = (side, i) => {
    if (!canEdit) return;
    const row = (ledger[side] || [])[i];
    patch({ ...ledger, [side]: (ledger[side] || []).filter((_, j) => j !== i) });
    if (repeatsAcrossDays(row) && row.repeatId) {
      syncRow({ ...row, span: PER_DIEM_SPANS.this, dates: dateKey ? [dateKey] : [] }, row.dates?.length ? row.dates : resolvePerDiemDates(row, dateKey, showDateKeys));
    }
  };
  const ledgerDayLabel = (key) => {
    const parts = String(key || '').split('-').map(Number);
    if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) return formatDateShort(key);
    const d = new Date(parts[0], parts[1] - 1, parts[2]);
    if (Number.isNaN(d.getTime())) return formatDateShort(key);
    return `${d.toLocaleDateString('en-US', { weekday: 'short' })} ${formatDateShort(key)}`;
  };
  const daySpanEditor = (row, i) => {
    const span = canSpanDays ? (row.span || PER_DIEM_SPANS.this) : PER_DIEM_SPANS.this;
    return (
    <>
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
        {pill(span === PER_DIEM_SPANS.this, 'This day', () => edit('expense', i, { span: PER_DIEM_SPANS.this }))}
        {pill(span === PER_DIEM_SPANS.all, 'All days', () => edit('expense', i, { span: PER_DIEM_SPANS.all }), !canSpanDays)}
        {pill(span === PER_DIEM_SPANS.dates, 'Choose days', () => edit('expense', i, { span: PER_DIEM_SPANS.dates }), !canSpanDays)}
      </View>
      {canSpanDays && span === PER_DIEM_SPANS.dates ? (
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
          {(showDateKeys || []).map((key) => {
            const on = resolvePerDiemDates(row, dateKey, showDateKeys).includes(key);
            const locked = key === dateKey;
            return (
              <TouchableOpacity
                key={key}
                disabled={locked || !canEdit}
                onPress={() => togglePerDiemDate(i, key)}
                style={[s.outlineBtn, { paddingVertical: 4, paddingHorizontal: 8, borderColor: on ? pal.ink : pal.line, opacity: locked ? 0.7 : 1 }]}
              >
                <Text style={[s.kicker, { color: on ? pal.ink : pal.mute }]}>{ledgerDayLabel(key)}</Text>
              </TouchableOpacity>
            );
          })}
        </View>
      ) : null}
    </>
    );
  };
  const pill = (active, label, onPress, lockedByTour = false) => {
    const disabled = lockedByTour || !canEdit;
    const button = (
      <TouchableOpacity
        disabled={disabled}
        pointerEvents={disabled ? 'none' : 'auto'}
        onPress={disabled ? undefined : onPress}
        accessibilityState={{ disabled }}
        style={[s.outlineBtn, { paddingVertical: 4, paddingHorizontal: 8, borderColor: active ? pal.ink : pal.line, backgroundColor: active ? pal.sunk : 'transparent', opacity: disabled ? 0.35 : 1 }]}
      >
        <Text style={[s.kicker, { color: disabled ? pal.faint : (active ? pal.ink : pal.mute) }]}>{label}</Text>
      </TouchableOpacity>
    );
    if (!lockedByTour) return button;
    return <HoverNote pal={pal} text={SELECT_TOUR_HINT}>{button}</HoverNote>;
  };
  const col = (side, title, rows) => {
    const totalColor = side === 'income' ? pal.ok : pal.alert;
    return (
    <View style={[s.card, s.ledgerCard, { backgroundColor: pal.surf, borderColor: pal.line, padding: 0, overflow: 'hidden', flex: 1, minWidth: 300, minHeight: 380 }]}>
      <View style={{ paddingHorizontal: 18, paddingVertical: 14 }}>
        <Text style={[s.label, { color: pal.mute }]}>{title}</Text>
      </View>
      <View style={{ flex: 1 }}>
      {(rows || []).map((r, i) => (
        <View key={`${side}-${i}`} style={{ borderTopWidth: 1, borderTopColor: pal.lineSoft }}>
          <View style={[s.tierRow, { borderTopWidth: 0, paddingHorizontal: 18 }]}>
            <TextInput editable={canEdit}
              value={r.label}
              onChangeText={(v) => edit(side, i, { label: v })}
              placeholder={side === 'income' ? 'Income line' : 'e.g. Fuel, snacks, laundry'}
              placeholderTextColor={pal.faint}
              {...opsInputClass('blend')}
              style={[inlineBlendInputStyle, { flex: 1, color: pal.ink, fontSize: 15 }]}
            />
            {r.kind === 'perdiem' ? (
              <Field pal={pal} align="right" color={totalColor} value={r.amount} onChange={(v) => edit(side, i, { amount: v })} />
            ) : r.kind === 'dayrate' ? (
              <Field pal={pal} align="right" color={totalColor} value={r.rate} onChange={(v) => edit(side, i, { rate: v })} />
            ) : r.kind === 'line' && r.rateType !== PER_DIEM_RATE_TYPES.fixed ? (
              <Text style={{ fontSize: 15, fontWeight: '600', color: totalColor, minWidth: 64, textAlign: 'right' }}>
                {formatUsd(perDiemAmount(r))}
              </Text>
            ) : r.kind === 'line' ? (
              <Field pal={pal} align="right" color={totalColor} value={r.rate} onChange={(v) => edit(side, i, { rate: v, rateType: PER_DIEM_RATE_TYPES.fixed })} />
            ) : (
              <Field pal={pal} align="right" color={totalColor} value={r.amount} onChange={(v) => edit(side, i, { amount: v })} />
            )}
            {canEdit ? (
              <TouchableOpacity onPress={() => remove(side, i)}>
                <Text style={{ color: pal.mute, fontSize: 16, width: 22, textAlign: 'center' }}>×</Text>
              </TouchableOpacity>
            ) : null}
          </View>
          {r.kind === 'line' && scope === 'day' ? (
            <View style={{ paddingHorizontal: 18, paddingBottom: 12, gap: 8 }}>
              <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
                {pill(r.rateType === PER_DIEM_RATE_TYPES.hourly, 'Hourly', () => edit(side, i, { rateType: PER_DIEM_RATE_TYPES.hourly }))}
                {pill(r.rateType === PER_DIEM_RATE_TYPES.day, 'Day rate', () => edit(side, i, { rateType: PER_DIEM_RATE_TYPES.day }))}
                {pill((r.rateType || PER_DIEM_RATE_TYPES.fixed) === PER_DIEM_RATE_TYPES.fixed, 'Fixed total', () => edit(side, i, { rateType: PER_DIEM_RATE_TYPES.fixed }))}
              </View>
              {r.rateType === PER_DIEM_RATE_TYPES.hourly || r.rateType === PER_DIEM_RATE_TYPES.day ? (
                <View style={{ flexDirection: 'row', gap: 8, alignItems: 'center' }}>
                  <Text style={[s.kicker, { color: pal.mute }]}>{r.rateType === PER_DIEM_RATE_TYPES.hourly ? 'Rate' : 'Day'}</Text>
                  <Field pal={pal} align="right" value={r.rate} onChange={(v) => edit(side, i, { rate: v })} flex={1} />
                  {r.rateType === PER_DIEM_RATE_TYPES.hourly ? (
                    <>
                      <Text style={[s.kicker, { color: pal.mute }]}>Hours</Text>
                      <Field pal={pal} align="right" value={r.hours} onChange={(v) => edit(side, i, { hours: v })} flex={1} />
                    </>
                  ) : null}
                </View>
              ) : null}
              {daySpanEditor(r, i)}
            </View>
          ) : null}
          {r.kind === 'perdiem' && scope === 'day' ? (
            <View style={{ paddingHorizontal: 18, paddingBottom: 12, gap: 8 }}>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                <Text style={[s.kicker, { color: pal.mute }]}>People</Text>
                <Field pal={pal} align="right" value={r.people} onChange={(v) => edit(side, i, { people: v })} flex={1} />
              </View>
              {daySpanEditor(r, i)}
            </View>
          ) : null}
          {r.kind === 'dayrate' && scope === 'day' ? (
            <View style={{ paddingHorizontal: 18, paddingBottom: 12, gap: 8 }}>
              {daySpanEditor(r, i)}
            </View>
          ) : null}
        </View>
      ))}
      {canEdit ? (
      <View style={[s.ledgerAddRow, { borderTopColor: pal.lineSoft }]}>
        <TouchableOpacity
          onPress={() => add(side)}
          style={[s.ledgerAddCell, side === 'expense' && scope === 'day' ? { borderRightWidth: 1, borderRightColor: pal.lineSoft } : null]}
        >
          <Text style={{ color: pal.teal, fontSize: 15 }}>+ Line</Text>
        </TouchableOpacity>
        {side === 'expense' && scope === 'day' ? (
          <HoverNote pal={pal} text={canSpanDays ? '' : SELECT_TOUR_HINT} style={{ flex: 1 }}>
            <TouchableOpacity
              disabled={!canSpanDays}
              pointerEvents={canSpanDays ? 'auto' : 'none'}
              onPress={addPerDiem}
              accessibilityState={{ disabled: !canSpanDays }}
              style={[s.ledgerAddCell, { borderRightWidth: 1, borderRightColor: pal.lineSoft, opacity: canSpanDays ? 1 : 0.35 }]}
            >
              <Text style={{ color: canSpanDays ? pal.teal : pal.faint, fontSize: 15 }}>+ Per Diem</Text>
            </TouchableOpacity>
          </HoverNote>
        ) : null}
        {side === 'expense' && scope === 'day' ? (
          <HoverNote pal={pal} text={canSpanDays ? '' : SELECT_TOUR_HINT} style={{ flex: 1 }}>
            <TouchableOpacity
              disabled={!canSpanDays}
              pointerEvents={canSpanDays ? 'auto' : 'none'}
              onPress={addDayRate}
              accessibilityState={{ disabled: !canSpanDays }}
              style={[s.ledgerAddCell, { opacity: canSpanDays ? 1 : 0.35 }]}
            >
              <Text style={{ color: canSpanDays ? pal.teal : pal.faint, fontSize: 15 }}>+ Day rate</Text>
            </TouchableOpacity>
          </HoverNote>
        ) : null}
      </View>
      ) : null}
      </View>
      <View style={s.ledgerCardFoot}>
        <View style={{ flexDirection: 'row', justifyContent: 'space-between', paddingHorizontal: 18, paddingVertical: 14, borderTopWidth: 1, borderTopColor: pal.line, backgroundColor: pal.sunk }}>
          <Text style={[s.label, { color: pal.mute }]}>Total {title.toLowerCase()}</Text>
          <Text style={{ fontSize: 18, fontWeight: '600', color: totalColor }}>{formatUsd(side === 'income' ? totals.income : totals.expense)}</Text>
        </View>
      </View>
    </View>
    );
  };
  return (
    <OpsEditContext.Provider value={canEdit}>
    <ScrollView style={{ flex: 1, backgroundColor: pal.page }} contentContainerStyle={{ padding: 24, paddingBottom: 48 }}>
      <View style={{ flexDirection: 'row', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', borderBottomWidth: 2, borderBottomColor: accent || pal.teal, paddingBottom: 14 }}>
        <View>
          <Text style={[s.heroSm, { color: pal.ink }]}>{scope === 'leg' ? 'Leg ledger' : 'Day ledger'}</Text>
          {canEdit ? null : <ViewOnlyBadge pal={pal} />}
          <Text style={{ color: pal.mute, marginTop: 4 }}>{scope === 'leg' ? 'Whole tour' : formatDateLong(dateKey) || 'Pick a day'}</Text>
        </View>
        <View style={{ flexDirection: 'row', gap: 22, flexWrap: 'wrap', alignItems: 'center' }}>
          <Stat pal={pal} k="Income" v={formatUsd(totals.income)} color={pal.ok} />
          <Stat pal={pal} k="Expenses" v={formatUsd(totals.expense)} color={pal.alert} />
          <Stat pal={pal} k="Net" v={formatUsd(totals.net)} color={netColor} />
          <TouchableOpacity onPress={() => setScope((p) => (p === 'day' ? 'leg' : 'day'))} style={[s.outlineBtn, { borderColor: pal.ink }]}>
            <Text style={{ color: pal.ink, fontWeight: '600' }}>{scope === 'day' ? 'View whole leg' : 'View this day'}</Text>
          </TouchableOpacity>
          <FileImportButton
            label="Import sheet"
            accept=".csv,.tsv,.txt,text/csv"
            pal={pal}
            solid
            onText={(text, name) => {
              const rows = String(text).split(/\r?\n/).map(splitLine);
              const parsed = parseSheetRows(rows);
              const total = parsed.income.length + parsed.expense.length;
              if (!total) {
                setNote(`Nothing readable in ${name} — needs a description and an amount.`);
                return;
              }
              setUndo(ledger);
              patch({
                income: [...(ledger.income || []), ...parsed.income],
                expense: [...(ledger.expense || []), ...parsed.expense],
              });
              setNote(`Read ${name} — ${parsed.income.length} income, ${parsed.expense.length} expense.`);
            }}
          />
        </View>
      </View>
      {note ? (
        <View style={{ flexDirection: 'row', gap: 12, marginTop: 12, padding: 12, borderWidth: 1, borderColor: pal.teal, borderRadius: 10, backgroundColor: pal.sunk }}>
          <Text style={{ flex: 1, color: pal.ink }}>{note}</Text>
          {undo && canEdit ? (
            <TouchableOpacity onPress={() => { patch(undo); setUndo(null); setNote('Import reverted.'); }}>
              <Text style={{ color: pal.teal, fontWeight: '600' }}>Undo import</Text>
            </TouchableOpacity>
          ) : null}
        </View>
      ) : null}
      <Text style={{ color: pal.mute, fontSize: 14, marginTop: 8 }}>CSV or TSV with a label and an amount. Nothing is invented.</Text>
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 18, marginTop: 18 }}>
        {col('income', 'Income', ledger.income)}
        {col('expense', 'Expenses', ledger.expense)}
      </View>

      {finance ? (
        <TourFinancePanel
          key={tourId}
          pal={pal}
          finance={finance}
          tourName={tourName}
          canEdit={canEdit}
          onSaveBudget={onSaveBudget}
          accent={accent || pal.teal}
        />
      ) : null}

      {canEdit ? (
        <TourFundSettlementSection
          tourId={tourId}
          tourName={tourName}
          isDarkMode={isDarkMode}
          accent={accent || pal.teal}
        />
      ) : null}
    </ScrollView>
    </OpsEditContext.Provider>
  );
}

function splitLine(line) {
  if (line.includes('\t')) return line.split('\t').map((c) => c.trim());
  return line.split(',').map((c) => c.trim());
}

function Stat({ pal, k, v, color }) {
  return (
    <View>
      <Text style={[s.label, { color: pal.mute }]}>{k}</Text>
      <Text style={{ fontSize: 21, fontWeight: '600', color: color || pal.ink }}>{v}</Text>
    </View>
  );
}

async function blobFromMerchPhotoPick(file) {
  if (file) {
    return {
      blob: file,
      name: String(file.name || `merch-${Date.now()}.jpg`),
      contentType: file.type || 'image/jpeg',
    };
  }
  const result = await DocumentPicker.getDocumentAsync({
    type: 'image/*',
    copyToCacheDirectory: true,
    multiple: false,
  });
  if (result.canceled || !result.assets?.[0]) return null;
  const asset = result.assets[0];
  if (!asset.uri) throw new Error('Could not read that photo.');
  const response = await fetch(asset.uri);
  const blob = await response.blob();
  return {
    blob,
    name: String(asset.name || `merch-${Date.now()}.jpg`),
    contentType: blob.type || 'image/jpeg',
  };
}

async function uploadMerchPhotoToStorage(tourId, dateKey, itemId, file) {
  const uid = getAuth().currentUser?.uid;
  if (!uid || !tourId || !dateKey) {
    throw new Error('Sign in and pick a tour day before uploading merch photos.');
  }
  const picked = await blobFromMerchPhotoPick(file);
  if (!picked) return '';
  const safeName = String(picked.name || `merch-${Date.now()}.jpg`).replace(/[^\w.\-]+/g, '_');
  const path = `calendarDayFiles/${uid}/${tourId}/${dateKey}/merch_${String(itemId || Date.now())}_${safeName}`;
  const ref = storageRef(getStorage(), path);
  await uploadBytes(ref, picked.blob, { contentType: picked.contentType || 'image/jpeg' });
  return getDownloadURL(ref);
}

function MerchPhotoWell({ uri, uploading, pal, onPickFile, onPressNative }) {
  const inputRef = useRef(null);
  const canEdit = useContext(OpsEditContext);
  const openPicker = () => {
    if (uploading) return;
    if (Platform.OS === 'web') {
      inputRef.current?.click?.();
      return;
    }
    onPressNative?.();
  };
  const well = uploading ? (
    <View style={{ width: 96, height: 96, borderRadius: 8, backgroundColor: pal.sunk, alignItems: 'center', justifyContent: 'center' }}>
      <ActivityIndicator size="small" color={pal.teal} />
    </View>
  ) : uri ? (
    <Image source={{ uri }} style={{ width: 96, height: 96, borderRadius: 8, backgroundColor: pal.sunk }} />
  ) : (
    <View style={{ width: 96, height: 96, borderRadius: 8, backgroundColor: pal.sunk, alignItems: 'center', justifyContent: 'center' }}>
      <Ionicons name="camera-outline" size={32} color={pal.mute} />
    </View>
  );
  if (!canEdit) return <View>{well}</View>;
  return (
    <View>
      <View style={{ width: 96, height: 96, position: 'relative' }}>
        <TouchableOpacity
          onPress={openPicker}
          accessibilityLabel="Add merch photo"
          disabled={uploading}
          style={Platform.OS === 'web' ? { cursor: uploading ? 'wait' : 'pointer' } : null}
        >
          {well}
        </TouchableOpacity>
        {Platform.OS === 'web' ? (
          <input
            ref={inputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif,image/*"
            disabled={uploading}
            aria-label="Add merch photo"
            title="Add photo"
            onChange={(ev) => {
              const file = ev.target.files && ev.target.files[0];
              ev.target.value = '';
              if (file) onPickFile(file);
            }}
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              width: 96,
              height: 96,
              opacity: 0,
              fontSize: 96,
              cursor: uploading ? 'wait' : 'pointer',
              zIndex: 4,
              pointerEvents: uploading ? 'none' : 'auto',
            }}
          />
        ) : null}
      </View>
      <TouchableOpacity onPress={openPicker} disabled={uploading} accessibilityLabel={uri ? 'Change photo' : 'Upload photo'}>
        <Text style={{ color: pal.teal, fontSize: 12, fontWeight: '600', marginTop: 4 }}>
          {uploading ? 'Uploading…' : (uri ? 'Change photo' : 'Upload photo')}
        </Text>
      </TouchableOpacity>
    </View>
  );
}

export function MasterTourMerch({
  tourId,
  dateKey,
  showDateKeys,
  merchText,
  catalogText = '',
  venueName,
  cityLine,
  isDarkMode,
  accent,
  onPersist,
  onPersistCatalog,
  onLoadMerchDate,
  canEdit = true,
}) {
  const pal = palette(isDarkMode);
  const showKey = resolveShowDate(dateKey, showDateKeys);
  const prevKey = previousShowKey(showKey, showDateKeys);
  const catalog = useMemo(() => parseMerchCatalog(catalogText), [catalogText]);
  const nightSheet = useMemo(() => parseMerchSheet(merchText), [merchText]);
  const persistMerch = useDebouncedCallback((nextRows, nextOpts) => {
    onPersist?.(merchRowsToSetlistDoc(nextRows, nextOpts), showKey);
    onPersistCatalog?.(serializeMerchCatalog(nextRows, nextOpts));
  }, 450);
  const [rows, setRows] = useState(() => mergeMerchNight({
    catalogItems: catalog.items,
    nightRows: nightSheet.rows,
    previousRows: coerceMerchStore(loadStoredJsonIfPresent('merch', tourId, prevKey)).rows,
  }));
  const [cut, setCut] = useState(() => NUM(nightSheet.merchRate) || NUM(catalog.merchRate));
  const [musicCut, setMusicCut] = useState(() => NUM(nightSheet.musicRate) || NUM(catalog.musicRate));
  const [taxOnTop, setTaxOnTop] = useState(() => !!(nightSheet.taxOnTop || catalog.taxOnTop));
  const [taxPct, setTaxPct] = useState(() => NUM(nightSheet.taxRate) || NUM(catalog.taxRate));
  const [carriedFrom, setCarriedFrom] = useState('');
  const [uploadingId, setUploadingId] = useState('');
  const [sizeTabById, setSizeTabById] = useState({});
  const [openDetailsById, setOpenDetailsById] = useState({});
  const [openMoreById, setOpenMoreById] = useState({});
  const [userOptions, setUserOptions] = useState(() => defaultMerchUserOptions());
  const [editingOption, setEditingOption] = useState(null);
  const merchBootKey = useRef('');
  const userOptionsRef = useRef(userOptions);
  userOptionsRef.current = userOptions;
  const rowsRef = useRef(rows);
  rowsRef.current = rows;
  const merchUid = currentMerchUserId();
  const sheetOpts = (extra = {}) => ({
    merchRate: extra.merchRate != null ? extra.merchRate : cut,
    musicRate: extra.musicRate != null ? extra.musicRate : musicCut,
    taxOnTop: extra.taxOnTop != null ? extra.taxOnTop : taxOnTop,
    taxRate: extra.taxRate != null ? extra.taxRate : taxPct,
    ...merchSheetCustomsFromUserOptions(extra.userOptions || userOptionsRef.current),
    ...(extra.customCategories ? { customCategories: extra.customCategories } : {}),
    ...(extra.customTypes ? { customTypes: extra.customTypes } : {}),
    ...(extra.customSizes ? { customSizes: extra.customSizes } : {}),
    ...(extra.customAvail ? { customAvail: extra.customAvail } : {}),
    ...(extra.customSellers ? { customSellers: extra.customSellers } : {}),
    ...(extra.customColors ? { customColors: extra.customColors } : {}),
  });
  useEffect(() => {
    const key = `${tourId}:${showKey}`;
    if (merchBootKey.current === key) return;
    let cancelled = false;
    const boot = async () => {
      const stored = coerceMerchStore(loadStoredJsonIfPresent('merch', tourId, showKey));
      const hasNight = (nightSheet.rows.length && !merchCountsAreEmpty(nightSheet.rows))
        || (stored.rows.length && !merchCountsAreEmpty(stored.rows));
      let previousRows = coerceMerchStore(loadStoredJsonIfPresent('merch', tourId, prevKey)).rows;
      if (!hasNight && !previousRows.length && prevKey && onLoadMerchDate) {
        const prevText = await onLoadMerchDate(prevKey);
        if (cancelled) return;
        previousRows = parseMerchSheet(prevText).rows;
      }
      if (cancelled) return;
      let next = hasNight
        ? (stored.rows.length ? stored.rows : nightSheet.rows)
        : mergeMerchNight({
          catalogItems: catalog.items,
          nightRows: nightSheet.rows,
          previousRows,
        });
      if (!next.length) next = [emptyMerchRow({ id: `m-${Date.now()}` })];
      merchBootKey.current = key;
      setRows(next);
      setCut(NUM(stored.merchRate) || NUM(nightSheet.merchRate) || NUM(catalog.merchRate));
      setMusicCut(NUM(stored.musicRate) || NUM(nightSheet.musicRate) || NUM(catalog.musicRate));
      setTaxOnTop(!!(stored.taxOnTop || nightSheet.taxOnTop || catalog.taxOnTop));
      setTaxPct(NUM(stored.taxRate) || NUM(nightSheet.taxRate) || NUM(catalog.taxRate));
      const uid = currentMerchUserId();
      const loaded = await loadMerchUserOptionsWithSource(uid);
      if (cancelled) return;
      let opts = loaded.options;
      opts = mergeTourCustomsIntoUserOptions(opts, stored);
      opts = mergeTourCustomsIntoUserOptions(opts, nightSheet);
      opts = mergeTourCustomsIntoUserOptions(opts, catalog);
      userOptionsRef.current = opts;
      setUserOptions(opts);
      if (loaded.source === 'default' || JSON.stringify(opts) !== JSON.stringify(loaded.options)) {
        persistMerchUserOptions(uid, opts);
      }
      const didCarry = !hasNight && previousRows.length && merchCountsAreEmpty(nightSheet.rows);
      setCarriedFrom(didCarry ? prevKey : '');
    };
    boot();
    return () => { cancelled = true; };
  }, [tourId, showKey, prevKey, nightSheet, catalog, onLoadMerchDate]);
  const persistState = (nextRows, extra = {}) => {
    if (!canEdit) return;
    const opts = sheetOpts(extra);
    rowsRef.current = nextRows;
    setRows(nextRows);
    setCut(NUM(opts.merchRate));
    setMusicCut(NUM(opts.musicRate));
    setTaxOnTop(!!opts.taxOnTop);
    setTaxPct(NUM(opts.taxRate));
    saveStoredJson('merch', tourId, showKey, { rows: nextRows, ...opts });
    persistMerch(nextRows, opts);
  };
  const patch = (next, extra) => persistState(next, extra || {});
  const patchRow = (i, nextRow) => {
    const current = rows[i];
    const id = current?.id;
    if (id) {
      const detailsWereOpen = openDetailsById[id] == null
        ? !merchRowHasContent(current)
        : !!openDetailsById[id];
      if (detailsWereOpen) {
        setOpenDetailsById((prev) => ({ ...prev, [id]: true }));
      }
    }
    const n = rows.slice();
    n[i] = emptyMerchRow({ ...current, ...nextRow, id: current?.id || nextRow.id });
    patch(n);
  };
  const patchSize = (i, variantKey, nextSize) => {
    const r = rows[i];
    const sizes = (r.sizes || []).map((z) => (
      merchVariantKey(z) === variantKey
        ? emptyMerchSize({ ...z, ...nextSize, size: z.size, color: z.color })
        : z
    ));
    patchRow(i, { sizes });
  };
  const addItem = () => {
    if (!canEdit) return;
    const blank = (rows || []).find((r) => (
      !String(r.name || '').trim()
      && !String(r.photoUrl || '').trim()
      && !merchActiveSizes(r)
      && !NUM(r.price)
      && !NUM(r.cogs)
      && merchCountsAreEmpty([r])
    ));
    if (blank?.id) {
      setOpenDetailsById((prev) => ({ ...prev, [blank.id]: true }));
      return;
    }
    const id = `m-${Date.now()}`;
    setOpenDetailsById((prev) => ({ ...prev, [id]: true }));
    patch([...(rows || []), emptyMerchRow({ id })]);
  };
  const removeItem = (i) => {
    if (!canEdit) return;
    const id = rows[i]?.id;
    patch((rows || []).filter((_, idx) => idx !== i));
    if (id) {
      setOpenDetailsById((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
    }
  };
  const split = merchSheetGrossSplit(rows);
  const venueOwed = rows.reduce((sum, r) => {
    const rate = NUM(r.cutOverride) || (String(r.category || r.kind) === 'Music' ? musicCut : cut);
    return sum + merchLineGross(r) * NUM(rate) / 100;
  }, 0);
  const net = split.total - venueOwed;
  const uploadRowPhoto = async (i, file) => {
    const r = rows[i];
    if (!canEdit || !r) return;
    setUploadingId(r.id || String(i));
    try {
      const url = await uploadMerchPhotoToStorage(tourId, showKey, r.id || `m-${i}`, file);
      if (!url) return;
      patchRow(i, { photoUrl: url });
    } catch (e) {
      Alert.alert('Merch photo', e?.message || 'Could not upload that photo.');
    } finally {
      setUploadingId('');
    }
  };
  const taxFor = (r) => NUM(r.taxOverride) || NUM(taxPct);
  const withTax = (price, r) => (taxOnTop ? NUM(price) * (1 + taxFor(r) / 100) : NUM(price));
  const merchRowHasContent = (r) => !!(
    String(r?.name || '').trim()
    || String(r?.photoUrl || '').trim()
    || merchActiveSizes(r)
    || (r?.colors || []).length
    || NUM(r?.price)
    || NUM(r?.cogs)
    || !merchCountsAreEmpty([r])
  );
  const merchItemVisible = (r, i) => {
    if (merchRowHasContent(r) || openDetailsById[r.id]) return true;
    const firstBlank = (rows || []).findIndex((row) => !merchRowHasContent(row));
    return firstBlank === i;
  };
  const filledCount = (rows || []).filter(merchRowHasContent).length;
  const rememberOptions = (nextOpts) => {
    if (!canEdit) return userOptionsRef.current;
    userOptionsRef.current = nextOpts;
    setUserOptions(nextOpts);
    persistMerchUserOptions(merchUid, nextOpts);
    return nextOpts;
  };
  const addUserOption = (field, label, i, rowPatch, sizeGroup) => {
    if (!canEdit) return;
    const nextOpts = rememberOptions(addMerchUserOption(userOptionsRef.current, { field, label, sizeGroup }));
    const extra = merchSheetCustomsFromUserOptions(nextOpts);
    const currentRows = rowsRef.current.slice();
    if (i == null) {
      patch(currentRows, extra);
      return;
    }
    const cur = currentRows[i];
    const applied = typeof rowPatch === 'function' ? rowPatch(cur) : { ...cur, ...(rowPatch || {}) };
    currentRows[i] = emptyMerchRow({ ...applied, id: cur?.id });
    persistState(currentRows, extra);
  };
  const rewriteUserOption = async ({ field, from, to }) => {
    if (!canEdit) return;
    const change = { field, from, to };
    const nextOpts = rememberOptions(rewriteMerchUserOptions(userOptionsRef.current, change));
    const extra = merchSheetCustomsFromUserOptions(nextOpts);
    const nextRows = rewriteMerchRowsOption(rows, change);
    persistState(nextRows, extra);
    rewriteAllLocalMerchSheets(change);
    setEditingOption(null);
    const otherKeys = (showDateKeys || []).filter((key) => key && key !== showKey);
    for (const key of otherKeys) {
      try {
        const local = coerceMerchStore(loadStoredJsonIfPresent('merch', tourId, key));
        if (local.rows.length) {
          onPersist?.(merchRowsToSetlistDoc(local.rows, { ...local, ...rewriteMerchSheetCustoms(local, change) }), key);
          continue;
        }
        if (!onLoadMerchDate || !onPersist) continue;
        const text = await onLoadMerchDate(key);
        if (!text) continue;
        const sheet = parseMerchSheet(text);
        const rewritten = rewriteMerchRowsOption(sheet.rows, change);
        const nextSheet = { ...sheet, ...rewriteMerchSheetCustoms(sheet, change), rows: rewritten };
        saveStoredJson('merch', tourId, key, nextSheet);
        onPersist(merchRowsToSetlistDoc(rewritten, nextSheet), key);
      } catch {
        /* other-night rewrite is best-effort */
      }
    }
  };
  const dashNum = (n) => (NUM(n) ? String(n) : '—');
  const dashUsd = (n) => (NUM(n) ? formatUsd(n) : '—');
  return (
    <OpsEditContext.Provider value={canEdit}>
    <ScrollView style={{ flex: 1, backgroundColor: pal.page }} contentContainerStyle={{ padding: 24, paddingBottom: 48 }}>
      <View style={{ flexDirection: 'row', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', borderBottomWidth: 2, borderBottomColor: accent || pal.teal, paddingBottom: 14 }}>
        <View>
          <Text style={[s.label, { color: pal.mute, marginBottom: 5 }]}>Merch</Text>
          {canEdit ? null : <ViewOnlyBadge pal={pal} />}
          <Text style={[s.heroSm, { color: pal.ink, fontSize: 27 }]}>{venueName || 'Merch'}</Text>
          <Text style={{ color: pal.mute, marginTop: 5 }}>
            {cityLine || 'First card is a new item. Add a type, size, seller, or For if you need one. Hover the i for what a field means.'}
          </Text>
          {carriedFrom ? (
            <Text style={{ color: pal.teal, marginTop: 6, fontSize: 13 }}>
              Count In carried from {formatDateShort(carriedFrom)} leftover.
            </Text>
          ) : null}
        </View>
        <View style={{ flexDirection: 'row', gap: 24, flexWrap: 'wrap' }}>
          <Stat pal={pal} k="Merch gross" v={formatUsd(split.merch)} />
          <Stat pal={pal} k="Music gross" v={formatUsd(split.music)} />
          <Stat pal={pal} k="Venue owed" v={formatUsd(venueOwed)} />
          <Stat pal={pal} k="Net to tour" v={formatUsd(net)} />
        </View>
      </View>
      <View style={[s.card, { backgroundColor: pal.surf, borderColor: pal.line, marginTop: 18, flexDirection: 'row', flexWrap: 'wrap', gap: 16, alignItems: 'flex-end' }]}>
        <LabeledBox pal={pal} label="Venue merch cut %" tip={MERCH_TIPS.merchCut}>
          <Field pal={pal} value={cut} onChange={(v) => patch(rows, { merchRate: v })} />
        </LabeledBox>
        <LabeledBox pal={pal} label="Music cut %" tip={MERCH_TIPS.musicCut}>
          <Field pal={pal} value={musicCut} onChange={(v) => patch(rows, { musicRate: v })} />
        </LabeledBox>
        <LabeledBox pal={pal} label="Tax %" tip={MERCH_TIPS.tax}>
          <Field pal={pal} value={taxPct} onChange={(v) => patch(rows, { taxRate: v })} />
        </LabeledBox>
        <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 1 }}>
          <TouchableOpacity
            disabled={!canEdit}
            onPress={() => patch(rows, { taxOnTop: !taxOnTop })}
            style={[s.outlineBtn, { borderColor: taxOnTop ? pal.teal : pal.line, backgroundColor: taxOnTop ? pal.teal : 'transparent' }]}
          >
            <Text style={{ color: taxOnTop ? pal.page : pal.mute, fontWeight: '600', fontSize: 13 }}>
              {taxOnTop ? 'Tax on top: on' : 'Tax on top: off'}
            </Text>
          </TouchableOpacity>
          <InfoTip pal={pal} text={MERCH_TIPS.taxOnTop} />
        </View>
      </View>
      {editingOption ? (
        <OptionEditCard
          pal={pal}
          field={editingOption.field}
          label={editingOption.label}
          onClose={() => setEditingOption(null)}
          onRename={(next) => rewriteUserOption({ field: editingOption.field, from: editingOption.label, to: next })}
          onDelete={() => rewriteUserOption({ field: editingOption.field, from: editingOption.label, to: '' })}
        />
      ) : null}
      {(rows || []).map((r, i) => {
        if (!merchItemVisible(r, i)) return null;
        const sizes = merchActiveSizes(r);
        const sizeGroups = userOptions.sizeGroups || {};
        const tab = sizeTabById[r.id] || 'Adult';
        const types = merchTypesForCategory(userOptions, r.category);
        const categories = userOptions.categories || [];
        const avail = userOptions.avail || [];
        const sellers = userOptions.sellers || [];
        const sexes = userOptions.sex || [];
        const reorders = userOptions.reorder || [];
        const colors = userOptions.colors || [];
        const selectedColors = r.colors || [];
        const isDraft = !merchRowHasContent(r);
        const detailsOpen = openDetailsById[r.id] == null ? isDraft : !!openDetailsById[r.id];
        const moreOpen = !!openMoreById[r.id];
        const sizeNames = (r.sizes || []).filter((z) => z.enabled !== false && z.size).map((z) => z.size).filter((name, idx, all) => all.indexOf(name) === idx).join(', ');
        const detailBits = [r.type, r.sex !== 'Unisex' ? r.sex : '', selectedColors.join(', '), sizeNames].filter(Boolean).join(' · ');
        return (
          <View key={r.id || i} style={[s.card, { backgroundColor: pal.surf, borderColor: pal.line, marginTop: 14, padding: 16 }]}>
            <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: 12 }}>
              <MerchPhotoWell
                uri={r.photoUrl}
                uploading={uploadingId && uploadingId === (r.id || String(i))}
                pal={pal}
                onPickFile={(file) => uploadRowPhoto(i, file)}
                onPressNative={() => uploadRowPhoto(i)}
              />
              <View style={{ flex: 1, minWidth: 200 }}>
                {isDraft ? (
                  <Text style={[s.label, { color: pal.teal, marginBottom: 6 }]}>New item</Text>
                ) : null}
                <TextInput editable={canEdit}
                  value={r.name}
                  onChangeText={(v) => patchRow(i, { name: v })}
                  placeholder="Item name"
                  placeholderTextColor={pal.faint}
                  {...opsInputClass('blend')}
                  style={[inlineBlendInputStyle, { color: pal.ink, fontSize: 18, fontWeight: '600' }]}
                />
                <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginTop: 10, alignItems: 'center' }}>
                  <Text style={[s.label, { color: pal.mute, marginRight: 2 }]}>Category</Text>
                  <InfoTip pal={pal} text={MERCH_TIPS.category} />
                  {categories.map((cat) => (
                    <OptionChip
                      key={cat}
                      pal={pal}
                      label={cat}
                      on={r.category === cat}
                      onPress={() => patchRow(i, { category: cat, kind: cat })}
                      onEdit={() => setEditingOption({ field: 'category', label: cat })}
                    />
                  ))}
                  <AddChoice
                    pal={pal}
                    label="Add category"
                    onAdd={(label) => addUserOption('category', label, i, { category: label, kind: label })}
                  />
                </View>
              </View>
              <View style={{ alignItems: 'flex-end', gap: 8 }}>
                <Text style={{ color: pal.ink, fontWeight: '700', fontSize: 16 }}>{dashUsd(merchLineGross(r))}</Text>
                <Text style={{ color: pal.mute, fontSize: 12 }}>{dashNum(merchSoldCount(r))} sold</Text>
                {canEdit ? (
                  <TouchableOpacity onPress={() => removeItem(i)} accessibilityLabel="Remove item" style={{ padding: 4 }}>
                    <Ionicons name="trash-outline" size={18} color={pal.alert || '#c0483c'} />
                  </TouchableOpacity>
                ) : null}
              </View>
            </View>

            <View style={{ marginTop: 16, paddingTop: 14, borderTopWidth: 1, borderTopColor: pal.lineSoft }}>
              <Text style={[s.label, { color: pal.mute, marginBottom: 10 }]}>Tonight’s counts</Text>
              {sizes ? (
                <ScrollView horizontal nestedScrollEnabled>
                  <View>
                    <View style={{ flexDirection: 'row', gap: 8, paddingBottom: 6, alignItems: 'center' }}>
                      <ColHead pal={pal} label={sizes.some((z) => z.color) ? 'Color / Size' : 'Size'} width={110} />
                      <ColHead pal={pal} label="SKU" tip={MERCH_TIPS.sku} width={110} />
                      <ColHead pal={pal} label="Price" width={72} />
                      <ColHead pal={pal} label="COGS" tip={MERCH_TIPS.cogs} width={72} />
                      <ColHead pal={pal} label="Qty Avail" tip={MERCH_TIPS.qtyAvail} width={72} />
                      <ColHead pal={pal} label="In" tip={MERCH_TIPS.countIn} width={72} />
                      <ColHead pal={pal} label="Add" tip={MERCH_TIPS.countAdd} width={72} />
                      <ColHead pal={pal} label="Total In" tip={MERCH_TIPS.totalIn} width={72} />
                      <ColHead pal={pal} label="Comp" tip={MERCH_TIPS.countComp} width={72} />
                      <ColHead pal={pal} label="Item Gross" tip={MERCH_TIPS.itemGross} width={72} />
                      <ColHead pal={pal} label="Inv Rmg" tip={MERCH_TIPS.invRmg} width={72} />
                      <ColHead pal={pal} label="Out" tip={MERCH_TIPS.countOut} width={72} />
                      <ColHead pal={pal} label="Sold" tip={MERCH_TIPS.sold} width={72} />
                      <ColHead pal={pal} label="Gross" tip={MERCH_TIPS.gross} width={72} />
                    </View>
                    {sizes.map((z) => {
                      const totalIn = NUM(z.countIn) + NUM(z.countAdd);
                      const sold = merchSizeSold(z);
                      const unit = merchUnitPrice(r, z);
                      const variantKey = merchVariantKey(z);
                      return (
                        <View key={variantKey} style={{ flexDirection: 'row', gap: 8, alignItems: 'center', paddingVertical: 4 }}>
                          <Text style={{ width: 110, color: pal.ink, fontWeight: '600' }}>{merchVariantLabel(z)}</Text>
                          <TextInput editable={canEdit} value={z.sku} onChangeText={(v) => patchSize(i, variantKey, { sku: v })} placeholder="SKU" placeholderTextColor={pal.faint} {...opsInputClass('blend')} style={[inlineBlendInputStyle, { color: pal.ink, width: 110 }]} />
                          <Field pal={pal} align="right" value={z.price || r.price} onChange={(v) => patchSize(i, variantKey, { price: v })} />
                          <Field pal={pal} align="right" value={z.cogs} onChange={(v) => patchSize(i, variantKey, { cogs: v })} />
                          <Field pal={pal} align="right" value={z.qtyAvail} onChange={(v) => patchSize(i, variantKey, { qtyAvail: v })} />
                          <Field pal={pal} align="right" value={z.countIn} onChange={(v) => patchSize(i, variantKey, { countIn: v })} />
                          <Field pal={pal} align="right" value={z.countAdd} onChange={(v) => patchSize(i, variantKey, { countAdd: v })} />
                          <Text style={{ width: 72, textAlign: 'right', color: pal.mute }}>{totalIn}</Text>
                          <Field pal={pal} align="right" value={z.countComp} onChange={(v) => patchSize(i, variantKey, { countComp: v })} />
                          <Text style={{ width: 72, textAlign: 'right', color: pal.ink }}>{formatUsd(totalIn * unit)}</Text>
                          <Text style={{ width: 72, textAlign: 'right', color: pal.mute }}>{Math.max(0, totalIn - NUM(z.countComp))}</Text>
                          <Field pal={pal} align="right" value={z.countOut} onChange={(v) => patchSize(i, variantKey, { countOut: v })} />
                          <Text style={{ width: 72, textAlign: 'right', color: pal.ink }}>{sold}</Text>
                          <Text style={{ width: 72, textAlign: 'right', color: pal.ink }}>{formatUsd(sold * unit)}</Text>
                        </View>
                      );
                    })}
                    {taxOnTop ? (
                      <Text style={{ color: pal.mute, fontSize: 12, marginTop: 4 }}>
                        Tax on top {taxFor(r)}% — example {formatUsd(withTax(merchUnitPrice(r, sizes[0]), r))} w/ tax on {merchVariantLabel(sizes[0]) || 'item'}.
                      </Text>
                    ) : null}
                  </View>
                </ScrollView>
              ) : (
                <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 12 }}>
                  <LabeledBox pal={pal} label="Price"><Field pal={pal} align="right" value={r.price} onChange={(v) => patchRow(i, { price: v })} /></LabeledBox>
                  <LabeledBox pal={pal} label="COGS" tip={MERCH_TIPS.cogs}><Field pal={pal} align="right" value={r.cogs} onChange={(v) => patchRow(i, { cogs: v })} /></LabeledBox>
                  <LabeledBox pal={pal} label="Qty Avail" tip={MERCH_TIPS.qtyAvail}><Field pal={pal} align="right" value={r.qtyAvail} onChange={(v) => patchRow(i, { qtyAvail: v })} /></LabeledBox>
                  <LabeledBox pal={pal} label="In" tip={MERCH_TIPS.countIn}><Field pal={pal} align="right" value={r.countIn} onChange={(v) => patchRow(i, { countIn: v })} /></LabeledBox>
                  <LabeledBox pal={pal} label="Add" tip={MERCH_TIPS.countAdd}><Field pal={pal} align="right" value={r.countAdd} onChange={(v) => patchRow(i, { countAdd: v })} /></LabeledBox>
                  <LabeledBox pal={pal} label="Out" tip={MERCH_TIPS.countOut}><Field pal={pal} align="right" value={r.countOut} onChange={(v) => patchRow(i, { countOut: v })} /></LabeledBox>
                  <LabeledBox pal={pal} label="Comp" tip={MERCH_TIPS.countComp}><Field pal={pal} align="right" value={r.countComp} onChange={(v) => patchRow(i, { countComp: v })} /></LabeledBox>
                  <LabeledBox pal={pal} label="Total In" tip={MERCH_TIPS.totalIn}><Text style={{ color: pal.mute, fontSize: 16, paddingTop: 8 }}>{dashNum(merchTotalIn(r))}</Text></LabeledBox>
                  <LabeledBox pal={pal} label="Item Gross" tip={MERCH_TIPS.itemGross}><Text style={{ color: pal.ink, fontSize: 16, paddingTop: 8 }}>{dashUsd(merchItemGrossIn(r))}</Text></LabeledBox>
                  <LabeledBox pal={pal} label="Inv Rmg" tip={MERCH_TIPS.invRmg}><Text style={{ color: pal.mute, fontSize: 16, paddingTop: 8 }}>{dashNum(merchInvRemaining(r))}</Text></LabeledBox>
                  <LabeledBox pal={pal} label="Sold" tip={MERCH_TIPS.sold}><Text style={{ color: pal.ink, fontSize: 16, paddingTop: 8 }}>{dashNum(merchSoldCount(r))}</Text></LabeledBox>
                  <LabeledBox pal={pal} label="Gross" tip={MERCH_TIPS.gross}><Text style={{ color: pal.ink, fontSize: 16, fontWeight: '600', paddingTop: 8 }}>{dashUsd(merchLineGross(r))}</Text></LabeledBox>
                </View>
              )}
              <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginTop: 12, alignItems: 'center' }}>
                <Text style={[s.label, { color: pal.mute, marginRight: 2 }]}>Sold by</Text>
                <InfoTip pal={pal} text={MERCH_TIPS.seller} />
                {sellers.map((seller) => (
                  <OptionChip
                    key={seller}
                    pal={pal}
                    label={seller}
                    on={String(r.seller || '') === seller}
                    onPress={() => patchRow(i, { seller })}
                    onEdit={() => setEditingOption({ field: 'seller', label: seller })}
                  />
                ))}
                <AddChoice
                  pal={pal}
                  label="Add type"
                  onAdd={(label) => addUserOption('seller', label, i, { seller: label })}
                />
              </View>
            </View>

            <TouchableOpacity
              onPress={() => setOpenDetailsById((prev) => ({ ...prev, [r.id]: !detailsOpen }))}
              style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginTop: 14, paddingTop: 12, borderTopWidth: 1, borderTopColor: pal.lineSoft }}
            >
              <View>
                <Text style={{ color: pal.ink, fontWeight: '600', fontSize: 14 }}>Item details</Text>
                {!detailsOpen && detailBits ? (
                  <Text style={{ color: pal.mute, fontSize: 12, marginTop: 2 }}>{detailBits}</Text>
                ) : (
                  <Text style={{ color: pal.mute, fontSize: 12, marginTop: 2 }}>Type, sizes, SKU, color, reorder</Text>
                )}
              </View>
              <Ionicons name={detailsOpen ? 'chevron-up' : 'chevron-down'} size={18} color={pal.mute} />
            </TouchableOpacity>
            {detailsOpen ? (
              <View style={{ marginTop: 12 }}>
                <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 8 }}>
                  <Text style={[s.label, { color: pal.mute }]}>Type</Text>
                  <InfoTip pal={pal} text={MERCH_TIPS.type} />
                </View>
                <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
                  {types.map((typ) => (
                    <OptionChip
                      key={typ}
                      pal={pal}
                      label={typ}
                      on={r.type === typ}
                      onPress={() => patchRow(i, { type: typ })}
                      onEdit={() => setEditingOption({ field: 'type', label: typ })}
                    />
                  ))}
                  <AddChoice
                    pal={pal}
                    label="Add type"
                    onAdd={(label) => addUserOption('type', label, i, { type: label })}
                  />
                </View>
                <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 12, marginTop: 14, alignItems: 'flex-end' }}>
                  <View>
                    <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 8 }}>
                      <Text style={[s.label, { color: pal.mute }]}>Sex</Text>
                      <InfoTip pal={pal} text={MERCH_TIPS.sex} />
                    </View>
                    <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
                      {sexes.map((sex) => (
                        <OptionChip
                          key={sex}
                          pal={pal}
                          label={sex}
                          on={r.sex === sex}
                          onPress={() => patchRow(i, { sex })}
                          onEdit={() => setEditingOption({ field: 'sex', label: sex })}
                        />
                      ))}
                      <AddChoice
                        pal={pal}
                        label="Add type"
                        onAdd={(label) => addUserOption('sex', label, i, { sex: label })}
                      />
                    </View>
                  </View>
                  {!sizes ? (
                    <LabeledBox pal={pal} label="SKU" tip={MERCH_TIPS.sku} width={140}>
                      <TextInput editable={canEdit} value={r.sku} onChangeText={(v) => patchRow(i, { sku: v })} placeholder="SKU" placeholderTextColor={pal.faint} {...opsInputClass('blend')} style={[inlineBlendInputStyle, { color: pal.ink }]} />
                    </LabeledBox>
                  ) : null}
                </View>
                <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 16, marginBottom: 8 }}>
                  <Text style={[s.label, { color: pal.mute }]}>Color</Text>
                  <InfoTip pal={pal} text={MERCH_TIPS.color} />
                </View>
                <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
                  {colors.map((colorName) => (
                    <OptionChip
                      key={colorName}
                      pal={pal}
                      label={colorName}
                      on={selectedColors.includes(colorName)}
                      onPress={() => patchRow(i, toggleMerchColor(r, colorName))}
                      onEdit={() => setEditingOption({ field: 'color', label: colorName })}
                    />
                  ))}
                  <AddChoice
                    pal={pal}
                    label="Add color"
                    onAdd={(label) => addUserOption('color', label, i, (row) => toggleMerchColor(row, label))}
                  />
                </View>
                <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 16, marginBottom: 8 }}>
                  <Text style={[s.label, { color: pal.mute }]}>Sizes</Text>
                  <InfoTip pal={pal} text={MERCH_TIPS.sizes} />
                </View>
                <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
                  {Object.keys(sizeGroups).map((group) => (
                    <Chip key={group} pal={pal} label={group} on={tab === group} onPress={() => setSizeTabById((prev) => ({ ...prev, [r.id]: group }))} />
                  ))}
                </View>
                <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                  {(sizeGroups[tab] || []).map((sizeName) => {
                    const on = (r.sizes || []).some((z) => z.size === sizeName && z.enabled !== false);
                    return (
                      <OptionChip
                        key={sizeName}
                        pal={pal}
                        label={sizeName}
                        on={on}
                        onPress={() => patchRow(i, toggleMerchSize(r, sizeName))}
                        onEdit={() => setEditingOption({ field: 'size', label: sizeName, sizeGroup: tab })}
                      />
                    );
                  })}
                  <AddChoice
                    pal={pal}
                    label="Add size"
                    onAdd={(label) => {
                      setSizeTabById((prev) => ({ ...prev, [r.id]: tab || 'Custom' }));
                      addUserOption('size', label, i, (row) => toggleMerchSize(row, label), tab || 'Custom');
                    }}
                  />
                </View>
                <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: 14, alignItems: 'center' }}>
                  <Text style={[s.label, { color: pal.mute }]}>Reorder</Text>
                  <InfoTip pal={pal} text={MERCH_TIPS.reorder} />
                  {reorders.map((lvl) => (
                    <OptionChip
                      key={lvl}
                      pal={pal}
                      label={lvl}
                      on={r.reorder === lvl}
                      onPress={() => patchRow(i, { reorder: lvl })}
                      onEdit={() => setEditingOption({ field: 'reorder', label: lvl })}
                    />
                  ))}
                  <AddChoice
                    pal={pal}
                    label="Add type"
                    onAdd={(label) => addUserOption('reorder', label, i, { reorder: label })}
                  />
                  <LabeledBox pal={pal} label="Alert # shows" tip={MERCH_TIPS.alertShows} width={110}>
                    <Field pal={pal} value={r.alertShows} onChange={(v) => patchRow(i, { alertShows: v })} />
                  </LabeledBox>
                </View>
                <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginTop: 12, alignItems: 'center' }}>
                  <Text style={[s.label, { color: pal.mute, marginRight: 4 }]}>For</Text>
                  <InfoTip pal={pal} text={MERCH_TIPS.avail} />
                  {avail.map((slot) => (
                    <OptionChip
                      key={slot}
                      pal={pal}
                      label={slot}
                      on={(r.available || []).includes(slot)}
                      onPress={() => {
                        const cur = r.available || [];
                        const next = cur.includes(slot) ? cur.filter((x) => x !== slot) : [...cur, slot];
                        patchRow(i, { available: next });
                      }}
                      onEdit={() => setEditingOption({ field: 'avail', label: slot })}
                    />
                  ))}
                  <AddChoice
                    pal={pal}
                    label="Add for"
                    onAdd={(label) => {
                      const cur = r.available || [];
                      addUserOption('avail', label, i, { available: cur.includes(label) ? cur : [...cur, label] });
                    }}
                  />
                </View>
              </View>
            ) : null}

            <TouchableOpacity
              onPress={() => setOpenMoreById((prev) => ({ ...prev, [r.id]: !moreOpen }))}
              style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginTop: 12 }}
            >
              <Text style={{ color: pal.mute, fontWeight: '600', fontSize: 13 }}>Crew sales, trailer comps, overrides</Text>
              <Ionicons name={moreOpen ? 'chevron-up' : 'chevron-down'} size={16} color={pal.mute} />
            </TouchableOpacity>
            {moreOpen ? (
              <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 12, marginTop: 10 }}>
                <LabeledBox pal={pal} label="Crew sales qty" tip={MERCH_TIPS.crewQty}><Field pal={pal} value={r.crewQty} onChange={(v) => patchRow(i, { crewQty: v })} /></LabeledBox>
                <LabeledBox pal={pal} label="Crew $" tip={MERCH_TIPS.crewAmount}><Field pal={pal} value={r.crewAmount} onChange={(v) => patchRow(i, { crewAmount: v })} /></LabeledBox>
                <LabeledBox pal={pal} label="Trailer comps" tip={MERCH_TIPS.trailerComp}><Field pal={pal} value={r.trailerComp} onChange={(v) => patchRow(i, { trailerComp: v })} /></LabeledBox>
                <LabeledBox pal={pal} label="Tax override %" tip={MERCH_TIPS.taxOverride}><Field pal={pal} value={r.taxOverride} onChange={(v) => patchRow(i, { taxOverride: v })} /></LabeledBox>
                <LabeledBox pal={pal} label="Cut override %" tip={MERCH_TIPS.cutOverride}><Field pal={pal} value={r.cutOverride} onChange={(v) => patchRow(i, { cutOverride: v })} /></LabeledBox>
              </View>
            ) : null}
          </View>
        );
      })}
      <MerchAddItemButton pal={pal} empty={!filledCount} onPress={addItem} />
      <View style={{ flexDirection: 'row', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, marginTop: 14, paddingHorizontal: 4, paddingVertical: 8 }}>
        <Text style={[s.label, { color: pal.mute }]}>Settlement line</Text>
        <Text style={{ fontWeight: '500', color: pal.ink }}>
          {formatUsd(split.merch)} merch · {formatUsd(split.music)} music · {formatUsd(net)} net
        </Text>
      </View>
    </ScrollView>
    </OpsEditContext.Provider>
  );
}

export function MasterTourFilesPage({ files, isDarkMode, accent, onUpload }) {
  const pal = palette(isDarkMode);
  const rows = (files || []).map((f) => ({
    name: f.name || f.fileName || 'File',
    kind: f.fileType || f.kind || 'file',
    size: f.sizeLabel || f.size || '',
    who: f.uploadedByName || f.who || '',
    when: f.updatedAt || f.when || '',
    url: f.url || '',
  }));
  return (
    <ScrollView style={{ flex: 1, backgroundColor: pal.page }} contentContainerStyle={{ padding: 24 }}>
      <View style={{ flexDirection: 'row', justifyContent: 'space-between', borderBottomWidth: 2, borderBottomColor: accent || pal.teal, paddingBottom: 14 }}>
        <Text style={[s.heroSm, { color: pal.ink, fontSize: 27 }]}>Files</Text>
        {onUpload ? (
          <TouchableOpacity onPress={onUpload} style={[s.primaryBtn, { backgroundColor: pal.ink }]}>
            <Text style={{ color: pal.page, fontWeight: '600' }}>Upload files</Text>
          </TouchableOpacity>
        ) : null}
      </View>
      <View style={[s.card, { backgroundColor: pal.surf, borderColor: pal.line, padding: 0, overflow: 'hidden', marginTop: 18 }]}>
        <View style={[s.tierHead, { paddingHorizontal: 18, paddingTop: 13 }]}>
          {['Name', 'Kind', 'Size', 'Owner', 'Updated'].map((h) => (
            <Text key={h} style={[s.colH, { color: pal.mute, flex: h === 'Name' ? 2 : 1 }]}>{h}</Text>
          ))}
        </View>
        {rows.length ? rows.map((fl, i) => (
          <TouchableOpacity
            key={`${fl.name}-${i}`}
            onPress={() => { if (fl.url) Linking.openURL(fl.url); }}
            disabled={!fl.url}
            style={[s.tierRow, { borderTopColor: pal.lineSoft, paddingHorizontal: 18 }]}
          >
            <Text style={{ flex: 2, color: pal.link || pal.ink, fontSize: 15 }} numberOfLines={1}>{fl.name}</Text>
            <Text style={{ flex: 1, color: pal.mute, fontSize: 13.5 }}>{fl.kind}</Text>
            <Text style={{ flex: 1, color: pal.mute, fontSize: 14, textAlign: 'right' }}>{fl.size}</Text>
            <Text style={{ flex: 1, color: pal.ink, fontSize: 14 }}>{fl.who}</Text>
            <Text style={{ flex: 1, color: pal.mute, fontSize: 14, textAlign: 'right' }}>{fl.when}</Text>
          </TouchableOpacity>
        )) : (
          <Text style={{ color: pal.mute, padding: 18 }}>No files on this tour yet.</Text>
        )}
      </View>
    </ScrollView>
  );
}

export function MasterTourLodgingPage({
  tour,
  tourDayMetaByDate,
  selectedTourFilterId,
  selectedDateKey,
  lodgingText = '',
  isDarkMode,
  accent,
  onSelectDate,
  onOpenPlanner,
  onPersist,
  canEdit = true,
}) {
  const pal = palette(isDarkMode);
  const days = tour?.days || [];
  const persistStays = useDebouncedCallback((next) => {
    onPersist?.(serializeLodgingStays(next));
  }, 450);
  const plannerStay = useMemo(() => {
    const day = (days || []).find((d) => String(d.scheduledDate || '').trim() === String(selectedDateKey || '').trim());
    const hotel = getLodgingVenueForDay(day);
    if (!hotel) return null;
    return {
      id: 'planner',
      name: hotel.name || '',
      city: hotel.city || '',
      address: hotel.address || '',
      parking: '',
      busDrop: '',
      busPick: '',
      notes: '',
    };
  }, [days, selectedDateKey]);
  const [stays, setStays] = useState(() => {
    const parsed = parseLodgingStays(lodgingText);
    if (parsed.length) return parsed;
    return plannerStay ? [plannerStay] : [emptyLodgingStay()];
  });
  const lodgingBoot = useRef('');
  useEffect(() => {
    const key = `${selectedDateKey}:${lodgingText}`;
    if (lodgingBoot.current === key) return;
    lodgingBoot.current = key;
    const parsed = parseLodgingStays(lodgingText);
    if (parsed.length) setStays(parsed);
    else setStays(plannerStay ? [{ ...plannerStay }] : [emptyLodgingStay()]);
  }, [selectedDateKey, lodgingText, plannerStay]);
  const patchStays = (next) => {
    if (!canEdit) return;
    setStays(next);
    persistStays(next);
  };
  const rows = days.map((day) => {
    const key = String(day.scheduledDate || '').trim();
    const card = pickTourDayCard(tourDayMetaByDate?.[key], selectedTourFilterId);
    const hotel = getLodgingVenueForDay(day);
    return {
      key,
      date: formatDateShort(key),
      city: String(card?.cityLine || hotel?.city || '').split(',')[0] || '—',
      hotel: hotel?.name || '—',
      addr: [hotel?.address, hotel?.city].filter(Boolean).join(', '),
      rooms: hotel?.rooms || day.lodgingRooms || '',
      status: hotel ? 'Confirmed' : 'Open',
    };
  }).filter((r) => r.key);
  const stayField = (stay, i, key, placeholder) => (
    <TextInput editable={canEdit}
      value={stay[key] || ''}
      onChangeText={(v) => {
        const next = stays.slice();
        next[i] = { ...stay, [key]: v };
        patchStays(next);
      }}
      placeholder={placeholder}
      placeholderTextColor={pal.faint}
      {...opsInputClass('well')}
      style={[
        fieldWellStyle(pal, isDarkPal(pal)),
        { color: pal.ink, fontSize: 14.5, borderWidth: 1, borderRadius: 8, paddingHorizontal: 10, paddingVertical: 8, marginTop: 8 },
      ]}
    />
  );
  return (
    <ScrollView style={{ flex: 1, backgroundColor: pal.page }} contentContainerStyle={{ padding: 24, paddingBottom: 48 }}>
      <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-end', gap: 12, borderBottomWidth: 2, borderBottomColor: accent || pal.teal, paddingBottom: 14, flexWrap: 'wrap' }}>
        <View>
          <Text style={[s.heroSm, { color: pal.ink, fontSize: 27 }]}>Lodging</Text>
          {canEdit ? null : <ViewOnlyBadge pal={pal} />}
        </View>
        {onOpenPlanner ? (
          <TouchableOpacity onPress={onOpenPlanner} style={[s.outlineBtn, { borderColor: pal.ink }]}>
            <Text style={{ color: pal.ink, fontWeight: '600' }}>Open Tour Planner map</Text>
          </TouchableOpacity>
        ) : null}
      </View>
      <Text style={{ color: pal.mute, marginTop: 10, fontSize: 13.5 }}>
        Edit stays here — parking, bus drop / pickup, notes, and extra hotels. Planner hotels stay on the map.
      </Text>
      {(stays || []).map((stay, i) => (
        <View key={stay.id || i} style={[s.card, { backgroundColor: pal.surf, borderColor: pal.line, marginTop: 16 }]}>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
            <Text style={[s.label, { color: pal.mute }]}>{i === 0 ? 'Stay' : `Stay ${i + 1}`}</Text>
            {canEdit && stays.length > 1 ? (
              <TouchableOpacity onPress={() => patchStays(stays.filter((_, j) => j !== i))}>
                <Text style={{ color: pal.alert, fontWeight: '600' }}>Remove</Text>
              </TouchableOpacity>
            ) : null}
          </View>
          {stayField(stay, i, 'name', 'Hotel name')}
          {stayField(stay, i, 'city', 'City')}
          {stayField(stay, i, 'address', 'Address')}
          {stayField(stay, i, 'parking', 'Parking')}
          {stayField(stay, i, 'busDrop', 'Bus drop')}
          {stayField(stay, i, 'busPick', 'Bus pickup')}
          <TextInput editable={canEdit}
            value={stay.notes || ''}
            onChangeText={(v) => {
              const next = stays.slice();
              next[i] = { ...stay, notes: v };
              patchStays(next);
            }}
            placeholder="Notes"
            placeholderTextColor={pal.faint}
            multiline
            {...opsInputClass('well')}
            style={[
              fieldWellStyle(pal, isDarkPal(pal)),
              { color: pal.ink, fontSize: 14.5, borderWidth: 1, borderRadius: 8, paddingHorizontal: 10, paddingVertical: 8, marginTop: 8, minHeight: 72, textAlignVertical: 'top' },
            ]}
          />
        </View>
      ))}
      {canEdit ? (
        <TouchableOpacity
          onPress={() => patchStays([...(stays || []), emptyLodgingStay()])}
          style={[s.outlineBtn, { borderColor: pal.teal, alignSelf: 'flex-start', marginTop: 14 }]}
        >
          <Text style={{ color: pal.teal, fontWeight: '600' }}>+ Stay</Text>
        </TouchableOpacity>
      ) : null}
      <View style={[s.card, { backgroundColor: pal.surf, borderColor: pal.line, padding: 0, overflow: 'hidden', marginTop: 22 }]}>
        <View style={[s.tierHead, { paddingHorizontal: 18, paddingTop: 13 }]}>
          {['Date', 'City', 'Hotel', 'Rooms', 'Status'].map((h) => (
            <Text key={h} style={[s.colH, { color: pal.mute, flex: h === 'Hotel' ? 2 : 1 }]}>{h}</Text>
          ))}
        </View>
        {rows.length ? rows.map((lg) => (
          <TouchableOpacity key={lg.key} onPress={() => onSelectDate?.(lg.key)} style={[s.tierRow, { borderTopColor: pal.lineSoft, paddingHorizontal: 18, backgroundColor: lg.key === selectedDateKey ? pal.sunk : 'transparent' }]}>
            <Text style={{ flex: 1, fontWeight: '600', color: pal.ink }}>{lg.date}</Text>
            <Text style={{ flex: 1, color: pal.ink }}>{lg.city}</Text>
            <View style={{ flex: 2 }}>
              <Text style={{ color: pal.link || pal.ink, fontWeight: '500' }}>{lg.hotel}</Text>
              {lg.addr ? <Text style={{ color: pal.mute, fontSize: 13.5 }}>{lg.addr}</Text> : null}
            </View>
            <Text style={{ flex: 1, color: pal.mute, fontSize: 14 }}>{lg.rooms || '—'}</Text>
            <Text style={{ flex: 1, textAlign: 'center', color: lg.status === 'Confirmed' ? pal.ok : pal.warn, fontSize: 13.5 }}>{lg.status}</Text>
          </TouchableOpacity>
        )) : (
          <Text style={{ color: pal.mute, padding: 18 }}>No lodging pinned on this tour yet.</Text>
        )}
      </View>
    </ScrollView>
  );
}

export function MasterTourPartyPage({
  partyMembers,
  tourName,
  isDarkMode,
  accent,
  onInvite,
  ownerId,
  currentUserId,
  tourId,
  tourShareSettings,
  theme,
  onError,
  onSuccess,
}) {
  const pal = palette(isDarkMode);
  const roster = partyMembers || [];
  const db = getFirestore();
  const isOwner = isTourOwner(currentUserId, ownerId);
  const [expandedMemberId, setExpandedMemberId] = useState(null);
  const [savingMemberId, setSavingMemberId] = useState(null);

  const persistEntry = useCallback(
    async (memberUid, nextEntry) => {
      if (!isOwner || !tourId || !ownerId) return;
      if (memberUid === ownerId) {
        onError?.('Tour creator permissions cannot be changed.');
        return;
      }
      try {
        setSavingMemberId(memberUid);
        await saveMemberPermissionEntry(db, ownerId, tourId, memberUid, nextEntry, tourShareSettings || {});
        onSuccess?.('Permissions updated.');
      } catch (err) {
        console.error('MasterTourPartyPage saveMemberPermissionEntry', err);
        onError?.(err?.message || 'Could not save permissions.');
      } finally {
        setSavingMemberId(null);
      }
    },
    [db, isOwner, tourId, ownerId, tourShareSettings, onError, onSuccess]
  );

  return (
    <ScrollView style={{ flex: 1, backgroundColor: pal.page }} contentContainerStyle={{ padding: 24, paddingBottom: 48, maxWidth: 720 }}>
      <View style={{ flexDirection: 'row', justifyContent: 'space-between', borderBottomWidth: 2, borderBottomColor: accent || pal.teal, paddingBottom: 14 }}>
        <View>
          <Text style={[s.heroSm, { color: pal.ink, fontSize: 27 }]}>Tour party</Text>
          <Text style={{ color: pal.mute, marginTop: 4 }}>{tourName || 'Tour'} · {roster.length} in party</Text>
        </View>
        {onInvite ? (
          <TouchableOpacity onPress={onInvite} style={[s.primaryBtn, { backgroundColor: pal.ink, alignSelf: 'flex-end' }]}>
            <Text style={{ color: pal.page, fontWeight: '600' }}>Invite</Text>
          </TouchableOpacity>
        ) : null}
      </View>
      <View style={{ marginTop: 18, gap: 8 }}>
        {roster.length ? roster.map((m, i) => {
          const rowKey = m.id || m.email || String(i);
          const editState = memberPermissionEditState(ownerId, m);
          const memberUid = editState.memberUid || rowKey;
          const expanded = isOwner && expandedMemberId === memberUid;
          const isOwnerMember = isTourOwner(memberUid, ownerId);
          const entry = isOwnerMember
            ? buildOwnerFullAccessEntry()
            : getMemberPermissionsEntry(tourShareSettings || {}, memberUid, tourShareSettings?.managerIds);
          const counts = summarizeMemberPermissionTabs(entry);
          const saving = savingMemberId === memberUid;
          return (
            <View key={rowKey} style={[s.card, { backgroundColor: pal.surf, borderColor: pal.line, padding: 0, overflow: 'hidden' }]}>
              <TouchableOpacity
                onPress={() => {
                  if (!isOwner) return;
                  setExpandedMemberId(expanded ? null : memberUid);
                }}
                disabled={!isOwner}
                activeOpacity={isOwner ? 0.8 : 1}
                accessibilityRole={isOwner ? 'button' : undefined}
                accessibilityLabel={isOwner ? `${m.name || m.displayName || 'Member'} permissions` : undefined}
                style={{ flexDirection: 'row', alignItems: 'center', gap: 13, padding: 18 }}
              >
                <PartyAvatar member={m} size={40} pal={pal} accent={accent} />
                <View style={{ flex: 1, minWidth: 0 }}>
                  <Text style={{ color: pal.ink, fontWeight: '600', fontSize: 15 }}>{m.name || m.displayName || 'Member'}</Text>
                  <Text style={{ color: pal.mute, fontSize: 14.5, marginTop: 2 }}>{m.role || m.permissionRole || 'Tour party'}</Text>
                  {m.email ? <Text style={{ color: pal.link || pal.ink, fontSize: 14, marginTop: 6 }}>{m.email}</Text> : null}
                </View>
                {isOwner ? (
                  <View style={{ alignItems: 'flex-end', gap: 6, flexShrink: 0 }}>
                    {saving ? (
                      <ActivityIndicator size="small" color={accent || pal.teal} />
                    ) : (
                      <>
                        <Text style={{ color: pal.mute, fontSize: 12 }}>
                          {counts.edit} edit · {counts.view} view
                        </Text>
                        <Ionicons
                          name={expanded ? 'chevron-up' : 'chevron-down'}
                          size={16}
                          color={pal.mute}
                        />
                      </>
                    )}
                  </View>
                ) : null}
              </TouchableOpacity>
              {expanded && theme ? (
                <View style={{ borderTopWidth: 1, borderTopColor: pal.line, paddingHorizontal: 10, paddingBottom: 12 }}>
                  <MemberPermissionsEditor
                    entry={entry}
                    theme={theme}
                    isDarkMode={isDarkMode}
                    saving={saving}
                    readOnly={!editState.canEdit}
                    readOnlyMessage={editState.readOnlyMessage}
                    onChangeTab={(tabKey, level) =>
                      persistEntry(memberUid, normalizeMemberPermissionsEntry({
                        ...entry,
                        tabs: { ...entry.tabs, [tabKey]: level },
                      }))
                    }
                    onToggleParty={(value) => persistEntry(memberUid, { ...entry, canManageParty: value })}
                    onToggleShares={(value) => persistEntry(memberUid, { ...entry, canManageShares: value })}
                    showSetlistModerator={!isOwnerMember && editState.canEdit}
                    isSetlistModerator={String(tourShareSettings?.setlistModeratorId || '') === memberUid}
                    onToggleSetlistModerator={(value) => {
                      saveSetlistModerator(db, ownerId, tourId, value ? memberUid : '', tourShareSettings || {})
                        .catch((err) => {
                          console.error('saveSetlistModerator', err);
                          onError?.(err?.message || 'Could not save setlist moderator.');
                        });
                    }}
                  />
                </View>
              ) : null}
            </View>
          );
        }) : (
          <Text style={{ color: pal.mute }}>No one on this tour yet. Invite from here or Tour settings.</Text>
        )}
      </View>
    </ScrollView>
  );
}

function ClickToEditText({
  value,
  placeholder,
  editing,
  canEdit,
  onStartEdit,
  onChangeText,
  onEndEdit,
  pal,
  textStyle,
  inputStyle,
}) {
  if (editing) {
    return (
      <TextInput
        autoFocus
        value={value}
        onChangeText={onChangeText}
        onBlur={onEndEdit}
        onSubmitEditing={onEndEdit}
        placeholder={placeholder}
        placeholderTextColor={pal.faint}
        {...opsInputClass('well')}
        style={inputStyle}
        blurOnSubmit
      />
    );
  }
  if (!canEdit) {
    return <Text style={textStyle}>{value || placeholder}</Text>;
  }
  return (
    <TouchableOpacity onPress={onStartEdit} onLongPress={onStartEdit} activeOpacity={0.75} accessibilityRole="button">
      <Text style={[textStyle, !value && { color: pal.faint }, Platform.OS === 'web' ? { cursor: 'text' } : null]}>
        {value || placeholder}
      </Text>
    </TouchableOpacity>
  );
}

function SortableStructuredRow({ id, disabled, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    disabled,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.88 : 1,
    zIndex: isDragging ? 2 : 0,
  };
  return (
    <View ref={setNodeRef} style={style}>
      {typeof children === 'function' ? children({ attributes, listeners }) : children}
    </View>
  );
}

function InlineStructuredList({
  value,
  onChange,
  pal,
  labels,
  numberCol = true,
  titleCol = 'Item',
  showBpm = false,
  highlightById = {},
}) {
  const canEdit = Boolean(onChange);
  const useDnD = canEdit && Platform.OS === 'web';
  const [editKey, setEditKey] = useState(null);
  const [bpmFocusId, setBpmFocusId] = useState(null);
  const [bpmDraft, setBpmDraft] = useState('');
  const [durationFocusId, setDurationFocusId] = useState(null);
  const [durationDraft, setDurationDraft] = useState('');
  const [songs, setSongs] = useState(() => parseSetlistText(value).songs || []);
  const skipHydrate = useRef(false);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  );

  useEffect(() => {
    if (skipHydrate.current) {
      skipHydrate.current = false;
      return;
    }
    setSongs(parseSetlistText(value).songs || []);
  }, [value]);

  const persist = (next) => {
    skipHydrate.current = true;
    setSongs(next);
    onChange?.(serializeSetlistDoc({ songs: next }));
  };

  const updateSong = (id, patch) => persist(songs.map((s) => (s.id === id ? { ...s, ...patch } : s)));
  const commitBpm = (id) => {
    const current = songs.find((s) => s.id === id);
    const nextBpm = commitBpmInput(bpmDraft, current?.bpm);
    persist(songs.map((s) => {
      if (s.id !== id) return s;
      const next = { ...s };
      if (nextBpm == null) delete next.bpm;
      else next.bpm = nextBpm;
      return next;
    }));
    setBpmFocusId(null);
    setBpmDraft('');
  };
  const commitDuration = (id) => {
    const current = songs.find((s) => s.id === id);
    const nextDuration = normalizeSongDuration(durationDraft);
    setDurationFocusId(null);
    setDurationDraft('');
    if (nextDuration == null) return;
    if ((current?.duration || '') === nextDuration) return;
    persist(songs.map((s) => {
      if (s.id !== id) return s;
      const next = { ...s };
      if (!nextDuration) delete next.duration;
      else next.duration = nextDuration;
      return next;
    }));
  };
  const updateLine = (songId, lineId, fieldIndex, text) => {
    persist(songs.map((s) => {
      if (s.id !== songId) return s;
      return {
        ...s,
        lines: (s.lines || []).map((line) => {
          if (line.id !== lineId) return line;
          const fields = [...(line.fields || [''])];
          while (fields.length <= fieldIndex) fields.push('');
          fields[fieldIndex] = text;
          return { ...line, fields: fields.slice(0, 2) };
        }),
      };
    }));
  };
  const addLine = (songId) => {
    const lineId = newSetlistLineId();
    persist(songs.map((s) => (
      s.id === songId
        ? { ...s, lines: [...(s.lines || []), { id: lineId, fields: [''] }] }
        : s
    )));
    setEditKey(`line:${songId}:${lineId}`);
  };
  const removeLine = (songId, lineId) => {
    persist(songs.map((s) => (
      s.id === songId ? { ...s, lines: (s.lines || []).filter((l) => l.id !== lineId) } : s
    )));
    setEditKey((key) => (key === `line:${songId}:${lineId}` ? null : key));
  };
  const removeSong = (id) => {
    persist(songs.filter((s) => s.id !== id));
    setEditKey((key) => {
      const k = String(key || '');
      return k === `title:${id}` || k.startsWith(`line:${id}:`) ? null : key;
    });
  };
  const addSong = () => {
    const id = newSetlistSongId();
    persist([...songs, { id, title: '', open: true, lines: [] }]);
    setEditKey(`title:${id}`);
  };
  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    persist(reorderListById(songs, active.id, over.id));
  };

  const inputStyle = [
    fieldWellStyle(pal, isDarkPal(pal)),
    {
      color: pal.ink,
      fontSize: 15,
      borderWidth: 1,
      borderRadius: 8,
      paddingHorizontal: 10,
      paddingVertical: 7,
      width: '100%',
    },
  ];
  const handleStyle = {
    width: 22,
    alignItems: 'center',
    justifyContent: 'center',
    paddingTop: 2,
    ...(Platform.OS === 'web' ? { cursor: 'grab', touchAction: 'none' } : {}),
  };

  const renderRow = (song, i, dragHandleProps) => {
    const reasons = highlightById[song.id] || [];
    const pending = reasons.length > 0;
    return (
    <View
      key={song.id || i}
      style={{
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: 10,
        borderTopWidth: 1,
        borderTopColor: pal.lineSoft,
        paddingHorizontal: 18,
        paddingVertical: 12,
        backgroundColor: pending ? hexWithAlpha(pal.warn || '#e8a43d', 0.18) : undefined,
      }}
    >
      {canEdit ? (
        <TouchableOpacity
          style={handleStyle}
          accessibilityLabel="Reorder"
          disabled={!useDnD}
          {...(dragHandleProps?.listeners || {})}
          {...(dragHandleProps?.attributes || {})}
        >
          <Ionicons name="reorder-three" size={20} color={pal.mute} />
        </TouchableOpacity>
      ) : null}
      {numberCol ? <Text style={{ width: 28, color: pal.mute, fontWeight: '600', paddingTop: 2 }}>{i + 1}</Text> : null}
      <View style={{ flex: 1, minWidth: 0, gap: 6 }}>
        <ClickToEditText
          value={song.title}
          placeholder={labels.titlePlaceholder || labels.untitledItem}
          editing={editKey === `title:${song.id}`}
          canEdit={canEdit}
          onStartEdit={() => setEditKey(`title:${song.id}`)}
          onChangeText={(t) => updateSong(song.id, { title: t })}
          onEndEdit={() => setEditKey((key) => (key === `title:${song.id}` ? null : key))}
          pal={pal}
          textStyle={{ color: pal.ink, fontSize: 15, fontWeight: '500' }}
          inputStyle={inputStyle}
        />
        {(song.lines || []).map((line) => {
          const lineKey = `line:${song.id}:${line.id}`;
          const lineValue = (line.fields || [])[0] || '';
          return (
            <View key={line.id} style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
              <View style={{ flex: 1, minWidth: 0 }}>
                <ClickToEditText
                  value={lineValue}
                  placeholder={labels.detailPlaceholder}
                  editing={editKey === lineKey}
                  canEdit={canEdit}
                  onStartEdit={() => setEditKey(lineKey)}
                  onChangeText={(t) => updateLine(song.id, line.id, 0, t)}
                  onEndEdit={() => setEditKey((key) => (key === lineKey ? null : key))}
                  pal={pal}
                  textStyle={{ color: pal.mute, fontSize: 13.5, lineHeight: 19 }}
                  inputStyle={[inputStyle, { fontSize: 14 }]}
                />
              </View>
              {canEdit ? (
                <TouchableOpacity onPress={() => removeLine(song.id, line.id)} accessibilityLabel="Remove note">
                  <Ionicons name="close" size={16} color={pal.mute} />
                </TouchableOpacity>
              ) : null}
            </View>
          );
        })}
        {canEdit ? (
          <TouchableOpacity onPress={() => addLine(song.id)} style={{ flexDirection: 'row', alignItems: 'center', gap: 6, paddingVertical: 2 }}>
            <Ionicons name="add" size={16} color={pal.teal} />
            <Text style={{ color: pal.teal, fontWeight: '600', fontSize: 13 }}>Add note</Text>
          </TouchableOpacity>
        ) : null}
        {pending ? (
          <Text style={{ color: pal.warn || '#e8a43d', fontSize: 12, fontWeight: '600' }}>
            Pending {reasons.join(' · ')}
          </Text>
        ) : null}
      </View>
      {showBpm ? (
        <View style={{ width: 88 }}>
          {canEdit ? (
            <TextInput
              value={durationFocusId === song.id ? durationDraft : (song.duration || '')}
              onFocus={() => {
                setDurationFocusId(song.id);
                setDurationDraft(song.duration || '');
              }}
              onChangeText={(t) => setDurationDraft(String(t || '').replace(/[^\d:]/g, '').slice(0, 5))}
              onBlur={() => commitDuration(song.id)}
              onSubmitEditing={() => commitDuration(song.id)}
              placeholder="3:45"
              placeholderTextColor={pal.faint}
              keyboardType="numbers-and-punctuation"
              maxLength={5}
              selectTextOnFocus
              {...opsInputClass('well')}
              style={[
                inputStyle,
                {
                  textAlign: 'right',
                  fontWeight: '600',
                  fontSize: 16,
                  color: reasons.includes('time') ? pal.warn || '#e8a43d' : pal.ink,
                },
              ]}
              accessibilityLabel={`Length for ${song.title || 'song'}`}
            />
          ) : (
            <Text
              style={{
                color: reasons.includes('time') ? pal.warn || '#e8a43d' : pal.ink,
                fontSize: 16,
                fontWeight: '600',
                textAlign: 'right',
                paddingVertical: 7,
              }}
            >
              {song.duration || '—'}
            </Text>
          )}
        </View>
      ) : null}
      {showBpm ? (
        <View style={{ width: 80 }}>
          {canEdit ? (
            <TextInput
              value={bpmFocusId === song.id ? bpmDraft : (song.bpm != null ? String(song.bpm) : '')}
              onFocus={() => {
                setBpmFocusId(song.id);
                setBpmDraft(song.bpm != null ? String(song.bpm) : '');
              }}
              onChangeText={(t) => setBpmDraft(parseBpmInputDraft(t))}
              onBlur={() => commitBpm(song.id)}
              onSubmitEditing={() => commitBpm(song.id)}
              placeholder="BPM"
              placeholderTextColor={pal.faint}
              keyboardType="number-pad"
              maxLength={3}
              selectTextOnFocus
              {...opsInputClass('well')}
              style={[
                inputStyle,
                {
                  textAlign: 'right',
                  fontWeight: '600',
                  color: reasons.includes('bpm') ? pal.warn || '#e8a43d' : pal.ink,
                },
              ]}
              accessibilityLabel={`BPM for ${song.title || 'song'}`}
            />
          ) : (
            <Text
              style={{
                color: reasons.includes('bpm') ? pal.warn || '#e8a43d' : pal.ink,
                fontSize: 15,
                fontWeight: '600',
                textAlign: 'right',
                paddingVertical: 7,
              }}
            >
              {song.bpm != null ? String(song.bpm) : '—'}
            </Text>
          )}
        </View>
      ) : null}
      {canEdit ? (
        <TouchableOpacity onPress={() => removeSong(song.id)} accessibilityLabel={labels.removeItemA11y} style={{ paddingTop: 2 }}>
          <Ionicons name="trash-outline" size={16} color={pal.warn || '#c0483c'} />
        </TouchableOpacity>
      ) : null}
    </View>
  );
  };

  const listInner = useDnD ? (
    <SortableContext items={songs.map((song) => song.id)} strategy={verticalListSortingStrategy}>
      {songs.map((song, i) => (
        <SortableStructuredRow key={song.id || i} id={song.id}>
          {(dragProps) => renderRow(song, i, dragProps)}
        </SortableStructuredRow>
      ))}
    </SortableContext>
  ) : (
    songs.map((song, i) => renderRow(song, i))
  );

  return (
    <View style={[s.card, { backgroundColor: pal.surf, borderColor: pal.line, padding: 0, overflow: 'visible', marginTop: 16, width: '100%' }]}>
      <View style={{ flexDirection: 'row', alignItems: 'center', paddingHorizontal: 18, paddingTop: 12, paddingBottom: 8, gap: 10 }}>
        {canEdit ? <View style={{ width: 22 }} /> : null}
        {numberCol ? <Text style={[s.colH, { color: pal.mute, width: 28 }]}>#</Text> : null}
        <Text style={[s.colH, { color: pal.mute, flex: 1 }]}>{titleCol}</Text>
        {showBpm ? <Text style={[s.colH, { color: pal.ink, width: 88, textAlign: 'right' }]}>Time</Text> : null}
        {showBpm ? <Text style={[s.colH, { color: pal.mute, width: 72, textAlign: 'right' }]}>BPM</Text> : null}
      </View>
      {songs.length ? (
        useDnD ? (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            {listInner}
          </DndContext>
        ) : listInner
      ) : (
        <Text style={{ color: pal.mute, padding: 18 }}>
          {canEdit ? `Nothing here yet. ${labels.addItem}.` : 'Nothing on this day yet.'}
        </Text>
      )}
      {canEdit ? (
        <TouchableOpacity
          onPress={addSong}
          style={{
            margin: 14,
            minHeight: 56,
            borderWidth: 1,
            borderStyle: 'dashed',
            borderColor: pal.line,
            borderRadius: 10,
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'row',
            gap: 8,
          }}
        >
          <Ionicons name="add" size={18} color={pal.mute} />
          <Text style={{ color: pal.mute, fontWeight: '600' }}>{labels.addItem}</Text>
        </TouchableOpacity>
      ) : null}
    </View>
  );
}

export function MasterTourSetlistPage({
  setlistText,
  venueName,
  cityLine,
  dateKey,
  tourName,
  tourId,
  isDarkMode,
  accent,
  onPersist,
  onPrintError,
  setlistsByDate = {},
  venueByDate = {},
  partyMembers = [],
  ownerId,
  currentUserId,
  currentUserName,
  tourShareSettings,
  canEdit = true,
}) {
  const pal = palette(isDarkMode);
  const db = getFirestore();
  const [note, setNote] = useState('');
  const [undo, setUndo] = useState(null);
  const [pickedModeratorId, setPickedModeratorId] = useState(null);
  const seededRef = useRef('');
  const venueLine = [venueName, cityLine].filter(Boolean).join(' · ');
  const printRaw = officialSetlistRaw(setlistText);
  const stats = summarizeSetlistTiming(printRaw);
  const displayRaw = serializeSetlistDoc({ songs: setlistDisplaySongs(setlistText) });
  const highlights = setlistHighlightMap(setlistText);
  const parsed = parseSetlistDoc(setlistText);
  const pending = parsed.pending;
  const canPublish = canPublishSetlist(currentUserId, ownerId, {
    ...tourShareSettings,
    setlistModeratorId: pickedModeratorId || tourShareSettings?.setlistModeratorId,
  });
  const moderatorId = pickedModeratorId || resolveSetlistModeratorId(ownerId, tourShareSettings);
  const moderator = (partyMembers || []).find((m) => String(m.id || '') === String(moderatorId || ''))
    || (moderatorId === ownerId ? (partyMembers || []).find((m) => String(m.id || '') === String(ownerId || '')) : null);
  const moderatorLabel = moderator?.name || moderator?.displayName || (moderatorId === ownerId ? 'Tour owner' : 'Tour owner');
  const isOwner = isTourOwner(currentUserId, ownerId);

  useEffect(() => {
    if (!canPublish || !onPersist) return;
    if (!needsHawkBpmSeed(setlistText, tourId, tourName)) return;
    const key = `${tourId || ''}:${dateKey || ''}:${parsed.songs.map((s) => s.id).join(',')}`;
    if (seededRef.current === key) return;
    seededRef.current = key;
    onPersist(serializeSetlistDocWithPending({
      songs: seedHawkSongBpms(parsed.songs),
      pending: parsed.pending,
    }));
  }, [canPublish, dateKey, onPersist, parsed.pending, parsed.songs, setlistText, tourId, tourName]);

  const persistFromEditor = (nextRaw) => {
    const nextSongs = parseSetlistText(nextRaw).songs || [];
    const hadPending = Boolean(parseSetlistDoc(setlistText).pending);
    const next = applySetlistEditorChange({
      currentRaw: setlistText,
      nextSongs,
      actorId: currentUserId,
      actorName: currentUserName,
      canPublish,
    });
    onPersist?.(next);
    const nowPending = Boolean(parseSetlistDoc(next).pending);
    if (!canPublish && nowPending && !hadPending) {
      const recipients = [ownerId, tourShareSettings?.setlistModeratorId].filter(Boolean);
      notifySetlistProposal({
        db,
        senderId: currentUserId,
        senderName: currentUserName,
        recipientIds: recipients,
        tourName,
        dateKey,
        dateLabel: formatSetlistDateLabel(dateKey),
      }).catch((err) => console.warn('setlist proposal notify', err));
    }
  };

  const handlePrintPdf = () => {
    if (Platform.OS !== 'web') {
      Alert.alert('Print setlist', 'Print and Save as PDF are available in the web app.');
      return;
    }
    try {
      openSetlistPrintWindow({
        dateLabel: formatSetlistDateLabel(dateKey),
        setlistRaw: printRaw,
        tourName: tourName || 'Untitled tour',
        venueLine,
      });
    } catch (err) {
      const message = err?.message || 'Could not open setlist print view.';
      if (onPrintError) onPrintError(message);
      else Alert.alert('Print setlist', message);
    }
  };

  const handlePrintAll = () => {
    if (Platform.OS !== 'web') {
      Alert.alert('Print setlists', 'Print and Save as PDF are available in the web app.');
      return;
    }
    try {
      const days = Object.keys(setlistsByDate || {}).sort().map((key) => ({
        dateKey: key,
        dateLabel: formatSetlistDateLabel(key),
        setlistRaw: officialSetlistRaw(setlistsByDate[key]),
        venueLine: venueByDate?.[key] || '',
      }));
      openSetlistPrintAllWindow({ days, tourName: tourName || 'Untitled tour' });
    } catch (err) {
      const message = err?.message || 'Could not print all setlists.';
      if (onPrintError) onPrintError(message);
      else Alert.alert('Print setlists', message);
    }
  };

  const assignModerator = (memberUid) => {
    if (!isOwner) {
      setNote('Only the tour owner can pick the setlist moderator.');
      return;
    }
    if (!ownerId || !tourId) {
      setNote('Could not change setlist moderator — tour is missing.');
      return;
    }
    const nextId = String(memberUid || '').trim();
    setPickedModeratorId(nextId || ownerId);
    const label = (partyMembers || []).find((row) => String(row.id || '') === (nextId || ownerId));
    setNote(`Setlist moderator: ${nextId ? (label?.name || 'Member') : 'Tour owner'}. They have final say on order and BPM.`);
    saveSetlistModerator(db, ownerId, tourId, nextId, tourShareSettings || {})
      .catch((err) => {
        console.error('saveSetlistModerator', err);
        setPickedModeratorId(null);
        setNote(err?.message || 'Could not change setlist moderator.');
      });
  };

  return (
    <View style={{ backgroundColor: pal.page, padding: 24, paddingBottom: 48, width: '100%' }}>
      <View style={{ flexDirection: 'row', justifyContent: 'space-between', borderBottomWidth: 2, borderBottomColor: accent || pal.teal, paddingBottom: 14, flexWrap: 'wrap', gap: 12 }}>
        <View>
          <Text style={[s.label, { color: pal.mute, marginBottom: 5 }]}>Setlist</Text>
          <Text style={[s.heroSm, { color: pal.ink, fontSize: 27 }]}>{venueName || 'Setlist'}</Text>
          <Text style={{ color: pal.mute, marginTop: 4 }}>{[cityLine, formatDateLong(dateKey)].filter(Boolean).join(' · ')}</Text>
          <Text style={{ color: pal.ink, marginTop: 8, fontSize: 13.5 }}>
            Setlist moderator: {moderatorLabel}
            {canPublish ? ' · You can publish' : ' · Order and BPM need approval'}
          </Text>
          {isOwner ? (
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: 8, zIndex: 2 }}>
              {(partyMembers || []).filter((m) => m.id && String(m.status || 'accepted').toLowerCase() === 'accepted').map((m) => {
                const uid = String(m.id || '');
                const active = uid === String(moderatorId || '');
                return (
                  <TouchableOpacity
                    key={`${uid}:${m.email || m.name || ''}`}
                    onPress={() => assignModerator(uid === ownerId ? '' : uid)}
                    accessibilityRole="button"
                    accessibilityLabel={`Set ${m.name || 'member'} as setlist moderator`}
                    style={[
                      s.outlineBtn,
                      {
                        borderColor: active ? pal.teal : pal.line,
                        backgroundColor: active ? hexWithAlpha(pal.teal, 0.22) : pal.sunk,
                        ...(Platform.OS === 'web' ? { cursor: 'pointer' } : null),
                      },
                    ]}
                  >
                    <Text style={{ color: pal.ink, fontWeight: '600', fontSize: 12.5 }}>
                      {uid === ownerId ? 'Tour owner' : (m.name || 'Member')}
                      {active ? ' · moderator' : ''}
                    </Text>
                  </TouchableOpacity>
                );
              })}
              <Text style={{ color: pal.mute, fontSize: 12, width: '100%' }}>
                Tap a name to give them final say on order and BPM.
              </Text>
            </View>
          ) : (
            <Text style={{ color: pal.mute, fontSize: 12.5, marginTop: 6 }}>
              Ask the tour owner to assign setlist moderator from these names.
            </Text>
          )}
        </View>
        <View style={{ alignItems: 'flex-end', gap: 10, justifyContent: 'flex-end' }}>
          <View style={{ flexDirection: 'row', gap: 18 }}>
            <Stat pal={pal} k="Songs" v={String(stats.songCount)} />
            <Stat pal={pal} k="Show time" v={stats.lengthLabel} />
            <Stat pal={pal} k="Avg BPM" v={stats.avgBpm ? String(stats.avgBpm) : '—'} />
          </View>
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, justifyContent: 'flex-end' }}>
            {canEdit ? (
              <FileImportButton
                label="Import setlist"
                accept=".txt,.csv,text/plain"
                pal={pal}
                onText={(text, name) => {
                  const imported = parseSetlistText(text);
                  if (!imported.songs?.length) {
                    setNote(`Nothing readable in ${name}.`);
                    setUndo(null);
                    return;
                  }
                  const songs = isHawkDemoTour(tourId, tourName) ? seedHawkSongBpms(imported.songs) : imported.songs;
                  setUndo(setlistText);
                  persistFromEditor(serializeSetlistDoc({ songs }));
                  setNote(`Imported ${songs.length} song(s) from ${name}.`);
                }}
              />
            ) : null}
            <TouchableOpacity
              onPress={handlePrintPdf}
              style={[s.outlineBtn, { borderColor: pal.ink, flexDirection: 'row', alignItems: 'center', gap: 6 }]}
              accessibilityLabel="Print this day's setlist as PDF"
            >
              <Ionicons name="print-outline" size={16} color={pal.ink} />
              <Text style={{ color: pal.ink, fontWeight: '600' }}>Print PDF</Text>
            </TouchableOpacity>
            <TouchableOpacity
              onPress={handlePrintAll}
              style={[s.outlineBtn, { borderColor: pal.ink }]}
              accessibilityLabel="Print PDFs for all setlists this month"
            >
              <Text style={{ color: pal.ink, fontWeight: '600' }}>Print PDFs for All</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
      {pending ? (
        <View style={{ marginTop: 12, padding: 12, backgroundColor: hexWithAlpha(pal.warn || '#e8a43d', 0.16), borderWidth: 1, borderColor: pal.warn || '#e8a43d', borderRadius: 9, gap: 10 }}>
          <Text style={{ color: pal.ink, fontSize: 14.5 }}>
            Pending setlist from {pending.byName || 'a tour member'}. Highlighted rows are proposed order / time / BPM / title. Notes are already live. Print still uses the last accepted list.
          </Text>
          {canPublish ? (
            <View style={{ flexDirection: 'row', gap: 8 }}>
              <TouchableOpacity
                onPress={() => onPersist?.(acceptSetlistPending(setlistText))}
                style={[s.primaryBtn, { backgroundColor: pal.ink }]}
              >
                <Text style={{ color: pal.page, fontWeight: '600' }}>Accept proposal</Text>
              </TouchableOpacity>
              <TouchableOpacity
                onPress={() => onPersist?.(rejectSetlistPending(setlistText))}
                style={[s.outlineBtn, { borderColor: pal.line }]}
              >
                <Text style={{ color: pal.ink, fontWeight: '600' }}>Keep current</Text>
              </TouchableOpacity>
            </View>
          ) : (
            <Text style={{ color: pal.mute, fontSize: 13 }}>Waiting on the setlist moderator to accept or rewrite this.</Text>
          )}
        </View>
      ) : null}
      {note ? (
        <View style={{ flexDirection: 'row', gap: 12, marginTop: 12, padding: 12, backgroundColor: pal.sunk, borderWidth: 1, borderColor: pal.teal, borderRadius: 9, alignItems: 'center' }}>
          <Text style={{ flex: 1, color: pal.ink, fontSize: 14.5 }}>{note}</Text>
          {undo != null ? (
            <TouchableOpacity onPress={() => { onPersist?.(undo); setUndo(null); setNote('Import reverted.'); }} style={[s.outlineBtn, { borderColor: pal.teal }]}>
              <Text style={{ color: pal.teal, fontWeight: '600', fontSize: 13.5 }}>Undo</Text>
            </TouchableOpacity>
          ) : null}
        </View>
      ) : null}
      <Text style={{ color: pal.mute, fontSize: 12.5, marginTop: 10 }}>
        Time, BPM, and song order are shared. Notes save immediately. Only the accepted list is printed.
      </Text>
      <InlineStructuredList
        value={displayRaw}
        onChange={canEdit ? persistFromEditor : undefined}
        pal={pal}
        labels={SETLIST_LABELS}
        titleCol="Song"
        showBpm
        highlightById={highlights}
      />
    </View>
  );
}

function allocColors(pal, status) {
  if (status === GUEST_ALLOC.IN) {
    return { fg: pal.page, bg: pal.ok, row: hexWithAlpha(pal.ok, 0.16), chip: hexWithAlpha(pal.ok, 0.22) };
  }
  if (status === GUEST_ALLOC.WAITLIST) {
    return { fg: pal.ink, bg: pal.warn, row: hexWithAlpha(pal.warn, 0.16), chip: hexWithAlpha(pal.warn, 0.22) };
  }
  if (status === GUEST_ALLOC.CONFLICT) {
    return { fg: pal.page, bg: pal.alert, row: hexWithAlpha(pal.alert, 0.16), chip: hexWithAlpha(pal.alert, 0.22) };
  }
  if (status === GUEST_ALLOC.CUT) {
    return { fg: pal.mute, bg: pal.sunk, row: hexWithAlpha(pal.mute, 0.12), chip: pal.sunk };
  }
  return { fg: pal.ink, bg: pal.sunk, row: 'transparent', chip: pal.sunk };
}

export function MasterTourGuestListPage({
  guestText,
  venueName,
  dateKey,
  showDateKeys,
  tourDayMetaByDate,
  selectedTourFilterId,
  isDarkMode,
  accent,
  onSelectDate,
  onPersist,
  spotsRaw,
  onPersistSpots,
  venueMode = false,
  guestAsMe = '',
  venueLinkUrl = '',
  onEnsureVenueLink,
  spotsSaving = false,
  spotsError = '',
}) {
  const pal = palette(isDarkMode);
  const canEdit = Boolean(onPersist) && !venueMode;
  const canSetSpots = Boolean(onPersistSpots);
  const [editingList, setEditingList] = useState(false);
  const [gName, setGName] = useState('');
  const [gNote, setGNote] = useState('');
  const [spotsDraft, setSpotsDraft] = useState(() => {
    const n = parseGuestCap(spotsRaw);
    return n == null ? '' : String(n);
  });
  const spotsDirty = useRef(false);

  useEffect(() => {
    const n = parseGuestCap(spotsRaw);
    const incoming = n == null ? '' : String(n);
    if (venueMode && spotsDirty.current) return;
    setSpotsDraft(incoming);
  }, [spotsRaw, dateKey, venueMode]);

  const songs = parseSetlistText(guestText).songs || [];
  const parties = useMemo(() => parseGuestParties(songs), [guestText]);
  const spots = parseGuestCap(spotsDraft === '' ? spotsRaw : serializeGuestCapDraft(spotsDraft));
  const allocs = useMemo(() => allocateGuestParties(parties, spots), [parties, spots]);
  const totalNames = countGuestNames(parties);
  const inCount = parties.reduce((n, p, i) => n + (allocs[i]?.status === GUEST_ALLOC.IN ? p.size : 0), 0);
  const conflictCount = allocs.filter((a) => a.status === GUEST_ALLOC.CONFLICT).length;
  const waitCount = allocs.filter((a) => a.status === GUEST_ALLOC.WAITLIST).length;
  const upcoming = useShowDates({ tourDayMetaByDate, selectedTourFilterId, showDateKeys, selectedDateKey: dateKey });
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } })
  );
  const spotsFilled = spotsDraft !== '';
  const canSaveSpots = canSetSpots && spotsFilled && !spotsSaving;

  const persistSongs = (next) => onPersist?.(serializeSetlistDoc({ songs: next }));

  const writeSpots = (text) => {
    if (!onPersistSpots) return Promise.resolve();
    const trimmed = sanitizeSpotCountInput(text);
    if (trimmed === '') return Promise.resolve();
    const n = Number(trimmed);
    if (!Number.isFinite(n) || n < 0) return Promise.resolve();
    return Promise.resolve(onPersistSpots(Math.floor(n)));
  };

  const persistSpotsDebounced = useDebouncedCallback(writeSpots, 450);

  const onSpotsKeyPress = (event) => {
    const key = String(event?.nativeEvent?.key || '');
    if (!key || key.length !== 1) return;
    if (!/\d/.test(key)) event.preventDefault?.();
  };

  const onSpotsChange = (text) => {
    const next = sanitizeSpotCountInput(text);
    if (venueMode) spotsDirty.current = true;
    setSpotsDraft(next);
    if (!venueMode && next !== '') persistSpotsDebounced(next);
  };

  const saveSpots = async () => {
    if (!canSaveSpots) return;
    try {
      await writeSpots(spotsDraft);
      spotsDirty.current = false;
    } catch {
      /* keep the typed count so a later snapshot cannot wipe it */
    }
  };

  const addGuests = () => {
    const names = String(gName || '').split(/,|\n/).map((s) => s.trim()).filter(Boolean);
    if (!names.length || !canEdit) return;
    persistSongs([...songs, newGuestPartySong({ who: guestAsMe || names[0], names, note: gNote })]);
    setGName('');
    setGNote('');
  };

  const handleDragEnd = ({ active, over }) => {
    if (!canEdit || !over || active.id === over.id) return;
    persistSongs(reorderListById(songs, active.id, over.id));
  };

  const statusSummary = spots == null
    ? 'Venue has not set spots yet'
    : conflictCount
      ? `${conflictCount} conflict${conflictCount === 1 ? '' : 's'} to sort`
      : waitCount
        ? `${inCount} in · ${waitCount} waitlist`
        : `${inCount} in`;

  const table = (
    <View style={[s.card, { backgroundColor: pal.surf, borderColor: pal.line, borderTopWidth: 3, borderTopColor: accent || pal.teal, padding: 0, overflow: 'hidden' }]}>
      <View style={{ flexDirection: 'row', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16, padding: 20, paddingBottom: 14 }}>
        <View style={{ minWidth: 180, flex: 1 }}>
          <Text style={[s.label, { color: pal.teal, marginBottom: 5 }]}>{venueMode ? 'Venue guest list' : 'Tonight'}</Text>
          <Text style={[s.heroSm, { color: pal.ink, fontSize: 27 }]}>{venueName || 'Guest list'}</Text>
          <Text style={{ color: pal.mute, marginTop: 4 }}>{formatDateLong(dateKey)}</Text>
        </View>
        <View style={{ flexDirection: 'row', gap: 22, flexWrap: 'wrap' }}>
          <View>
            <Text style={[s.label, { color: pal.mute }]}>Total</Text>
            <Text style={{ fontSize: 19, fontWeight: '600', color: pal.ink }}>{totalNames} names</Text>
          </View>
          <View>
            <Text style={[s.label, { color: pal.mute }]}>Status</Text>
            <Text style={{ fontSize: 19, fontWeight: '600', color: conflictCount ? pal.alert : pal.ink }}>{statusSummary}</Text>
          </View>
          <View style={{ minWidth: 148 }}>
            <Text style={[s.label, { color: pal.mute }]}>Spots available</Text>
            {canSetSpots ? (
              <TextInput
                value={spotsDraft}
                onChangeText={onSpotsChange}
                onKeyPress={onSpotsKeyPress}
                onSubmitEditing={venueMode ? saveSpots : undefined}
                keyboardType="number-pad"
                inputMode="numeric"
                maxLength={4}
                placeholder={SPOTS_PLACEHOLDER}
                placeholderTextColor={pal.faint}
                accessibilityLabel="How many guest list spots are available tonight"
                autoCorrect={false}
                autoComplete="off"
                importantForAutofill="no"
                {...opsInputClass('well')}
                style={[
                  fieldWellStyle(pal, isDarkPal(pal)),
                  {
                    fontSize: 19,
                    fontWeight: '600',
                    color: pal.ink,
                    borderWidth: 1,
                    borderRadius: 8,
                    paddingHorizontal: 10,
                    paddingVertical: 6,
                    minWidth: 88,
                    textAlign: 'right',
                  },
                ]}
              />
            ) : (
              <Text style={{ fontSize: 19, fontWeight: '600', color: pal.ink }}>{spots == null ? '—' : spots}</Text>
            )}
            <Text style={{ color: pal.mute, fontSize: 11, marginTop: 4, maxWidth: 160 }}>
              {venueMode ? 'Enter a number, then press Save.' : 'How many people the venue can take tonight.'}
            </Text>
            {spotsSaving ? <Text style={{ color: pal.mute, fontSize: 11, marginTop: 4 }}>Saving…</Text> : null}
            {spotsError ? <Text style={{ color: pal.alert, fontSize: 11, marginTop: 4 }}>{spotsError}</Text> : null}
          </View>
        </View>
      </View>
      <Text style={{ color: pal.mute, fontSize: 13, paddingHorizontal: 20, paddingBottom: 10 }}>
        Priority is top to bottom. Green is in, yellow is waitlist, red is a party that only partly fits — that one has to be decided, not auto-notified.
      </Text>
      <View style={[s.tierHead, { paddingHorizontal: 20 }]}>
        <Text style={[s.colH, { flex: 1.2, color: pal.mute }]}>Requested by</Text>
        <Text style={[s.colH, { width: 64, textAlign: 'right', color: pal.mute }]}>Names</Text>
        <Text style={[s.colH, { flex: 1.1, color: pal.mute }]}>Note</Text>
        <Text style={[s.colH, { width: 118, textAlign: 'right', color: pal.mute }]}>Status</Text>
      </View>
      {parties.length ? (
        canEdit ? (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={parties.map((p) => p.id)} strategy={verticalListSortingStrategy}>
              {parties.map((p, i) => (
                <SortableStructuredRow key={p.id} id={p.id}>
                  {({ attributes, listeners }) => (
                    <GuestPartyRow
                      party={p}
                      alloc={allocs[i]}
                      pal={pal}
                      canEdit={canEdit}
                      onPatch={(patch) => {
                        persistSongs(songs.map((song) => {
                          if (song.id !== p.id) return song;
                          if (patch.cut) return toggleGuestPartyCut(song);
                          return patchGuestPartySong(song, patch);
                        }));
                      }}
                      dragHandleProps={{ attributes, listeners }}
                    />
                  )}
                </SortableStructuredRow>
              ))}
            </SortableContext>
          </DndContext>
        ) : (
          parties.map((p, i) => (
            <GuestPartyRow key={p.id} party={p} alloc={allocs[i]} pal={pal} />
          ))
        )
      ) : (
        <Text style={{ color: pal.mute, paddingHorizontal: 20, paddingVertical: 18 }}>No guests on this day yet.</Text>
      )}
    </View>
  );

  return (
    <View
      style={{
        backgroundColor: pal.page,
        padding: 24,
        paddingBottom: venueMode ? 120 : 48,
        width: '100%',
        minHeight: venueMode ? 'calc(100dvh - 88px)' : undefined,
      }}
    >
      {table}
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 10, marginTop: 12, alignItems: 'center' }}>
        {canEdit ? (
          <TouchableOpacity
            onPress={() => setEditingList((v) => !v)}
            style={[s.outlineBtn, { borderColor: pal.line, backgroundColor: pal.surf }]}
          >
            <Text style={{ color: pal.ink, fontWeight: '600' }}>{editingList ? 'Done' : 'Edit list'}</Text>
          </TouchableOpacity>
        ) : null}
        {onEnsureVenueLink ? (
          <TouchableOpacity
            onPress={onEnsureVenueLink}
            style={[s.outlineBtn, { borderColor: pal.teal }]}
          >
            <Text style={{ color: pal.teal, fontWeight: '600' }}>
              {venueLinkUrl ? 'Copy venue link' : 'Make venue link'}
            </Text>
          </TouchableOpacity>
        ) : null}
        {venueLinkUrl ? (
          <Text style={{ color: pal.mute, fontSize: 12.5, flex: 1, minWidth: 180 }} numberOfLines={2}>
            Persistent link for the venue manager to set spots: {venueLinkUrl}
          </Text>
        ) : null}
      </View>
      {editingList && canEdit ? (
        <InlineStructuredList
          value={guestText}
          onChange={onPersist}
          pal={pal}
          labels={GUEST_LIST_LABELS}
          numberCol={false}
          titleCol="Guest"
        />
      ) : null}
      {canEdit ? (
        <View style={[s.card, { backgroundColor: pal.surf, borderColor: pal.line, marginTop: 14 }]}>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
            <Text style={[s.label, { color: pal.mute }]}>Add guests</Text>
            <Text style={{ color: pal.mute, fontSize: 12.5 }}>Requesting as {guestAsMe || 'you'}</Text>
          </View>
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 9, alignItems: 'center' }}>
            <TextInput
              value={gName}
              onChangeText={setGName}
              placeholder="Names, comma separated"
              placeholderTextColor={pal.faint}
              {...opsInputClass('well')}
              style={[s.field, fieldWellStyle(pal, isDarkPal(pal)), { width: undefined, flex: 1.4, minWidth: 180, color: pal.ink }]}
            />
            <TextInput
              value={gNote}
              onChangeText={setGNote}
              placeholder="Note, e.g. 2 photo passes"
              placeholderTextColor={pal.faint}
              {...opsInputClass('well')}
              style={[s.field, fieldWellStyle(pal, isDarkPal(pal)), { width: undefined, flex: 1, minWidth: 140, color: pal.ink }]}
            />
            <TouchableOpacity onPress={addGuests} style={[s.primaryBtn, { backgroundColor: pal.ink }]}>
              <Text style={{ color: pal.page, fontWeight: '600' }}>Request</Text>
            </TouchableOpacity>
          </View>
          <Text style={{ color: pal.mute, fontSize: 12.5, marginTop: 9 }}>
            Names land on this day in list order. Drag a row to change priority before the venue sets spots.
          </Text>
        </View>
      ) : null}
      <Text style={[s.label, { color: pal.mute, marginTop: 26, marginBottom: 10 }]}>Next dates</Text>
      <View style={[s.card, { backgroundColor: pal.surf, borderColor: pal.line, padding: 0, overflow: 'hidden' }]}>
        {upcoming.map((u) => (
          <TouchableOpacity key={u.key} onPress={() => onSelectDate?.(u.key)} style={[s.tierRow, { borderTopColor: pal.lineSoft, paddingHorizontal: 18 }]}>
            <Text style={{ flex: 1, fontWeight: '600', color: pal.ink }}>{u.label}</Text>
            <Text style={{ flex: 1, color: pal.ink }}>{u.city}</Text>
          </TouchableOpacity>
        ))}
      </View>
      {venueMode ? (
        <View
          style={[
            s.spotsSaveBar,
            {
              backgroundColor: pal.surf,
              borderTopColor: pal.line,
              ...(Platform.OS === 'web' ? { position: 'fixed' } : { position: 'absolute' }),
            },
          ]}
        >
          <Text style={{ color: pal.mute, fontSize: 13, flex: 1, minWidth: 160 }}>
            Save how many spots you have available tonight.
          </Text>
          <TouchableOpacity
            onPress={saveSpots}
            disabled={!canSaveSpots}
            accessibilityRole="button"
            accessibilityState={{ disabled: !canSaveSpots }}
            style={[
              s.primaryBtn,
              {
                backgroundColor: pal.ink,
                minWidth: 148,
                alignItems: 'center',
                opacity: canSaveSpots ? 1 : 0.35,
                ...(Platform.OS === 'web' ? { cursor: canSaveSpots ? 'pointer' : 'not-allowed' } : {}),
              },
            ]}
          >
            <Text style={{ color: pal.page, fontWeight: '700', fontSize: 16 }}>
              {spotsSaving ? 'Saving…' : 'Save spots'}
            </Text>
          </TouchableOpacity>
        </View>
      ) : null}
    </View>
  );
}

function serializeGuestCapDraft(text) {
  const trimmed = String(text ?? '').trim();
  if (trimmed === '') return '';
  const n = Number(trimmed);
  if (!Number.isFinite(n) || n < 0) return '';
  return JSON.stringify({ v: 1, spots: Math.floor(n) });
}

function GuestPartyRow({ party, alloc, pal, dragHandleProps, canEdit, onPatch }) {
  const status = alloc?.status || GUEST_ALLOC.UNSET;
  const colors = allocColors(pal, status);
  const handleStyle = {
    width: 22,
    alignItems: 'center',
    justifyContent: 'center',
    ...(Platform.OS === 'web' ? { cursor: 'grab', touchAction: 'none' } : {}),
  };
  return (
    <View style={{ paddingHorizontal: 18, paddingVertical: 12, borderTopWidth: 1, borderTopColor: pal.lineSoft, backgroundColor: colors.row }}>
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
        {dragHandleProps ? (
          <TouchableOpacity style={handleStyle} accessibilityLabel="Reorder priority" {...(dragHandleProps.listeners || {})} {...(dragHandleProps.attributes || {})}>
            <Ionicons name="reorder-three" size={20} color={pal.mute} />
          </TouchableOpacity>
        ) : null}
        {canEdit ? (
          <TextInput
            value={party.who}
            onChangeText={(v) => onPatch?.({ who: v })}
            placeholder="Requested by"
            placeholderTextColor={pal.faint}
            {...opsInputClass('blend')}
            style={[inlineBlendInputStyle, { flex: 1.2, fontSize: 14, fontWeight: '500', color: pal.ink }]}
          />
        ) : (
          <Text style={{ flex: 1.2, fontSize: 14, fontWeight: '500', color: pal.ink }}>{party.who}</Text>
        )}
        <Text style={{ width: 64, textAlign: 'right', fontSize: 15, fontWeight: '600', color: pal.ink }}>{party.size}</Text>
        {canEdit ? (
          <TextInput
            value={party.note}
            onChangeText={(v) => onPatch?.({ note: v })}
            placeholder="Note"
            placeholderTextColor={pal.faint}
            {...opsInputClass('blend')}
            style={[inlineBlendInputStyle, { flex: 1.1, fontSize: 13, color: pal.mute }]}
          />
        ) : (
          <Text style={{ flex: 1.1, fontSize: 13, color: pal.mute }}>{party.note}</Text>
        )}
        <View style={{ width: 118, alignItems: 'flex-end', gap: 6 }}>
          <View style={{ backgroundColor: colors.bg, borderRadius: 4, paddingHorizontal: 8, paddingVertical: 3 }}>
            <Text style={{ fontSize: 10.5, fontWeight: '600', letterSpacing: 0.7, textTransform: 'uppercase', color: colors.fg }}>
              {guestAllocLabel(status)}
            </Text>
          </View>
          {canEdit ? (
            <TouchableOpacity onPress={() => onPatch?.({ cut: true })}>
              <Text style={{ fontSize: 11, fontWeight: '600', color: party.cut ? pal.teal : pal.mute }}>
                {party.cut ? 'Uncut' : 'Cut stop'}
              </Text>
            </TouchableOpacity>
          ) : null}
        </View>
      </View>
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 5, marginTop: 8, paddingLeft: dragHandleProps ? 32 : 0 }}>
        {(party.names || []).map((name, ni) => (
          <View key={`${party.id}-${ni}`} style={{ backgroundColor: ni === 0 ? colors.chip : pal.sunk, borderRadius: 5, paddingHorizontal: 9, paddingVertical: 3 }}>
            <Text style={{ fontSize: 12.5, color: pal.ink }}>{name}</Text>
          </View>
        ))}
        {status === GUEST_ALLOC.CONFLICT ? (
          <Text style={{ fontSize: 12.5, color: pal.alert, fontWeight: '600' }}>
            {alloc.spotsUsed} of {party.size} would fit — pick who goes.
          </Text>
        ) : null}
      </View>
    </View>
  );
}

export function MasterTourDaySheetPoster({
  tourName,
  dateKey,
  venueName,
  cityLine,
  scheduleText,
  isDarkMode,
  accent,
  onPersist,
  bannerUrl = '',
  backgroundUrl = '',
  onUploadBanner,
  onUploadBackground,
  assetSaving = false,
}) {
  const pal = palette(isDarkMode);
  const canEdit = Boolean(onPersist);
  const hasBanner = Boolean(String(bannerUrl || '').trim());
  const hasBg = Boolean(String(backgroundUrl || '').trim());
  const { lines } = parseScheduleText(String(scheduleText || ''));
  const visibleLines = (lines || []).filter((ln) => ln && ln.kind !== 'blank');
  const [pdfBusy, setPdfBusy] = useState(false);

  const printSheet = () => {
    let preview = null;
    if (typeof window !== 'undefined') {
      preview = window.open('about:blank', '_blank');
    }
    setPdfBusy(true);
    const rows = buildDaySheetPreviewRows(scheduleText, { useDefaults: false });
    downloadDaySheetPdf({
      tourName,
      dateKey,
      headline: formatDateLong(dateKey),
      venueName,
      cityLine,
      bannerUrl,
      backgroundUrl,
      scheduleRows: rows,
      fileName: `day-sheet_${dateKey || 'date'}.pdf`,
      openWindow: preview,
    }).catch((err) => {
      console.error('day sheet pdf', err);
      if (preview && !preview.closed) preview.close();
    }).finally(() => setPdfBusy(false));
  };

  return (
    <View style={{ flex: 1, backgroundColor: pal.page, width: '100%' }}>
      <View style={{
        flexDirection: 'row',
        flexWrap: 'wrap',
        justifyContent: 'flex-end',
        alignItems: 'center',
        gap: 8,
        paddingHorizontal: 20,
        paddingTop: 16,
        paddingBottom: 10,
        borderBottomWidth: 2,
        borderBottomColor: accent || pal.teal,
      }}>
        {canEdit ? (
          <>
            <TouchableOpacity
              onPress={() => onPersist?.(appendDaySheetCue(scheduleText))}
              style={[s.outlineBtn, { borderColor: pal.line }]}
            >
              <Text style={{ color: pal.ink, fontWeight: '600' }}>Add call time</Text>
            </TouchableOpacity>
            <TouchableOpacity
              onPress={() => onPersist?.(appendDaySheetText(scheduleText))}
              style={[s.outlineBtn, { borderColor: pal.line }]}
            >
              <Text style={{ color: pal.ink, fontWeight: '600' }}>+ Text line</Text>
            </TouchableOpacity>
            {onUploadBanner ? (
              <TouchableOpacity
                onPress={onUploadBanner}
                disabled={assetSaving}
                style={[s.outlineBtn, { borderColor: hasBanner ? (accent || pal.teal) : pal.line, opacity: assetSaving ? 0.5 : 1 }]}
              >
                <Text style={{ color: hasBanner ? (accent || pal.teal) : pal.ink, fontWeight: '600' }}>
                  {hasBanner ? 'Replace banner' : 'Banner logo'}
                </Text>
              </TouchableOpacity>
            ) : null}
            {onUploadBackground ? (
              <TouchableOpacity
                onPress={onUploadBackground}
                disabled={assetSaving}
                style={[s.outlineBtn, { borderColor: hasBg ? (accent || pal.teal) : pal.line, opacity: assetSaving ? 0.5 : 1 }]}
              >
                <Text style={{ color: hasBg ? (accent || pal.teal) : pal.ink, fontWeight: '600' }}>
                  {hasBg ? 'Replace watermark' : 'Watermark'}
                </Text>
              </TouchableOpacity>
            ) : null}
          </>
        ) : null}
        <TouchableOpacity
          onPress={printSheet}
          disabled={pdfBusy}
          style={[s.outlineBtn, { borderColor: pal.ink, opacity: pdfBusy ? 0.5 : 1 }]}
        >
          <Text style={{ color: pal.ink, fontWeight: '600' }}>{pdfBusy ? 'Opening PDF…' : 'Print day sheet'}</Text>
        </TouchableOpacity>
      </View>
      <ScrollView
        style={{ flex: 1 }}
        contentContainerStyle={{ padding: 24, alignItems: 'center' }}
      >
        <View
          style={[
            s.card,
            {
              width: '100%',
              maxWidth: 620,
              backgroundColor: pal.surf,
              borderColor: pal.line,
              paddingVertical: 40,
              paddingHorizontal: 36,
              overflow: 'visible',
              position: 'relative',
            },
          ]}
        >
          {hasBg ? (
            <Image
              source={{ uri: backgroundUrl }}
              style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, width: '100%', height: '100%', opacity: isDarkMode ? 0.22 : 0.16 }}
              resizeMode="contain"
            />
          ) : null}
          {hasBanner ? (
            <Image
              source={{ uri: bannerUrl }}
              style={{ width: '100%', height: 88, marginBottom: 16, zIndex: 1 }}
              resizeMode="contain"
            />
          ) : null}
          <Text
            style={{
              fontSize: 42,
              lineHeight: 46,
              fontWeight: '700',
              textAlign: 'center',
              letterSpacing: 0.4,
              textTransform: 'uppercase',
              color: pal.ink,
              zIndex: 1,
            }}
          >
            {tourName || 'Day sheet'}
          </Text>
          <View style={{
            borderWidth: 1.5,
            borderColor: pal.ink,
            padding: 14,
            marginVertical: 22,
            zIndex: 1,
          }}>
            <Text style={{
              textAlign: 'center',
              fontStyle: 'italic',
              fontWeight: '700',
              fontSize: 18,
              lineHeight: 26,
              color: pal.alert,
            }}>
              {[formatDateLong(dateKey), venueName, cityLine].filter(Boolean).join('\n')}
            </Text>
          </View>
          <View style={{ zIndex: 1 }}>
            {canEdit ? (
              <DaySheetScheduleEditor
                value={scheduleText || ''}
                onChange={onPersist}
                pal={pal}
                simpleActions
                hideFooter
              />
            ) : visibleLines.length ? visibleLines.map((sr, i) => (
              <View
                key={sr.id || `${sr.time}-${i}`}
                style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, paddingVertical: 4 }}
              >
                <Text style={{
                  flexShrink: 1,
                  fontWeight: '700',
                  letterSpacing: 0.4,
                  textTransform: sr.kind === 'text' ? 'none' : 'uppercase',
                  color: pal.ink,
                  fontSize: 15,
                }}>
                  {sr.kind === 'text' ? sr.text : (sr.item || sr.notes)}
                </Text>
                {sr.kind === 'text' ? null : (
                  <Text style={{ fontFamily: FONT.mono, color: pal.ink, fontSize: 15, flexShrink: 0 }}>
                    {[sr.time, sr.period].filter(Boolean).join(' ')}
                  </Text>
                )}
              </View>
            )) : (
              <Text style={{ textAlign: 'center', color: pal.mute }}>No schedule times on this day.</Text>
            )}
          </View>
        </View>
      </ScrollView>
    </View>
  );
}

const s = StyleSheet.create({
  label: { fontFamily: FONT.mono, fontSize: 12, letterSpacing: 1.2, textTransform: 'uppercase' },
  meta: { fontFamily: FONT.mono, fontSize: 11.5 },
  kicker: { fontFamily: FONT.mono, fontSize: 12.5, letterSpacing: 0.7, textTransform: 'uppercase' },
  hero: { fontSize: 36, fontWeight: '700', letterSpacing: -0.6 },
  heroSm: { fontSize: 24, fontWeight: '700', letterSpacing: -0.4 },
  card: { borderWidth: 1, borderRadius: 12, padding: 18 },
  ledgerCard: { flexDirection: 'column' },
  ledgerCardFoot: { marginTop: 'auto' },
  ledgerAddRow: { flexDirection: 'row', alignItems: 'stretch', borderTopWidth: 1 },
  ledgerAddCell: { flex: 1, paddingVertical: 12, paddingHorizontal: 18, justifyContent: 'center', alignItems: 'center' },
  primaryBtn: { paddingVertical: 9, paddingHorizontal: 14, borderRadius: 8 },
  outlineBtn: { paddingVertical: 7, paddingHorizontal: 12, borderRadius: 8, borderWidth: 1 },
  field: { width: 74, borderWidth: 1, borderRadius: 6, paddingVertical: 5, paddingHorizontal: 7, fontSize: 14.5 },
  spotsSaveBar: {
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 120,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: 12,
    paddingHorizontal: 20,
    paddingTop: 12,
    paddingBottom: 12,
    borderTopWidth: 1,
    ...(Platform.OS === 'web'
      ? { paddingBottom: 'max(12px, env(safe-area-inset-bottom, 0px))' }
      : {}),
  },
  tierHead: { flexDirection: 'row', gap: 10, paddingBottom: 6 },
  colH: { fontFamily: FONT.mono, fontSize: 12, letterSpacing: 1, textTransform: 'uppercase' },
  tierRow: { flexDirection: 'row', alignItems: 'center', gap: 8, paddingVertical: 9, borderTopWidth: 1 },
});
